import { DetailPageLayout } from "layouts/DetailPageLayout";
import { ServerErrorBoundary } from "layouts/errors/ServerErrorBoundary";
import { FullPageLayout } from "layouts/FullPageLayout";
import { type IListPageChildProps, ListPageLayout } from "layouts/ListPageLayout";
import { PrintPageLayout } from "layouts/PrintPageLayout";
import { ResearchPreviewLayout } from "layouts/ResearchPreviewLayout";
import { type IWebContainerProps, WebviewPageLayout } from "layouts/WebviewPageLayout";
import { isEqual } from "lodash-es";
import moment from "moment";
import queryString from "query-string";
import { Component, useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Redirect, Route, useHistory, useLocation, useParams, useRouteMatch } from "react-router";
import { type RouteComponentProps, Switch } from "react-router-dom";
import { CompatRouter } from "react-router-dom-v5-compat";

import type { InvoiceFormat } from "@btnet/services-btnet/handlers/models/invoiceFormat";
import type { TemplateType } from "@btnet/services-btnet/handlers/models/templateType";

import { BdsLinkConfig } from "@buildertrend/components";

import { AppProvider } from "helpers/AppProvider";
import { AppRouteChange } from "helpers/AppRouteChange";
import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";
import { BuilderInfoContext, WhenBuilderFlag } from "helpers/globalContext/BuilderInfoContext";
import { EnvironmentInfoContext } from "helpers/globalContext/EnvironmentInfoContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLocalStorage, BTSessionStorage } from "types/btStorage";
import {
    BTLoginTypes,
    DirectionTypes,
    DirectoryType,
    EmailFolder,
    ForbiddenReason,
    MediaType,
    MenuItemName,
    type OpenActivityType,
    OpenedFrom,
    POMappingEntityTypes,
    type ReplyType,
    type SelectionsDynamicGridColumnTypes,
} from "types/enum";

import {
    dispatchTracking,
    type ITrackingProp,
    processTracking,
    track,
} from "utilities/analytics/analytics";
import { dailyLogViewRoute } from "utilities/flatRoutes/dailyLog.routes";
import { jobDetailRoute } from "utilities/flatRoutes/job.routes";
import {
    preliminaryEstimateGenerateRoute,
    preliminaryEstimateRoute,
} from "utilities/flatRoutes/preliminaryEstimate.routes";
import { parseJobPickerNextUrl } from "utilities/jobPicker/jobPicker";
import { useJobPickerDetailRedirect } from "utilities/jobPicker/useJobPickerDetailRedirect";
import { lazyLoadComponent } from "utilities/LazyLoad.utility";
import { ListEntityType } from "utilities/list/list.types";
import { isInPortal } from "utilities/portal/portal";
import {
    type IWebviewRouteParams,
    routes,
    updateWebformsQueryStringParams,
} from "utilities/routes";
import { agentTesterRoute, apiDocsRoute } from "utilities/routes/pages/devTools.routes";
import { routesWebforms } from "utilities/routesWebforms";
import { AppRouter } from "utilities/routing/AppRouter";
import { StepUpAuthCallback } from "utilities/stepUpAuth";
import { stringToArray } from "utilities/string/string";
import { parseParams, useQueryString, useRouteProps } from "utilities/url/url";

import { BTLink } from "commonComponents/btWrappers/BTLink/BTLink";
import { BTSuspense } from "commonComponents/btWrappers/BTSuspense/BTSuspense";
import { type IDTypes } from "commonComponents/entity/accounting/common/accounting.api.types";
import { type ISignatureRequestWebviewRouteParams } from "commonComponents/entity/media/signatures/SignatureRequest/webview/SignatureRequestWebviewContainer";
import { type ISignDocumentWebviewRouteParams } from "commonComponents/entity/media/signatures/SignDocument/webview/SignDocumentWebviewContainer";
import { type IPaymentMethodRedirectDetails } from "commonComponents/financial/Checkout/Checkout.api.types";
import EntityListWrapper from "commonComponents/helpers/EntityListWrapper/EntityListWrapper";
import { BrowserSupportedWarning } from "commonComponents/utilities/BrowserSupportedWarning/BrowserSupportedWarning";
import { isValidId } from "commonComponents/utilities/DropDown/DropDown";
import {
    JobPickerDisplayModes,
    JobPickerSelectModes,
} from "commonComponents/utilities/JobPicker/JobPicker.types";
import { JobPickerRouter } from "commonComponents/utilities/JobPicker/JobPickerRouter";
import { useInfoArchOptIn } from "commonComponents/utilities/MainNavigation/InfoArchOptInToggle/useInfoArchOptIn";
import PageTitle from "commonComponents/utilities/PageTitle/PageTitle";
import { ShowOnPortal } from "commonComponents/utilities/ShowOnPortal/ShowOnPortal";
import { getSystemFilter } from "commonComponents/utilities/SystemFilters/SystemFilterUtils";
import RefreshPrompt from "commonComponents/utilities/Versioning/RefreshPrompt";

import { type IBidDetailsWebviewRouteParams } from "entity/bid/Bid/BidDetailsWebviewContainer";
import { type IBidSubsDetailsWebviewRouteParams } from "entity/bid/Bid/BidSubsDetailsWebviewContainer";
import { useIsDraftBillsEffective } from "entity/bill/hooks/useIsDraftBillsEffective";
import {
    FROM_LIEN_WAIVERS_PARAM,
    parseLienWaiverReturnParams,
} from "entity/bill/LienWaiverReview/lienWaiverPaymentNavigation";
import { type IChangeOrderDetailsWebviewRouteParams } from "entity/changeOrder/ChangeOrder/ChangeOrderDetailsWebviewContainer";
import { CommentListTabKeys } from "entity/comment/CommentList/CommentList.api.types";
import { CostCatalogViewType } from "entity/costCatalog/CostCatalog.types";
import { type IDepositPrintParams } from "entity/deposit/DepositPrint/DepositPrint.api.types";
import { type IReadonlyProposalWorksheetWebviewRouteParams } from "entity/estimate/Estimate/ReadonlyProposalWorksheet/ReadonlyProposalWorksheetWebviewContainer";
import RedirectToFrozenPage from "entity/FrozenBuilder/RedirectToFrozenPage";
import { hawkRouteConfig } from "entity/hawk/routes/routeConfig";
import { InternalUserTabKeys } from "entity/internalUser/InternalUserDetails/InternalUser.api.types";
import { JobPage } from "entity/job/Job/JobFullPage";
import { JobListTabs } from "entity/job/JobList/JobList.api.types";
import { type IJobPriceSummaryPrintWebviewRouteParams } from "entity/job/JobPriceSummary/JobPriceSummaryPrintWebviewContainer";
import { parseJpsSortStates } from "entity/job/JobPriceSummary/jpsSortParams";
import { OAuthType } from "entity/login/Login/Login.api.types";
import { useOAuthState } from "entity/login/Login/useOAuthState";
import { MfaEnrollmentBlock } from "entity/login/MfaBlock/MfaEnrollmentBlock";
import RedirectForCredentialUpgrade from "entity/login/RedirectForCredentialUpgrade/RedirectForCredentialUpgrade";
import { type ISharedMediaRouteProps } from "entity/media/common/mediaTypes";
import { getMessageListPropsFromRoute } from "entity/message/MessageList/MessageList.utils";
import { type AdminOnlinePaymentReportTabs } from "entity/onlinePayment/AdminOnlinePaymentReports/AdminOnlinePaymentReports.types";
import { type IDocInstanceAndJobId } from "entity/photo/PhotoPreview/PhotoPreview.api.types";
import { parsePurchaseOrderMappingQueryStringParams } from "entity/purchaseOrder/PoMapping/PoMappingUtilities";
import { type IPurchasingDashboardRouteProps } from "entity/rebate/PurchasingDashboard/PurchasingDashboard";
import {
    BaselineVsActualDurationReportLazyLoaded,
    BudgetedVsProjectedReportLazyLoaded,
    CashflowReportLazyLoaded,
    ChangeOrderProfitReportLazyLoaded,
    DailyLogCreationByJobReportLazyLoaded,
    DailyLogsByUserReportLazyLoaded,
    HoursWorkedByEmployeeReportLazyLoaded,
    HoursWorkedByJobReportLazyLoaded,
    InvoicingReportLazyLoaded,
    LaborActualsVsBudgetedReportLazyLoaded,
    LeadActivitiesBySalespersonReportLazyLoaded,
    LeadCountBySalespersonReportLazyLoaded,
    LeadStatusBySourceReportLazyLoaded,
    ProfitabilityReportLazyLoaded,
    ReportListLazyLoaded,
    SchedulePercentCompleteByJobReportLazyLoaded,
    WorkInProgressReportLazyLoaded,
} from "entity/reporting";
import { ScheduleEntityDefaultParams } from "entity/schedule/Schedule/Schedule.api.types";
import { type IScheduleDetailsWebviewRouteParams } from "entity/schedule/Schedule/ScheduleDetailsWebviewContainer";
import { ScheduleListContainerTabs } from "entity/schedule/ScheduleListContainer/ScheduleListContainer.api.types";
import { SelectionListContainerTabs } from "entity/selection/SelectionListContainer/SelectionListContainer.types";
import { SelectionsAreaRouteGuard } from "entity/selection/SelectionListContainer/SelectionsAreaRouteGuard";
import { type SelectionOptionStateOverride } from "entity/selectionOptions/SelectionOption/SelectionOption.types";
import RedirectToSetupSummary from "entity/setup/SetupSummary/RedirectToSetupSummary";
import { LeadSourceValues } from "entity/SignupForm/SignupForm.api.types";
import { SpecificationDefaultView } from "entity/specification/SpecificationDefaultView";
import { type SubSetupTabKeys } from "entity/sub/SubSetup/SubSetup.api.types";
import { type SubTabKeys } from "entity/sub/SubVendor/SubVendor.api.types";
import { useSubmittalJobRedirect } from "entity/submittal/useSubmittalJobRedirect";
import { SurveyListContainerTabs } from "entity/survey/common/SurveyListContainer/SurveyListContainer.types";
import { TemplateListTabs } from "entity/template/TemplateList/TemplateListTab/TemplateListTab.api.types";
import { type IShiftWebviewRouteParams } from "entity/timeClock/Shift/ShiftWebviewContainer";
import { ShiftListContainerTabs } from "entity/timeClock/ShiftList/ShiftListContainer/ShiftListContainer.api.types";
import { ToDoPrintDisplayOptions } from "entity/toDo/print/ToDoPrint/ToDoPrint.api.types";
import { type VendorTypes } from "entity/vendor/common/vendor.types";
import { useRedirectOnJobPickerSwitch } from "entity/warranty/Warranty/useRedirectOnJobPickerSwitch";
import { WarrantyAddJobPickerModal } from "entity/warranty/Warranty/WarrantyAddJobPickerModal";
import { type IWarrantyServiceUrlProps } from "entity/warranty/WarrantyService/WarrantyService";

import ServerError from "./layouts/errors/ServerError";

// #region Lazy Loaded Components
/** These Components are lazy loaded so we only ship down parts of the app we need */
/**
 * BTAdmin-only chrome (BTAdminNavigation + BTAdminUserInfoProvider) that no builder, sub, or
 * owner ever renders. Lazy loaded at the layout level rather than around BTAdminNavigation
 * itself: BTAdminNavigation is `position: sticky` (BTAdminNavigation.less), and wrapping it
 * directly would make lazyLoadComponent's BTSuspense div its containing block, breaking the
 * sticky offset.
 */
const BTAdminLayout = lazyLoadComponent(() => import("layouts/BTAdminLayout"));
const ApiDocs = lazyLoadComponent(() => import("./apiDocs/ApiDocs"));
const AgentTester = lazyLoadComponent(() => import("./agentTester/AgentTester"));
const OrgLinkNotConnectedEmptyState = lazyLoadComponent(
    () => import("./entity/OrgLinkNotConnectedEmptyState/OrgLinkNotConnectedEmptyState"),
);
const SubscriptionInactiveEmptyState = lazyLoadComponent(
    () => import("./entity/SubscriptionInactiveEmptyState/SubscriptionInactiveEmptyState"),
);
const JobFinancials = lazyLoadComponent(
    () => import("entity/job/JobPriceSummary/JobPriceSummaryFullPage/JobFinancials"),
);
const EmailResubscribe = lazyLoadComponent(
    () => import("entity/lead/LeadEmailResubscribe/LeadEmailResubscribe"),
);
const OutboundAccountSetup = lazyLoadComponent(
    () => import("entity/onlinePayment/OutboundAccountSetup/OutboundAccountSetup"),
);

const TimeCardPage = lazyLoadComponent(
    () => import("./entity/timeClock/ShiftList/ShiftListContainer/ShiftListContainer"),
);

const MarketplaceRedirect = lazyLoadComponent(
    () => import("./entity/marketplace/Redirect/MarketplaceRedirect"),
);

const RFI = lazyLoadComponent(() => import("./entity/rfi/Rfi/Rfi"));
const PurchaseOrderPrint = lazyLoadComponent(
    () => import("./entity/purchaseOrder/PurchaseOrderPrint/PurchaseOrderPrint"),
);
const Warranty = lazyLoadComponent(() => import("./entity/warranty/Warranty/Warranty"));
const PreliminaryEstimate = lazyLoadComponent(
    () => import("./entity/preliminaryEstimate/PreliminaryEstimate/PreliminaryEstimate"),
);
const PreliminaryEstimateList = lazyLoadComponent(
    () => import("./entity/preliminaryEstimate/PreliminaryEstimateList/PreliminaryEstimateList"),
);
const LienWaiver = lazyLoadComponent(() => import("entity/lienWaiver/LienWaiver/LienWaiver"));
const SubmittalListPageContainer = lazyLoadComponent(
    () => import("./entity/submittal/SubmittalList/SubmittalListPageContainer"),
);
const Submittal = lazyLoadComponent(() => import("./entity/submittal/Submittal"));
const SubmittalPrint = lazyLoadComponent(
    () => import("./entity/submittal/SubmittalPrint/SubmittalPrint"),
);
const LienWaiverPrint = lazyLoadComponent(
    () => import("entity/lienWaiver/LienWaiverPrint/LienWaiverPrint"),
);
const LeadProposalExternal = lazyLoadComponent(
    () => import("entity/leadProposal/LeadProposalExternal/LeadProposalExternal"),
);
const WarrantyPrint = lazyLoadComponent(
    () => import("./entity/warranty/print/WarrantyPrint/WarrantyPrint"),
);
const QuestionReportPrint = lazyLoadComponent(
    () => import("./entity/survey/print/QuestionReportPrint/QuestionReportPrint"),
);
const QuestionReport = lazyLoadComponent(
    () => import("./entity/survey/QuestionReport/QuestionReport"),
);
const SelectionPrint = lazyLoadComponent(
    () => import("./entity/selection/SelectionPrint/SelectionPrint"),
);
const SpecificationPrint = lazyLoadComponent(
    () => import("./entity/specification/SpecificationPrint/SpecificationPrint"),
);
const TaskDetail = lazyLoadComponent(() => import("./entity/task/detail/TaskDetail"));
const TaskPageContainer = lazyLoadComponent(() =>
    import("./entity/task/list/TaskPageContainer").then((m) => ({ default: m.TaskPageContainer })),
);
const AllowancePrint = lazyLoadComponent(
    () => import("./entity/legacyAllowance/AllowancePrint/AllowancePrint"),
);
const SurveyPrint = lazyLoadComponent(
    () => import("./entity/survey/print/SurveyPrint/SurveyPrint"),
);
const ToDoPrint = lazyLoadComponent(() => import("./entity/toDo/print/ToDoPrint/ToDoPrint"));
const BidPrint = lazyLoadComponent(() => import("./entity/bid/BidPrint/BidPrint"));
const ChangeOrderPrint = lazyLoadComponent(
    () => import("./entity/changeOrder/ChangeOrderPrint/ChangeOrderPrint"),
);
const OwnerInvoice = lazyLoadComponent(
    () => import("./entity/ownerInvoice/OwnerInvoice/OwnerInvoice"),
);
const LeadProposalCheckout = lazyLoadComponent(
    () => import("./entity/leadProposal/LeadProposalPayOnline/LeadProposalCheckout"),
);
const OwnerInvoiceCheckout = lazyLoadComponent(
    () => import("./entity/ownerInvoice/OwnerInvoiceCheckout/OwnerInvoiceCheckout"),
);
const OwnerInvoiceCheckoutExternal = lazyLoadComponent(
    () => import("./entity/ownerInvoice/OwnerInvoiceCheckoutExternal/OwnerInvoiceCheckoutExternal"),
);
const SharedExternalOwnerInvoice = lazyLoadComponent(
    () => import("./entity/ownerInvoice/ExternalOwnerInvoice/SharedExternalOwnerInvoice"),
);
const OnlinePaymentExternal = lazyLoadComponent(
    () => import("./entity/onlinePayment/OnlinePaymentExternal/OnlinePaymentExternal"),
);

const OnlineInvoiceExternal = lazyLoadComponent(
    () => import("./entity/onlinePayment/OnlineInvoiceExternal/OnlineInvoiceExternal"),
);
const OutboundPaymentsExternal = lazyLoadComponent(
    () => import("./entity/onlinePayment/OutboundPaymentsExternal/OutboundPaymentsExternal"),
);
const OutboundPaymentShortLink = lazyLoadComponent(
    () => import("./entity/onlinePayment/OutboundPaymentShortLink/OutboundPaymentShortLink"),
);
const DepositCheckout = lazyLoadComponent(
    () => import("./entity/deposit/DepositCheckout/DepositCheckout"),
);
const DepositExternal = lazyLoadComponent(
    () => import("./entity/deposit/DepositExternal/DepositExternal"),
);
const DepositExternalWebviewContainer = lazyLoadComponent(
    () => import("entity/deposit/DepositExternal/DepositExternalWebviewContainer"),
);
const DepositPrint = lazyLoadComponent(() => import("./entity/deposit/DepositPrint/DepositPrint"));
const DepositPrintWebviewContainer = lazyLoadComponent(
    () => import("./entity/deposit/DepositPrint/DepositPrintWebviewContainer"),
);
const OwnerInvoicePrint = lazyLoadComponent(
    () => import("./entity/ownerInvoice/OwnerInvoicePrint/OwnerInvoicePrint"),
);
const CostCodesExportToAccounting = lazyLoadComponent(
    () => import("./entity/costCode/CostCodeExportToAccounting/CostCodeExportToAccounting"),
);
const BidPackagePrint = lazyLoadComponent(
    () => import("./entity/bidPackage/BidPackagePrint/BidPackagePrint"),
);
const Lead = lazyLoadComponent(() => import("./entity/lead/Lead/Lead"));
const Auth0Redirect = lazyLoadComponent(
    () => import("./commonComponents/utilities/Auth0/Auth0Redirect"),
);
const IntegrationsSettings = lazyLoadComponent(
    () => import("./entity/lead/IntegrationsSettings/IntegrationsSettings"),
);
const Reconsent = lazyLoadComponent(() => import("./entity/marketplace/Reconsent/Reconsent"));
const LeadActivity = lazyLoadComponent(() => import("./entity/lead/LeadActivity/LeadActivity"));
const ChangeOrderOwnerRequest = lazyLoadComponent(
    () => import("./entity/changeOrder/ChangeOrderOwnerRequest/ChangeOrderOwnerRequest"),
);
const ChangeOrderOwnerRequestWebviewContainer = lazyLoadComponent(
    () =>
        import(
            "entity/changeOrder/ChangeOrderOwnerRequest/ChangeOrderOwnerRequestWebviewContainer"
        ),
);
const PaymentRequest = lazyLoadComponent(
    () => import("./entity/sub/PaymentRequest/PaymentRequest"),
);
const ClockInClockOut = lazyLoadComponent(
    () => import("./entity/timeClock/ClockInClockOut/ClockInClockOut"),
);
const ImportWizard = lazyLoadComponent(
    () => import("./entity/importWizard/ImportWizard/ImportWizard"),
);
const CreditMemo = lazyLoadComponent(() => import("entity/creditMemo/CreditMemo/CreditMemo"));

const SurveySettings = lazyLoadComponent(
    () => import("./entity/survey/SurveySettings/SurveySettings"),
);
const Survey = lazyLoadComponent(() => import("./entity/survey/Survey/Survey"));
const Notification = lazyLoadComponent(
    () => import("./entity/message/NotificationDisplay/NotificationDisplay"),
);
const MessageCompose = lazyLoadComponent(
    () => import("./entity/message/MessageCompose/MessageCompose"),
);
const MediaMoveFolder = lazyLoadComponent(
    () => import("./entity/media/MediaMoveFolder/MediaMoveFolder"),
);
const MessagePrint = lazyLoadComponent(() => import("./entity/message/Message/MessagePrint"));
const DailyLog = lazyLoadComponent(() => import("./entity/dailyLog/DailyLog/DailyLog"));
const DailyLogView = lazyLoadComponent(() => import("./entity/dailyLog/DailyLogView/DailyLogView"));
const SubVendor = lazyLoadComponent(() => import("./entity/sub/SubVendor/SubVendor"));
const ChangeOrderPayment = lazyLoadComponent(
    () => import("./entity/changeOrder/ChangeOrderPayment/ChangeOrderPayment"),
);
const ChangeOrderApprovalSuccess = lazyLoadComponent(
    () => import("./entity/changeOrder/ChangeOrderApprovalSuccess/ChangeOrderApprovalSuccess"),
);
const Schedule = lazyLoadComponent(() => import("./entity/schedule/Schedule/Schedule"));
const ToDo = lazyLoadComponent(() => import("./entity/toDo/details/ToDoDetails/ToDo"));
const Contact = lazyLoadComponent(() => import("./entity/contact/Contact/Contact"));
const LinkEntityToAccounting = lazyLoadComponent(
    () =>
        import(
            "./commonComponents/entity/accounting/LinkEntityToAccounting/LinkEntityToAccounting"
        ),
);
const BidPackage = lazyLoadComponent(() => import("./entity/bidPackage/BidPackage/BidPackage"));
const Allowance = lazyLoadComponent(() => import("./entity/legacyAllowance/Allowance/Allowance"));
const DependentSelections = lazyLoadComponent(
    () => import("./entity/selectionChoice/common/DependentSelections/DependentSelections"),
);
const SelectionChoice = lazyLoadComponent(
    () => import("./entity/selectionChoice/SelectionChoice/SelectionChoice"),
);
const Selection = lazyLoadComponent(() => import("./entity/selection/Selection/Selection"));
const SelectionOption = lazyLoadComponent(
    () => import("./entity/selectionOptions/SelectionOption/SelectionOption"),
);
const SelectionOptionRouteGuard = lazyLoadComponent(() =>
    import("./entity/selectionOptions/SelectionOption/SelectionOptionRouteGuard").then((m) => ({
        default: m.SelectionOptionRouteGuard,
    })),
);
const CopyChoiceFromSelection = lazyLoadComponent(
    () => import("./entity/selectionChoice/common/CopyChoiceFromSelection/CopyChoiceFromSelection"),
);
const CopyMedia = lazyLoadComponent(() => import("./entity/media/CopyMedia/CopyMedia"));
const ChangeOrderPOMapping = lazyLoadComponent(
    () => import("entity/changeOrder/ChangeOrderPOMapping/ChangeOrderPOMapping"),
);
const BidPOMapping = lazyLoadComponent(() => import("entity/bid/BidPOMapping/BidPOMapping"));
const EstimatePOMapping = lazyLoadComponent(
    () => import("entity/estimate/EstimatePOMapping/EstimatePOMapping"),
);
const SelectionPOMapping = lazyLoadComponent(
    () => import("entity/selection/SelectionPOMapping/SelectionPOMapping"),
);
const JobPageWebviewContainer = lazyLoadComponent(
    () => import("./entity/job/Job/JobPageWebviewContainer"),
);
const JobPrint = lazyLoadComponent(() => import("./entity/job/JobPrint/JobPrint"));
const JobFromTemplate = lazyLoadComponent(
    () => import("./entity/job/JobFromTemplate/JobFromTemplate"),
);
const JobPriceSummaryPrintWebviewContainer = lazyLoadComponent(
    () => import("./entity/job/JobPriceSummary/JobPriceSummaryPrintWebviewContainer"),
);
const JobFinancialsWebviewContainer = lazyLoadComponent(
    () =>
        import(
            "./entity/job/JobPriceSummary/JobPriceSummaryFullPage/JobFinancialsWebviewContainer"
        ),
);
const JobPriceSummaryPrint = lazyLoadComponent(
    () => import("./entity/job/JobPriceSummaryPrint/JobPriceSummaryPrint"),
);
const Shift = lazyLoadComponent(() => import("./entity/timeClock/Shift/Shift"));
const BidRequest = lazyLoadComponent(() => import("./entity/bid/Bid/Bid"));
const BuilderRiskInsuranceSettings = lazyLoadComponent(
    () =>
        import(
            "./entity/builderRiskInsurance/BuilderRiskInsuranceStatus/BuilderRiskInsuranceSettings"
        ),
);
const NoAccess = lazyLoadComponent(() => import("./commonComponents/error/NoAccess/NoAccess"));
const RelatedRfi = lazyLoadComponent(() => import("./entity/rfi/RelatedRfi/RelatedRfi"));
const ConversationWithEntityPrompt = lazyLoadComponent(
    () => import("./entity/conversation/Conversation/ConversationEntityPrompt"),
);
const SignupForm = lazyLoadComponent(() => import("./entity/SignupForm/SignupForm"));
const UnreadDocument = lazyLoadComponent(
    () => import("./entity/document/UnreadDocument/UnreadDocument"),
);
const Template = lazyLoadComponent(() => import("./entity/template/Template/Template"));
const TemplateFromTemplate = lazyLoadComponent(
    () => import("./entity/template/TemplateFromTemplate/TemplateFromTemplate"),
);
const LeadPrint = lazyLoadComponent(() => import("./entity/lead/LeadPrint/LeadPrint"));
const DailyLogPrint = lazyLoadComponent(
    () => import("entity/dailyLog/DailyLogPrint/DailyLogPrint"),
);
const ContactPrint = lazyLoadComponent(() => import("entity/contact/ContactPrint/ContactPrint"));
const InternalUserQuickAdd = lazyLoadComponent(
    () => import("entity/internalUser/InternalUserQuickAdd/InternalUserQuickAdd"),
);
const InternalUserRoleListDev = lazyLoadComponent(
    () =>
        import(
            "entity/internalUser/InternalUserRoleSettings/InternalUserRoleList/InternalUserRoleList"
        ),
);
const InternalUserRoleDetailsDev = lazyLoadComponent(
    () => import("entity/internalUser/InternalUserRoleDetails/InternalUserRoleDetails"),
);
const InternalUsersList = lazyLoadComponent(
    () => import("entity/internalUser/InternalUserList/InternalUserList/InternalUserList"),
);
const ContactsList = lazyLoadComponent(() => import("entity/contact/ContactList/ContactList"));
const SubList = lazyLoadComponent(() => import("entity/sub/SubList/SubList"));
const SubscriptionManager = lazyLoadComponent(
    () => import("entity/SubscriptionManager/SubscriptionManager"),
);
const SubscriptionList = lazyLoadComponent(() => import("entity/Subscription/SubscriptionList"));
const CreditMemoPrint = lazyLoadComponent(
    () => import("entity/creditMemo/CreditMemoPrint/CreditMemoPrint"),
);
const PurchaseOrderList = lazyLoadComponent(
    () => import("entity/purchaseOrder/PurchaseOrderList/PurchaseOrderList"),
);
const ScheduleOfValuesPage = lazyLoadComponent(
    () => import("entity/scheduleOfValues/ScheduleOfValuesPage/ScheduleOfValuesPage"),
);
const ScheduleOfValuesList = lazyLoadComponent(
    () => import("entity/scheduleOfValues/ScheduleOfValuesList/ScheduleOfValuesList"),
);
const CreditMemosList = lazyLoadComponent(
    () => import("./entity/creditMemo/CreditMemoList/CreditMemoList"),
);
const PurchaseOrder = lazyLoadComponent(
    () => import("entity/purchaseOrder/PurchaseOrder/PurchaseOrder"),
);
const CostCodeImportFromAccounting = lazyLoadComponent(
    () => import("entity/costCode/CostCodeImportFromAccounting/CostCodeImportFromAccounting"),
);
const ChangeOrder = lazyLoadComponent(() => import("entity/changeOrder/ChangeOrder/ChangeOrder"));
const SubBill = lazyLoadComponent(() => import("entity/bill/SubBill/SubBill"));
const Bill = lazyLoadComponent(() => import("entity/bill/Bill/Bill"));
const BillPrint = lazyLoadComponent(() => import("entity/bill/Bill/BillPrint/BillPrint"));
const BillApprovalQueueWebviewContainer = lazyLoadComponent(
    () => import("entity/bill/BillApprovalQueue/BillApprovalQueueWebviewContainer"),
);
const BillsListPageContent = lazyLoadComponent(() =>
    import("entity/bill/BillList/BillsListPageContent").then((m) => ({
        default: m.BillsListPageContent,
    })),
);
const BillApprovalQueue = lazyLoadComponent(() =>
    import("entity/bill/BillApprovalQueue/BillApprovalQueue").then((m) => ({
        default: m.BillApprovalQueue,
    })),
);
const BillsInbox = lazyLoadComponent(() =>
    import("entity/bill/BillsInbox/BillsInbox").then((m) => ({
        default: m.BillsInbox,
    })),
);
const MediaFolderProperties = lazyLoadComponent(
    () => import("entity/media/MediaFolderProperties/MediaFolderProperties"),
);
const PhotoPreview = lazyLoadComponent(() => import("entity/photo/PhotoPreview/PhotoPreview"));
const Document = lazyLoadComponent(() => import("entity/document/Document/Document"));
const SignatureFlow = lazyLoadComponent(
    () => import("entity/signature/SignatureFlow/SignatureFlow"),
);
const Photo = lazyLoadComponent(() => import("entity/photo/Photo/Photo"));
const RFIPrint = lazyLoadComponent(() => import("./entity/rfi/RfiPrint/RfiPrint"));
const BrandingSettings = lazyLoadComponent(
    () => import("./entity/branding/BuilderAccountBranding/BuilderAccountBranding"),
);

const MediaFolderEmailInfo = lazyLoadComponent(
    () => import("entity/media/MediaFolderEmailInfo/MediaFolderEmailInfo"),
);
const ScheduleConflictReport = lazyLoadComponent(
    () =>
        import(
            "commonComponents/entity/scheduleConflicts/ScheduleConflictReport/ScheduleConflictReport"
        ),
);
const OwnerPortalRedirect = lazyLoadComponent(
    () => import("commonComponents/helpers/OwnerPortalRedirect/OwnerPortalRedirect"),
);
const AdminPaymentsPrescreening = lazyLoadComponent(
    () => import("./entity/onlinePayment/PaymentsPrescreening/PaymentsPrescreening"),
);
const ExcludeFromChurn = lazyLoadComponent(
    () => import("./entity/excludeFromChurn/ExcludeFromChurn/ExcludeFromChurn"),
);
const MediaReplace = lazyLoadComponent(() => import("entity/media/MediaReplace/MediaReplace"));
const AccountingSettings = lazyLoadComponent(
    () => import("./entity/accounting/AccountingSettings/AccountingSettings"),
);

const AuthRedirect = lazyLoadComponent(() => import("./entity/oauth/AuthRedirect/AuthRedirect"));
const QuickBooksSyncSchedule = lazyLoadComponent(
    () =>
        import(
            "./entity/accounting/AccountingSettings/QuickBooksSyncSchedule/QuickBooksSyncSchedule"
        ),
);
const OAuthRedirect = lazyLoadComponent(
    () => import("./entity/accounting/AccountingSettings/OAuthRedirect"),
);
const BidExternal = lazyLoadComponent(() => import("entity/bid/BidExternal/BidExternal"));
const PurchaseOrderExternal = lazyLoadComponent(
    () => import("entity/purchaseOrder/PurchaseOrderExternal/PurchaseOrderExternal"),
);
const ReceiptList = lazyLoadComponent(() => import("./entity/receipt/ReceiptList/ReceiptList"));
const ReceiptsUpload = lazyLoadComponent(
    () => import("./entity/receipt/ReceiptUpload/ReceiptUpload"),
);
const ProposalPdfGeneration = lazyLoadComponent(
    () => import("./entity/estimate/Proposal/ProposalPdfGeneration"),
);
const LeadProposalPdfGeneration = lazyLoadComponent(
    () =>
        import(
            "./entity/leadProposal/LeadProposal/DeprecatedLeadProposalFiles/LeadProposalPdfGeneration"
        ),
);
const Video = lazyLoadComponent(() => import("entity/video/Video/Video"));
const OwnerSurvey = lazyLoadComponent(() => import("entity/survey/OwnerSurvey/OwnerSurvey"));
const CostCatalog = lazyLoadComponent(() => import("entity/costCatalog/CostCatalog"));
const CostGroup = lazyLoadComponent(() => import("entity/CostGroup/CostGroup"));
const BuilderAccountChangeRequest = lazyLoadComponent(
    () =>
        import(
            "entity/builderAccountChangeRequest/BuilderAccountChangeRequest/BuilderAccountChangeRequest"
        ),
);
const AdminCancellation = lazyLoadComponent(
    () => import("entity/AdminCancellation/AdminCancellation"),
);
const CostItem = lazyLoadComponent(() => import("entity/costItem/CostItem/CostItem"));
const CostCodeItemUsage = lazyLoadComponent(
    () => import("entity/costCode/CostCodeItemUsage/CostCodeItemUsage"),
);
const ProposalPrint = lazyLoadComponent(
    () => import("entity/estimate/ProposalPrint/ProposalPrint"),
);
const LeadProposalPrint = lazyLoadComponent(
    () => import("entity/leadProposal/LeadProposalPrint/LeadProposalPrint"),
);
const MobileLeadProposalPreview = lazyLoadComponent(
    () => import("entity/leadProposal/MobileLeadProposalPreview/MobileLeadProposalPreview"),
);
const Proposal = lazyLoadComponent(() => import("entity/estimate/Proposal/Proposal"));
const LeadProposal = lazyLoadComponent(
    () => import("entity/leadProposal/LeadProposal/LeadProposal"),
);
const InternalUser = lazyLoadComponent(
    () => import("entity/internalUser/InternalUserDetails/InternalUser"),
);
const RouteMyUserDetails = lazyLoadComponent(
    () => import("entity/internalUser/InternalUserDetails/RouteMyUserDetails"),
);
const HomeDepotSettings = lazyLoadComponent(
    () => import("entity/vendor/HomeDepotSettings/HomeDepotSettings"),
);
const LowesSettings = lazyLoadComponent(() => import("entity/lowes/lowesSettings/LowesSettings"));
const LeadEmailWizard = lazyLoadComponent(
    () => import("entity/lead/LeadEmailWizard/LeadEmailWizard"),
);
const VendorStoreFinder = lazyLoadComponent(
    () => import("entity/vendor/VendorStoreFinder/VendorStoreFinder"),
);

const TradeAgreement = lazyLoadComponent(
    () => import("./entity/tradeAgreement/TradeAgreement/TradeAgreement"),
);
const NotFoundError = lazyLoadComponent(() => import("./layouts/errors/NotFoundError"));
const PreliminaryEstimateGeneratePage = () => (
    <WhenBuilderFlag
        isEnabled="aIPreliminaryEstimate"
        then={<PreliminaryEstimate />}
        otherwise={<NotFoundError />}
    />
);
const PreliminaryEstimateListPage = () => (
    <WhenBuilderFlag
        isEnabled="aIPreliminaryEstimate"
        then={<PreliminaryEstimateList />}
        otherwise={<NotFoundError />}
    />
);
const TradeAgreementPrint = lazyLoadComponent(
    () => import("./entity/tradeAgreement/TradeAgreementPrint/TradeAgreementPrint"),
);
const SignupPriceCode = lazyLoadComponent(() => import("./entity/SignupPriceCode/SignupPriceCode"));
const SignupWhatsNext = lazyLoadComponent(
    () => import("./entity/SignupWhatsNext/SignupWhatsNext/SignupWhatsNext"),
);
const ContactUs = lazyLoadComponent(() => import("./entity/ContactUs/ContactUs"));
const VendorReceipt = lazyLoadComponent(
    () => import("./entity/receipt/VendorReceipts/VendorReceipt"),
);
const SubSetup = lazyLoadComponent(() => import("./entity/sub/SubSetup/SubSetup"));
const OwnerSetup = lazyLoadComponent(() => import("./entity/OwnerSetup/OwnerSetup"));
const PaymentServiceBankAccountExternal = lazyLoadComponent(
    () =>
        import(
            "commonComponents/financial/PaymentService/BankAccount/PaymentServiceBankAccountExternal"
        ),
);
const LeadProposalPayOnlineMobile = lazyLoadComponent(
    () => import("./entity/leadProposal/LeadProposalPayOnline/LeadProposalPayOnlineMobile"),
);
const SharedContent = lazyLoadComponent(
    () => import("./entity/sharedContent/SharedContent/SharedContent"),
);
const MessageExternal = lazyLoadComponent(
    () => import("./entity/message/MessageExternal/MessageExternal"),
);
const AdminLogExceptionInSentry = lazyLoadComponent(
    () => import("./entity/AdminLogExceptionInSentry/AdminLogExceptionInSentry"),
);

const LienWaiverReview = lazyLoadComponent(
    () => import("./entity/bill/LienWaiverReview/LienWaiverReview"),
);
const LienWaiverReviewExternal = lazyLoadComponent(
    () => import("./entity/bill/LienWaiverReview/LienWaiverReviewExternal"),
);

const Receipt = lazyLoadComponent(() => import("./entity/receipt/Receipt/Receipt"));
const SharedMedia = lazyLoadComponent(() => import("./entity/media/SharedMedia/SharedMedia"));
const PartnerListContainer = lazyLoadComponent(
    () => import("entity/integrationPartner/btadmin/PartnerList/PartnerList"),
);
const Partner = lazyLoadComponent(
    () => import("entity/integrationPartner/btadmin/Partner/Partner"),
);
const FeatureFlag = lazyLoadComponent(() => import("entity/featureFlag/FeatureFlag/FeatureFlag"));
const FeatureFlagListContainer = lazyLoadComponent(
    () => import("entity/featureFlag/FeatureFlagList/FeatureFlagList"),
);
const SearchUtils = lazyLoadComponent(() => import("entity/searchUtils/SearchUtils"));
const SubscriptionItemSetup = lazyLoadComponent(
    () => import("entity/Subscription/SubscriptionItemSetupUtility/SubscriptionItemSetupUtility"),
);
const SubscriptionPlanLimitsDetails = lazyLoadComponent(
    () => import("entity/Subscription/SubscriptionPlanLimits/SubscriptionPlanLimits"),
);
const ScheduleNotificationPrompt = lazyLoadComponent(
    () => import("./commonComponents/entity/scheduleNotifications/ScheduleNotificationPrompt"),
);
const ScheduleConfirmationExternal = lazyLoadComponent(
    () => import("entity/schedule/ScheduleConfirmationExternal/ScheduleConfirmationExternal"),
);

const Hawk = lazyLoadComponent(() => import("entity/hawk/HawkApp"));

const RebateReceiptReviewList = lazyLoadComponent(
    () => import("entity/rebateReceiptReview/RebateReceiptReviewList/RebateReceiptReviewList"),
);

const RebateHawkQueue = lazyLoadComponent(
    () => import("entity/rebateReceiptReview/RebateReceiptReviewQueue/RebateReceiptReviewQueue"),
);

const RebateHawkQueueList = lazyLoadComponent(
    () => import("entity/rebateReceiptReview/Queues/RebateHawkQueueList"),
);

const BuilderSetup = lazyLoadComponent(() => import("entity/builderSetup/BuilderSetup"));
const BuilderSetupSearch = lazyLoadComponent(
    () => import("entity/builderSetup/BuilderSetupSearch/BuilderSetupSearch"),
);
const SetupSummary = lazyLoadComponent(() => import("entity/setup/SetupSummary/SetupSummary"));
const SetupSummaryWebviewContainer = lazyLoadComponent(
    () => import("entity/setup/SetupSummary/SetupSummaryWebviewContainer"),
);

// Lazy Load SPA List pages
const LandingPage = lazyLoadComponent(() => import("entity/landingPage/LandingPage/LandingPage"));

const FrozenBuilderWrapper = lazyLoadComponent(
    () => import("./entity/FrozenBuilder/FrozenBuilderWrapper"),
);

const EmailUnsubscribe = lazyLoadComponent(
    () => import("./entity/lead/LeadEmailUnsubscribe/LeadEmailUnsubscribe"),
);

const DailyLogList = lazyLoadComponent(() => import("./entity/dailyLog/DailyLogList/DailyLogList"));
const DailyLogListWebviewContainer = lazyLoadComponent(
    () => import("./entity/dailyLog/DailyLogList/DailyLogListWebviewContainer"),
);
const DailyLogViewWebviewContainer = lazyLoadComponent(
    () => import("./entity/dailyLog/DailyLogView/DailyLogViewWebviewContainer"),
);
const DailyLogAddWebviewContainer = lazyLoadComponent(
    () => import("./entity/dailyLog/DailyLog/DailyLogAddWebviewContainer"),
);
const DailyLogWebviewContainer = lazyLoadComponent(
    () => import("./entity/dailyLog/DailyLog/DailyLogWebviewContainer"),
);
const ToDoListWithPanel = lazyLoadComponent(() => import("./entity/toDo/ToDoList/ToDoList"));
const SpecificationList = lazyLoadComponent(
    () => import("./entity/specification/SpecificationList/SpecificationList"),
);
const SpecificationBook = lazyLoadComponent(
    () => import("./entity/specification/book/SpecificationBook"),
);
const SpecificationConversationRedirect = lazyLoadComponent(
    () => import("./entity/specification/SpecificationConversationRedirect"),
);
const InvoicePaymentsList = lazyLoadComponent(
    () => import("./entity/invoicePayment/InvoicePaymentList/InvoicePaymentList"),
);
const MessagesList = lazyLoadComponent(() => import("./entity/message/MessageList/MessageList"));
const DocumentList = lazyLoadComponent(() => import("./entity/document/DocumentList/DocumentList"));
const PhotoList = lazyLoadComponent(() => import("./entity/photo/PhotoList/PhotoList"));
const VideoList = lazyLoadComponent(() => import("./entity/video/VideoList/VideoList"));

const BidPackageList = lazyLoadComponent(
    () => import("./entity/bidPackage/BidPackageList/BidPackageList"),
);
const BidList = lazyLoadComponent(() => import("entity/bid/BidList/BidList"));
const BidDetailsWebviewContainer = lazyLoadComponent(
    () => import("entity/bid/Bid/BidDetailsWebviewContainer"),
);
const BidSubsDetailsWebviewContainer = lazyLoadComponent(
    () => import("entity/bid/Bid/BidSubsDetailsWebviewContainer"),
);
const PaymentsOverview = lazyLoadComponent(
    () => import("./commonComponents/financial/PaymentsOverview/PaymentsOverview"),
);
const Worksheet = lazyLoadComponent(() => import("entity/estimate/Estimate/Worksheet"));
const EstimateWebviewContainer = lazyLoadComponent(
    () => import("entity/estimate/Estimate/EstimateWebviewContainer"),
);
const ProposalDashboardWebviewContainer = lazyLoadComponent(
    () => import("entity/estimate/Estimate/ProposalDashboard/ProposalDashboardWebviewContainer"),
);
const ReadonlyProposalWorksheetWebviewContainer = lazyLoadComponent(
    () =>
        import(
            "entity/estimate/Estimate/ReadonlyProposalWorksheet/ReadonlyProposalWorksheetWebviewContainer"
        ),
);
const BuildProposalWebviewContainer = lazyLoadComponent(
    () => import("entity/estimate/Estimate/BuildProposal/BuildProposalWebviewContainer"),
);
const ProposalDetailsWebviewContainer = lazyLoadComponent(
    () => import("entity/estimate/Estimate/ProposalDetails/ProposalDetailsWebviewContainer"),
);
const ShiftWebviewContainer = lazyLoadComponent(
    () => import("entity/timeClock/Shift/ShiftWebviewContainer"),
);
const ReceiptWebviewContainer = lazyLoadComponent(
    () => import("entity/receipt/Receipt/ReceiptWebviewContainer"),
);
const SelectionOptionWebviewContainer = lazyLoadComponent(
    () => import("entity/selectionOptions/SelectionOption/SelectionOptionWebviewContainer"),
);
const SelectionOptionModalWebviewContainer = lazyLoadComponent(
    () => import("entity/selectionOptions/SelectionOption/SelectionOptionModalWebviewContainer"),
);
const SelectionWebviewContainer = lazyLoadComponent(
    () => import("entity/selection/Selection/SelectionWebviewContainer"),
);
const SelectionChoiceWebviewContainer = lazyLoadComponent(
    () => import("entity/selectionChoice/SelectionChoice/SelectionChoiceWebviewContainer"),
);
const AllowanceWebviewContainer = lazyLoadComponent(
    () => import("entity/legacyAllowance/Allowance/AllowanceWebviewContainer"),
);
const MediaPropertiesWebviewContainer = lazyLoadComponent(
    () => import("commonComponents/entity/media/MediaProperties/MediaPropertiesWebviewContainer"),
);
const LeadWebviewContainer = lazyLoadComponent(
    () => import("entity/lead/Lead/LeadWebviewContainer"),
);
const RfiWebviewContainer = lazyLoadComponent(() => import("entity/rfi/Rfi/RfiWebviewContainer"));
const RelatedRfiWebviewContainer = lazyLoadComponent(
    () => import("entity/rfi/RelatedRfi/RelatedRfiWebviewContainer"),
);
const CreditMemoListWebviewContainer = lazyLoadComponent(
    () => import("entity/creditMemo/CreditMemoList/CreditMemoListWebviewContainer"),
);
const DepositWebviewContainer = lazyLoadComponent(
    () => import("entity/deposit/Deposit/DepositWebviewContainer"),
);
const CreditMemoPrintWebviewContainer = lazyLoadComponent(
    () => import("entity/creditMemo/CreditMemoPrint/CreditMemoPrintWebviewContainer"),
);
const ChangeOrderDetailsWebviewContainer = lazyLoadComponent(
    () => import("entity/changeOrder/ChangeOrder/ChangeOrderDetailsWebviewContainer"),
);
const PaymentsOverviewWebviewContainer = lazyLoadComponent(
    () => import("commonComponents/financial/PaymentsOverview/PaymentsOverviewWebviewContainer"),
);
const PaymentsHostedOnboardingWizardWebviewContainer = lazyLoadComponent(
    () =>
        import(
            "commonComponents/financial/PaymentsHostedOnboardingWizard/PaymentsHostedOnboardingWizardWebviewContainer"
        ),
);
const ScheduleDetailsWebviewContainer = lazyLoadComponent(
    () => import("entity/schedule/Schedule/ScheduleDetailsWebviewContainer"),
);
const CreditMemoWebviewContainer = lazyLoadComponent(
    () => import("entity/creditMemo/CreditMemo/CreditMemoWebviewContainer"),
);
const SubSetupWebviewContainer = lazyLoadComponent(
    () => import("entity/sub/SubSetup/SubSetupWebviewContainer"),
);
const OnlinePaymentListWebviewContainer = lazyLoadComponent(
    () => import("entity/onlinePayment/OnlinePaymentList/OnlinePaymentListWebviewContainer"),
);
const NotificationsWebviewContainer = lazyLoadComponent(
    () => import("entity/notification/panel/webview/NotificationsWebviewContainer"),
);
const ProposalDashboard = lazyLoadComponent(
    () => import("entity/estimate/Estimate/ProposalDashboard/ProposalDashboard"),
);
const ReadonlyProposalWorksheet = lazyLoadComponent(
    () => import("entity/estimate/Estimate/ReadonlyProposalWorksheet/ReadonlyProposalWorksheet"),
);
const OwnerProposal = lazyLoadComponent(
    () => import("entity/estimate/OwnerProposal/OwnerProposal"),
);
const OwnerProposalWebviewContainer = lazyLoadComponent(
    () => import("entity/estimate/OwnerProposal/OwnerProposalWebviewContainer"),
);
const WarrantiesList = lazyLoadComponent(
    () => import("./entity/warranty/WarrantyListContainer/WarrantyListContainer"),
);

const OwnerInvoiceList = lazyLoadComponent(
    () => import("./entity/ownerInvoice/OwnerInvoiceList/OwnerInvoiceList"),
);

const JobsList = lazyLoadComponent(() => import("./entity/job/JobList/JobList"));
const JobsMapList = lazyLoadComponent(() => import("./entity/job/JobList/JobsMapList"));

const TemplateListLanding = lazyLoadComponent(
    () => import("./entity/template/TemplateList/TemplateListLanding"),
);

const CommentsList = lazyLoadComponent(() => import("./entity/comment/CommentList/CommentList"));
const SurveyListContainer = lazyLoadComponent(
    () => import("entity/survey/common/SurveyListContainer/SurveyListContainer"),
);
const OwnerSurveyList = lazyLoadComponent(
    () => import("entity/survey/OwnerSurveyList/OwnerSurveyList"),
);
const ScheduleListContainer = lazyLoadComponent(
    () => import("./entity/schedule/ScheduleListContainer/ScheduleListContainer"),
);

const NotificationHistoryList = lazyLoadComponent(
    () => import("./entity/notification/NotificationList/NotificationList"),
);

const AdminNotificationHistory = lazyLoadComponent(
    () => import("./entity/notification/AdminNotificationHistory/AdminNotificationHistory"),
);

const ChangeOrderList = lazyLoadComponent(
    () => import("./entity/changeOrder/ChangeOrderList/ChangeOrderList"),
);
const ChangeOrderListWebviewContainer = lazyLoadComponent(
    () => import("./entity/changeOrder/ChangeOrderList/ChangeOrderListWebviewContainer"),
);
const OwnerChangeOrderList = lazyLoadComponent(
    () => import("./entity/changeOrder/OwnerChangeOrderList/OwnerChangeOrderList"),
);
const RFIsList = lazyLoadComponent(() => import("./entity/rfi/RfiList/RfiList"));

const DepositList = lazyLoadComponent(() => import("entity/deposit/DepositList/DepositList"));
const SettingList = lazyLoadComponent(() => import("entity/setting/SettingList/SettingList"));
const BTEditorMobile = lazyLoadComponent(
    () => import("commonComponents/btWrappers/editor/BTEditorMobile/BTEditorMobile"),
);
const OnlinePaymentSettings = lazyLoadComponent(
    () => import("entity/onlinePayment/OnlinePaymentSettings/OnlinePaymentSettings"),
);
const PaymentsHostedOnboarding = lazyLoadComponent(
    () =>
        import(
            "commonComponents/financial/PaymentsHostedOnboardingWizard/PaymentsHostedOnboardingWizard"
        ),
);
const EnrollmentWizard = lazyLoadComponent(
    () => import("./entity/rebate/EnrollmentWizard/EnrollmentWizard"),
);
const PaymentTos = lazyLoadComponent(
    () => import("commonComponents/financial/PaymentTos/PaymentTos"),
);

const ChatList = lazyLoadComponent(() => import("entity/chat/Chat"));
const ChatWebviewContainer = lazyLoadComponent(
    () => import("entity/chat/webview/ChatWebviewContainer"),
);

const InvoicePaymentListWebviewContainer = lazyLoadComponent(
    () => import("entity/invoicePayment/InvoicePaymentList/InvoicePaymentListWebviewContainer"),
);

const TaskListWebviewContainer = lazyLoadComponent(
    () => import("entity/task/mobile/TaskListWebviewContainer"),
);

const TaskDetailWebviewContainer = lazyLoadComponent(
    () => import("entity/task/mobile/TaskDetailWebviewContainer"),
);

const PlansListWebviewContainer = lazyLoadComponent(
    () => import("entity/Plans/PlansList/PlansListWebviewContainer"),
);

const PlanPageDetailsWebviewContainer = lazyLoadComponent(
    () => import("entity/Plans/PlanPageDetails/PlanPageDetailsWebviewContainer"),
);

const PlanRevisionsReviewWebviewContainer = lazyLoadComponent(() =>
    import("entity/Plans/PlanRevisionsReview/PlanRevisionsReviewWebviewContainer").then((m) => ({
        default: m.PlanRevisionsReviewWebviewContainer,
    })),
);

const BillsListWebviewContainer = lazyLoadComponent(
    () => import("entity/bill/BillList/BillsListWebviewContainer"),
);

const SubmittalListWebviewContainer = lazyLoadComponent(
    () => import("entity/submittal/SubmittalList/SubmittalListWebviewContainer"),
);

const SubmittalWebviewContainer = lazyLoadComponent(
    () => import("entity/submittal/SubmittalWebviewContainer"),
);

const SubmittalPrintWebviewContainer = lazyLoadComponent(
    () => import("entity/submittal/SubmittalPrint/SubmittalPrintWebviewContainer"),
);

const InternalUserListWebviewContainer = lazyLoadComponent(
    () =>
        import(
            "entity/internalUser/InternalUserList/InternalUserList/InternalUserListWebviewContainer"
        ),
);

const ContactListWebviewContainer = lazyLoadComponent(
    () => import("entity/contact/ContactList/ContactListWebviewContainer"),
);

const SubListWebviewContainer = lazyLoadComponent(
    () => import("entity/sub/SubList/SubListWebviewContainer"),
);

const BillWebviewContainer = lazyLoadComponent(
    () => import("entity/bill/Bill/BillWebviewContainer"),
);

const PurchaseOrderListWebviewContainer = lazyLoadComponent(
    () => import("entity/purchaseOrder/PurchaseOrderList/PurchaseOrderListWebviewContainer"),
);

const PurchaseOrderWebviewContainer = lazyLoadComponent(
    () => import("entity/purchaseOrder/PurchaseOrder/PurchaseOrderWebviewContainer"),
);

const SelectionOptionsListWebviewContainer = lazyLoadComponent(
    () => import("entity/selectionOption/SelectionOptionsListWebviewContainer"),
);

const SelectionListWebviewContainer = lazyLoadComponent(
    () => import("entity/selection/SelectionListContainer/SelectionListWebviewContainer"),
);

const ClientUpdateListWebviewContainer = lazyLoadComponent(
    () => import("entity/clientUpdate/list/ClientUpdateListWebviewContainer"),
);

const OwnerProjectUpdatesWebviewContainer = lazyLoadComponent(
    () => import("entity/OwnerSummary/OwnerProjectUpdates/OwnerProjectUpdatesWebviewContainer"),
);

const ClientUpdateSettingsWebviewContainer = lazyLoadComponent(
    () => import("entity/clientUpdate/settings/ClientUpdateSettingsWebviewContainer"),
);

const ClientUpdateDetailWebviewContainer = lazyLoadComponent(
    () => import("entity/clientUpdate/ClientUpdateDetailWebviewContainer"),
);

const ClientUpdatePrintWebviewContainer = lazyLoadComponent(
    () => import("entity/clientUpdate/ClientUpdatePrintWebviewContainer"),
);

const CommentListWebviewContainer = lazyLoadComponent(
    () => import("entity/comment/CommentList/CommentListWebviewContainer"),
);

const LeadListWebviewContainer = lazyLoadComponent(
    () => import("entity/lead/LeadListContainer/LeadListWebviewContainer"),
);
const LeadContactFormPreviewWebviewContainer = lazyLoadComponent(
    () => import("entity/lead/LeadContactForm/LeadContactFormPreviewWebviewContainer"),
);

const VideoListWebviewContainer = lazyLoadComponent(
    () => import("entity/video/VideoList/VideoListWebviewContainer"),
);

const JobsListWebviewContainer = lazyLoadComponent(
    () => import("entity/job/JobList/JobsListWebviewContainer"),
);

const BidSubsListWebviewContainer = lazyLoadComponent(
    () => import("entity/bid/BidList/BidSubsListWebviewContainer"),
);

const BidPackageListWebviewContainer = lazyLoadComponent(
    () => import("entity/bidPackage/BidPackageList/BidPackageListWebviewContainer"),
);

const BidPackageWebviewContainer = lazyLoadComponent(
    () => import("entity/bidPackage/BidPackage/BidPackageWebviewContainer"),
);

const NotificationListWebviewContainer = lazyLoadComponent(
    () => import("entity/notification/NotificationList/NotificationListWebviewContainer"),
);

const OwnerInvoiceWebviewContainer = lazyLoadComponent(
    () => import("entity/ownerInvoice/OwnerInvoiceWebview/OwnerInvoiceWebviewContainer"),
);

const OnlinePaymentGroupedListWebviewContainer = lazyLoadComponent(
    () =>
        import(
            "entity/onlinePayment/OnlinePaymentGroupedList/OnlinePaymentGroupedListWebviewContainer"
        ),
);

const DocumentListWebviewContainer = lazyLoadComponent(
    () => import("entity/document/DocumentList/DocumentListWebviewContainer"),
);

const UnsignedDocumentListWebviewContainer = lazyLoadComponent(
    () => import("entity/document/DocumentList/UnsignedDocumentListWebviewContainer"),
);

const MessageListWebviewContainer = lazyLoadComponent(
    () => import("entity/message/MessageList/MessageListWebviewContainer"),
);

const MessageWebviewContainer = lazyLoadComponent(
    () => import("entity/message/Message/MessageWebviewContainer"),
);

const MessagePrintWebviewContainer = lazyLoadComponent(
    () => import("entity/message/Message/MessagePrintWebviewContainer"),
);
const MessageComposeWebviewContainer = lazyLoadComponent(
    () => import("entity/message/MessageCompose/MessageComposeWebviewContainer"),
);

const OwnerInvoiceListWebviewContainer = lazyLoadComponent(
    () => import("entity/ownerInvoice/OwnerInvoiceList/OwnerInvoiceListWebviewContainer"),
);

const DepositsListWebviewContainer = lazyLoadComponent(
    () => import("entity/deposit/DepositList/DepositsListWebviewContainer"),
);

const RfiListWebviewContainer = lazyLoadComponent(
    () => import("entity/rfi/RfiList/RfiListWebviewContainer"),
);

const ReceiptsListWebviewContainer = lazyLoadComponent(
    () => import("entity/receipt/ReceiptList/ReceiptListWebviewContainer"),
);

const WarrantyListWebviewContainer = lazyLoadComponent(
    () => import("entity/warranty/WarrantyListContainer/WarrantyListWebviewContainer"),
);
const WarrantyWebviewContainer = lazyLoadComponent(
    () => import("entity/warranty/Warranty/WarrantyWebviewContainer"),
);
const WarrantyAddWebviewContainer = lazyLoadComponent(
    () => import("entity/warranty/Warranty/WarrantyAddWebviewContainer"),
);
const WarrantyServiceWebviewContainer = lazyLoadComponent(
    () => import("entity/warranty/WarrantyService/WarrantyServiceWebviewContainer"),
);
const WarrantyServiceComponent = lazyLoadComponent(
    () => import("entity/warranty/WarrantyService/WarrantyService"),
);

const PhotoListWebviewContainer = lazyLoadComponent(
    () => import("entity/photo/PhotoList/PhotoListWebviewContainer"),
);

const ShiftListWebviewContainer = lazyLoadComponent(
    () => import("entity/timeClock/ShiftList/ShiftListContainer/ShiftListWebviewContainer"),
);

const ClockInClockOutWebviewContainer = lazyLoadComponent(
    () => import("entity/timeClock/ClockInClockOut/ClockInClockOutWebviewContainer"),
);

const ReportListWebviewContainer = lazyLoadComponent(
    () => import("entity/reporting/ReportList/ReportListWebviewContainer"),
);

const WorkInProgressReportWebviewContainer = lazyLoadComponent(
    () => import("entity/reporting/WorkInProgressReport/WorkInProgressReportWebviewContainer"),
);

const DailyLogCreationByJobReportWebviewContainer = lazyLoadComponent(
    () =>
        import(
            "entity/reporting/DailyLogCreationByJobReport/DailyLogCreationByJobReportWebviewContainer"
        ),
);

const BudgetedVsProjectedReportWebviewContainer = lazyLoadComponent(
    () =>
        import(
            "entity/reporting/BudgetedVsProjectedReport/BudgetedVsProjectedReportWebviewContainer"
        ),
);

const HoursWorkedByJobWebviewContainer = lazyLoadComponent(
    () => import("entity/reporting/HoursWorkedByJobReport/HoursWorkedByJobWebviewContainer"),
);

const HoursWorkedByEmployeeWebviewContainer = lazyLoadComponent(
    () =>
        import(
            "entity/reporting/HoursWorkedByEmployeeReport/HoursWorkedByEmployeeWebviewContainer"
        ),
);

const DailyLogsByUserReportWebviewContainer = lazyLoadComponent(
    () => import("entity/reporting/DailyLogsByUserReport/DailyLogsByUserReportWebviewContainer"),
);

const LaborActualsVsBudgetedReportWebviewContainer = lazyLoadComponent(
    () =>
        import(
            "entity/reporting/LaborActualsVsBudgetedReport/LaborActualsVsBudgetedReportWebviewContainer"
        ),
);

const CashflowReportWebviewContainer = lazyLoadComponent(
    () => import("entity/reporting/CashflowReport/CashflowReportWebviewContainer"),
);

const ProfitabilityReportWebviewContainer = lazyLoadComponent(
    () => import("entity/reporting/ProfitabilityReport/ProfitabilityReportWebviewContainer"),
);

const InvoicingReportWebviewContainer = lazyLoadComponent(
    () => import("entity/reporting/InvoicingReport/InvoicingReportWebviewContainer"),
);

const LeadActivitiesBySalespersonReportWebviewContainer = lazyLoadComponent(
    () =>
        import(
            "entity/reporting/LeadActivitiesBySalespersonReport/LeadActivitiesBySalespersonReportWebviewContainer"
        ),
);

const LeadStatusBySourceReportWebviewContainer = lazyLoadComponent(
    () =>
        import(
            "entity/reporting/LeadStatusBySourceReport/LeadStatusBySourceReportWebviewContainer"
        ),
);

const LeadCountBySalespersonReportWebviewContainer = lazyLoadComponent(
    () =>
        import(
            "entity/reporting/LeadCountBySalespersonReport/LeadCountBySalespersonReportWebviewContainer"
        ),
);

const BaselineVsActualDurationReportWebviewContainer = lazyLoadComponent(
    () =>
        import(
            "entity/reporting/BaselineVsActualDurationReport/BaselineVsActualDurationReportWebviewContainer"
        ),
);

const ChangeOrderProfitReportWebviewContainer = lazyLoadComponent(
    () =>
        import("entity/reporting/ChangeOrderProfitReport/ChangeOrderProfitReportWebviewContainer"),
);

const SchedulePercentCompleteByJobReportWebviewContainer = lazyLoadComponent(
    () =>
        import(
            "entity/reporting/SchedulePercentCompleteByJobReport/SchedulePercentCompleteByJobReportWebviewContainer"
        ),
);

const SpecificationBookWebviewContainer = lazyLoadComponent(
    () => import("entity/specification/mobile/SpecificationBookWebviewContainer"),
);

const TaskSinglePrintPage = lazyLoadComponent(
    () => import("entity/task/list/print/TaskSinglePrintPage"),
);
const TaskBulkPrintPage = lazyLoadComponent(
    () => import("entity/task/list/print/TaskBulkPrintPage"),
);

const SignDocumentWebviewContainer = lazyLoadComponent(
    () =>
        import(
            "commonComponents/entity/media/signatures/SignDocument/webview/SignDocumentWebviewContainer"
        ),
);

const SignatureRequestWebviewContainer = lazyLoadComponent(
    () =>
        import(
            "commonComponents/entity/media/signatures/SignatureRequest/webview/SignatureRequestWebviewContainer"
        ),
);

const SignatureFlowWebviewContainer = lazyLoadComponent(
    () => import("entity/signature/SignatureFlow/webview/SignatureFlowWebviewContainer"),
);

const CommentThread = lazyLoadComponent(
    () => import("commonComponents/entity/comment/CommentContainer/CommentThread/CommentThread"),
);

const StuckPaymentUtility = lazyLoadComponent(
    () => import("entity/onlinePayment/StuckPaymentUtility/StuckPaymentUtility"),
);

const BRIUpdateUtility = lazyLoadComponent(
    () => import("entity/builderRiskInsurance/BRIUpdateUtility/BRIUpdateUtility"),
);

const MobileLoginEmailUsernameEntry = lazyLoadComponent(
    () => import("entity/login/MobileLoginEmailUsernameEntry/MobileLoginEmailUsernameEntry"),
);

const LoginEmailUsernameEntry = lazyLoadComponent(
    () => import("entity/login/LoginEmailUsernameEntry/LoginEmailUsernameEntry"),
);
const Login = lazyLoadComponent(() => import("entity/login/Login/Login"));
const IdpReAuth = lazyLoadComponent(() => import("entity/login/IdpReAuth/IdpReAuth"));

const MobileLoginByCode = lazyLoadComponent(
    () => import("entity/login/MobileLoginByCode/MobileLoginByCode"),
);

const FinancialDocumentCollection = lazyLoadComponent(
    () => import("entity/onlinePayment/FinancialDocumentCollection/FinancialDocumentCollection"),
);
const OwnerSummary = lazyLoadComponent(() => import("entity/OwnerSummary/OwnerSummary"));
const SubSummary = lazyLoadComponent(() => import("entity/subSummary/SubSummary"));
const CostCodeList = lazyLoadComponent(() => import("entity/costCode/CostCodeList/CostCodeList"));

const JobCostingBudget = lazyLoadComponent(
    () => import("entity/budget/JobCostingBudget/JobCostingBudget"),
);
const JobCostingBudgetWebviewContainer = lazyLoadComponent(
    () => import("entity/budget/JobCostingBudget/JobCostingBudgetWebviewContainer"),
);
const LeadActivityWebviewContainer = lazyLoadComponent(
    () => import("entity/lead/LeadActivity/LeadActivityWebviewContainer"),
);
const OwnerSetupWebviewContainer = lazyLoadComponent(
    () => import("entity/OwnerSetup/OwnerSetupWebviewContainer"),
);
const ExpectedBudget = lazyLoadComponent(
    () => import("entity/budget/ExpectedBudget/ExpectedBudget"),
);
const OwnerBudget = lazyLoadComponent(() => import("entity/budget/OwnerBudget/OwnerBudget"));
const AdminOnlinePaymentReports = lazyLoadComponent(
    () => import("entity/onlinePayment/AdminOnlinePaymentReports/AdminOnlinePaymentReports"),
);
const OnlinePaymentReports = lazyLoadComponent(
    () => import("./entity/onlinePayment/common/OnlinePaymentReports"),
);
const OnlinePaymentsList = lazyLoadComponent(
    () => import("./entity/onlinePayment/OnlinePaymentList/OnlinePaymentList"),
);

const LeadListContainer = lazyLoadComponent(
    () => import("entity/lead/LeadListContainer/LeadListContainer"),
);
const AdminMain = lazyLoadComponent(() => import("entity/admin/AdminMain"));

const BTAdminUsersList = lazyLoadComponent(
    () => import("entity/admin/BTAdminUsersList/RouteBTAdminUsersList"),
);

const CustomerFeedbackList = lazyLoadComponent(
    () => import("entity/admin/CustomerFeedback/RouteCustomerFeedbackList"),
);

const LowesAccountStatus = lazyLoadComponent(
    () => import("entity/admin/LowesAccountStatus/LowesAccountStatus"),
);

const AdminUsernameLookup = lazyLoadComponent(
    () => import("entity/AdminUsernameLookup/AdminUsernameLookup"),
);
const SpamEmailRemoval = lazyLoadComponent(
    () => import("entity/admin/SpamEmailRemoval/SpamEmailRemoval"),
);
const AdminAppSettings = lazyLoadComponent(() => import("entity/admin/AppSettings/AppSettings"));

const AdminBuilders = lazyLoadComponent(() => import("entity/admin/AdminBuilders/AdminBuilders"));

const ImpersonationRedirect = lazyLoadComponent(
    () => import("entity/admin/AdminBuilders/ImpersonationRedirect/ImpersonationRedirect"),
);

const AdminSubscriptionManager = lazyLoadComponent(
    () => import("entity/adminSubscriptionManager/AdminSubscriptionManager"),
);

const WorkdayExceptionBDS = lazyLoadComponent(
    () => import("entity/schedule/WorkDayException/WorkdayException"),
);

const SelectionListContainer = lazyLoadComponent(
    () => import("entity/selection/SelectionListContainer/SelectionListContainer"),
);

const SubSelectionsListPageComponent = lazyLoadComponent(() =>
    import("entity/selection/SelectionsListPage/SubSelectionsListPage").then((m) => ({
        default: m.SubSelectionsListPage,
    })),
);

const SubSelectionsListWebviewContainer = lazyLoadComponent(
    () => import("entity/selection/SelectionsListPage/SubSelectionsListWebviewContainer"),
);

const MobileDownload = lazyLoadComponent(() => import("entity/mobileDownload/MobileDownload"));

const EmailLinkRedirect = lazyLoadComponent(
    () => import("entity/emailLinkRedirect/EmailLinkRedirect"),
);

const PurchasingDashboard = lazyLoadComponent(
    () => import("entity/rebate/PurchasingDashboard/PurchasingDashboard"),
);

const ClientDefaultJobPermissions = lazyLoadComponent(
    () => import("./entity/contact/Contact/Client/ClientDefaultJobPermissions"),
);

const ClientUpdateList = lazyLoadComponent(
    () => import("entity/clientUpdate/list/ClientUpdateList"),
);
const ClientUpdatePrint = lazyLoadComponent(() => import("entity/clientUpdate/ClientUpdatePrint"));

const MultiFactorAuthModal = lazyLoadComponent(
    () =>
        import(
            "commonComponents/utilities/MultiFactorAuthModal/multiFactorAuthModal/MultiFactorAuthModal"
        ),
);

const PostLogin = lazyLoadComponent(() => import("entity/login/PostLogin/PostLogin"));
const StandaloneLoginFrame = lazyLoadComponent(
    () => import("entity/login/StandaloneLoginFrame/StandaloneLoginFrame"),
);

const EntityLinkRouter = lazyLoadComponent(
    () => import("helpers/routing/EntityLinkRouter/EntityLinkRouter"),
);

const AccountingWebhookTestingPage = lazyLoadComponent(
    () => import("entity/AccountingWebhookTesting/AccountingWebhookTesting"),
);

const AdminInAppMessages = lazyLoadComponent(
    () => import("entity/admin/InAppMessages/InAppMessages"),
);
const AdminNotificationServiceTemplates = lazyLoadComponent(
    () =>
        import("entity/admin/AdminNotificationServiceTemplates/AdminNotificationServiceTemplates"),
);

const SelectionChoiceSignaturePadPage = lazyLoadComponent(
    () =>
        import(
            "entity/selectionChoice/common/SelectionChoiceSignaturePad/SelectionChoiceSignaturePadPage"
        ),
);

const SelectionFavoriteListPage = lazyLoadComponent(
    () => import("entity/selection/SelectionFavoriteList/SelectionFavoriteListPage"),
);

const ProjectedTotalPage = lazyLoadComponent(
    () => import("entity/selection/ProjectedTotal/ProjectedTotalPage"),
);

const PlansList = lazyLoadComponent(() => import("entity/Plans/PlansList/PlansList"));
const PlanPageDetails = lazyLoadComponent(
    () => import("entity/Plans/PlanPageDetails/PlanPageDetails"),
);
const PlanRevisionsReview = lazyLoadComponent(() =>
    import("entity/Plans/PlanRevisionsReview/PlanRevisionsReview").then((m) => ({
        default: m.PlanRevisionsReview,
    })),
);

// #endregion

// #region Page Definitions
const OrgLinkNotConnectedEmptyStatePage = () => {
    const urlParams = useParams<{ fromLoginPage: string }>();
    const isFromLogin = urlParams.fromLoginPage === "true";
    return <OrgLinkNotConnectedEmptyState fromLoginPage={isFromLogin} />;
};

const IndexPage = () => <div />;

const MultiFactorAuthPage = (reactRouterProps: RouteComponentProps) => {
    const urlParams: any = reactRouterProps.match.params;
    const credentialId = urlParams.credentialId;
    return <MultiFactorAuthModal {...reactRouterProps} credentialId={credentialId} />;
};

const ShiftPage = (reactRouterProps: RouteComponentProps) => {
    const urlParams: any = reactRouterProps.match.params;
    const id = parseInt(urlParams.id);
    const jobId = parseInt(urlParams.jobId);

    return <Shift id={id} jobId={jobId} {...reactRouterProps} />;
};
const ClockInClockOutPage = (
    reactRouterProps: RouteComponentProps<{
        id: string;
        jobId: string;
        isClockIn: string;
        inMultiple: string;
    }>,
) => {
    const urlParams: any = reactRouterProps.match.params;
    const id = parseInt(urlParams.id);
    const jobId = parseInt(urlParams.jobId);
    const isClockIn = urlParams.isClockIn === "true";
    const inMultiple = urlParams.isMultiple === "true";

    return (
        <ClockInClockOut
            id={id}
            jobId={jobId}
            isClockIn={isClockIn}
            isMultiple={inMultiple}
            {...reactRouterProps}
        />
    );
};

const SignupFormPage = (
    reactRouterProps: RouteComponentProps<{
        quoteID: string;
        code: string;
        autoCode: string;
        salesforceAccountID: string;
        salesforceOpportunityID: string;
        rewinBuilderID: string;
        sfRewinContactLoginID: string;
        isRewin: string;
        testExperience: string;
    }>,
) => {
    const urlParams = reactRouterProps.match.params;
    const signupCode = urlParams.code;
    const isAutoCode = urlParams.autoCode ? urlParams.autoCode === "true" : false;
    // Existing routes in our legacy code only pass up the rewin builder id. In this situation we are passing 0 for the salesforce account and opp ids. If 0 is passed we want to set these values to undefined.
    const salesforceAccountID =
        urlParams.salesforceAccountID === "0" ? undefined : urlParams.salesforceAccountID;
    const salesforceOpportunityID =
        urlParams.salesforceOpportunityID === "0" ? undefined : urlParams.salesforceOpportunityID;
    const rewinBuilderID = urlParams.rewinBuilderID;
    const sfRewinContactLoginID = urlParams.sfRewinContactLoginID;
    const testExperience = urlParams.testExperience;

    // eslint-disable-next-line deprecate/member-expression
    const qs: any = queryString.parse(reactRouterProps.location.search);
    const websiteTestExperience = qs.t;
    let leadSource = undefined;
    if (qs.p_usrc !== undefined) {
        leadSource = new LeadSourceValues({
            source: qs.p_usrc,
            medium: qs.p_med,
            campaign: qs.p_camp,
            term: qs.p_term,
            content: qs.p_content,
            gclid: qs.p_gclid,
            msclkid: qs.p_msclkid,
            fbclid: qs.p_fbclid,
            sourceTags: qs.p_src,
            wbraid: qs.p_wbraid,
            gbraid: qs.p_gbraid,
            campaignId: qs.p_campid,
            reference: qs.p_refer,
            landing: qs.p_lp,
            formURL: reactRouterProps.location.pathname,
        });
    }

    return (
        <SignupForm
            quoteID=""
            code={signupCode}
            isAutoCode={isAutoCode}
            salesforceAccountID={salesforceAccountID}
            salesforceOpportunityID={salesforceOpportunityID}
            rewinBuilderID={rewinBuilderID}
            sfRewinContactLoginID={sfRewinContactLoginID}
            testExperience={testExperience}
            websiteTestExperience={websiteTestExperience}
            leadSource={leadSource}
            {...reactRouterProps}
        />
    );
};

const SignupFormWithQuotePage = (
    reactRouterProps: RouteComponentProps<{
        quoteID: string;
        code: string;
        autoCode: string;
        salesforceAccountID: string;
        salesforceOpportunityID: string;
        rewinBuilderID: string;
        sfRewinContactLoginID: string;
        isRewin: string;
        testExperience: string;
    }>,
) => {
    const urlParams = reactRouterProps.match.params;
    const quoteID = urlParams.quoteID;
    const leadSource = undefined;

    return (
        <SignupForm
            quoteID={quoteID}
            code=""
            isAutoCode={false}
            salesforceAccountID=""
            salesforceOpportunityID=""
            rewinBuilderID=""
            sfRewinContactLoginID=""
            testExperience=""
            websiteTestExperience=""
            leadSource={leadSource}
            {...reactRouterProps}
        />
    );
};

const SchedulePage = (reactRouterProps: RouteComponentProps<{ id: string; jobId: string }>) => {
    const urlParams = reactRouterProps.match.params;
    const scheduleItemId = parseInt(urlParams.id);
    const jobId = parseInt(urlParams.jobId);

    // eslint-disable-next-line deprecate/member-expression
    const qs: any = queryString.parse(reactRouterProps.location.search, { parseBooleans: true });

    const entityDefaultParams =
        Object.keys(qs).length > 0 ? new ScheduleEntityDefaultParams(qs) : undefined;
    const disableLinks = qs.disableRFI;

    const initialTab = qs.initialTab ? Number(qs.initialTab) : undefined;

    return (
        <Schedule
            id={scheduleItemId}
            jobId={jobId}
            entityDefaultParams={entityDefaultParams}
            disableLinks={disableLinks}
            initialTab={initialTab}
            {...reactRouterProps}
        />
    );
};

const LienWaiverReviewPage = (reactRouterProps: RouteComponentProps) => {
    return <LienWaiverReview {...reactRouterProps} />;
};

const LienWaiverReviewPageExternal = (
    reactRouterProps: RouteComponentProps<{
        builderId: string;
        subId: string;
        shareToken?: string;
    }>,
) => {
    const returnParams = parseLienWaiverReturnParams(reactRouterProps.location.search);
    return (
        <LienWaiverReviewExternal
            shareToken={reactRouterProps.match.params.shareToken}
            encodedBuilderId={reactRouterProps.match.params.builderId}
            encodedSubId={reactRouterProps.match.params.subId}
            signedLienWaiverId={returnParams.signedLienWaiverId}
            paymentDestination={returnParams.paymentDestination}
            {...reactRouterProps}
        />
    );
};

const CostCodesExportToAccountingPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const urlValues: any = reactRouterProps.match.params;
    const checkExpenseAccount = urlValues.checkExpenseAccount === "true";
    const serviceItemsOnly = urlValues.serviceItemsOnly === "true";
    const doRefresh = urlValues.doRefresh === "true";
    let costCodeIds: number[];
    if (qsValues.costCodeIds !== "") {
        costCodeIds = stringToArray(qsValues.costCodeIds);
    } else {
        costCodeIds = [];
    }
    return (
        <CostCodesExportToAccounting
            {...reactRouterProps}
            checkExpenseAccount={checkExpenseAccount}
            serviceItemsOnly={serviceItemsOnly}
            costCodeIds={costCodeIds}
            doRefresh={doRefresh}
            isModal={true}
        />
    );
};
const CostCodesImportFromAccountingPage = (reactRouterProps: RouteComponentProps) => {
    return <CostCodeImportFromAccounting {...reactRouterProps} />;
};
const RelatedRfiPage = (
    reactRouterProps: RouteComponentProps<{
        linkedId: string;
        linkedType: string;
        builderId: string;
        jobId: string;
    }>,
) => {
    const urlParams = reactRouterProps.match.params;
    const builderId = parseInt(urlParams.builderId);
    const jobId = parseInt(urlParams.jobId);
    const linkedId = parseInt(urlParams.linkedId);
    const linkedType = parseInt(urlParams.linkedType);

    return (
        <RelatedRfi
            builderId={builderId}
            jobId={jobId}
            linkedId={linkedId}
            linkedType={linkedType}
            {...reactRouterProps}
        />
    );
};

const RFIPage = (reactRouterProps: RouteComponentProps) => {
    const urlParams: any = reactRouterProps.match.params;
    const rfiId = parseInt(urlParams.id);
    const jobId = parseInt(urlParams.jobId);

    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const linkedId: number = qsValues.linkedId;
    const linkedType: number = qsValues.linkedType;

    return (
        <RFI
            id={rfiId}
            jobId={jobId}
            disableLinks={linkedId !== undefined}
            linkedId={linkedId}
            linkedType={linkedType}
            {...reactRouterProps}
        />
    );
};

const PurchaseOrderPrintPage = (
    reactRouterProps: RouteComponentProps<{
        shareToken?: string;
    }>,
) => {
    const params = reactRouterProps.match.params;
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const purchaseOrderIds = stringToArray(qsValues.poid);
    const isPdfMode: boolean = qsValues.isPDFMode ? qsValues.isPDFMode : false;
    const showPaymentInfo: boolean = qsValues.showPaymentInfo ? qsValues.showPaymentInfo : false;

    return (
        <PurchaseOrderPrint
            ids={purchaseOrderIds}
            isPdfMode={isPdfMode}
            showPaymentInfoForPdf={showPaymentInfo}
            shouldAddMargin={isPdfMode}
            shareToken={params.shareToken}
        />
    );
};

const WarrantyPrintPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const warrantyIds = stringToArray(qsValues.ids);

    return <WarrantyPrint ids={warrantyIds} />;
};

const WarrantyDetailsPageInner = ({ jobIds }: { jobIds: number[] }) => {
    const routeProps = useRouteProps();
    const urlParams = useParams<{ id: string; jobId: string }>();
    const warrantyId = parseInt(urlParams.id);
    const jobId = parseInt(urlParams.jobId);
    const isBuilder = isInPortal({ builder: true });
    const title = isBuilder ? "Warranty" : "Warranty request";

    useRedirectOnJobPickerSwitch(jobIds, routeProps.history);

    return (
        <>
            <PageTitle title={title} />
            <Warranty id={warrantyId} jobId={jobId} {...routeProps} />
        </>
    );
};
// ListPageLayout invokes `props.component(...)` as a plain function, so the
// direct `component` here must NOT use hooks. Delegate to an inner element.
const WarrantyPage = (listProps: IListPageChildProps) => (
    <WarrantyDetailsPageInner jobIds={listProps.jobIds} />
);

// When jobId=0 we render WarrantyAddJobPickerModal instead of mounting <Warranty>,
// which would otherwise show a full-page DependentEntitiesPrompt via withJobPrompt's
// page-mode rendering.
const WarrantyAddPageInner = ({ jobIds }: { jobIds: number[] }) => {
    const routeProps = useRouteProps();
    const urlParams = useParams<{ jobId: string }>();
    const jobId = Number(urlParams.jobId);

    useRedirectOnJobPickerSwitch(jobIds, routeProps.history);

    if (!isValidId(jobId)) {
        return (
            <WarrantyAddJobPickerModal
                history={routeProps.history}
                location={routeProps.location}
            />
        );
    }

    return (
        <>
            <PageTitle title="Add Warranty" />
            <Warranty id={0} jobId={jobId} {...routeProps} />
        </>
    );
};
const WarrantyAddPage = (listProps: IListPageChildProps) => (
    <WarrantyAddPageInner jobIds={listProps.jobIds} />
);

const WarrantyServicePageInner = ({ jobIds }: { jobIds: number[] }) => {
    const routeProps = useRouteProps();
    const routeMatch = useRouteMatch<{
        warrantyId: string;
        jobId: string;
        serviceId: string;
        completed: string;
    }>();
    const params = routeMatch.params;
    const builderInfo = useContext(BuilderInfoContext);
    const flagOn = !!builderInfo?.flags.warrantiesModalConversion;

    // Same reason as the sibling warranty pages: this route is registered with
    // jobPickerSelectMode Multiple, so changing the picker mid-page would otherwise leave
    // the user editing an appointment for a job no longer in scope.
    useRedirectOnJobPickerSwitch(jobIds, routeProps.history);

    // The flat route names the warranty id :warrantyId for readability, but
    // IWarrantyServiceUrlProps calls it `id` (same value as the relative service
    // route's :id). Build a correctly-typed match that maps warrantyId -> id.
    const match: RouteComponentProps<IWarrantyServiceUrlProps>["match"] = {
        ...routeMatch,
        params: {
            id: params.warrantyId,
            jobId: params.jobId,
            serviceId: params.serviceId,
            completed: params.completed,
        },
    };

    // Appointments never render for owners (the owner GET can't populate the full
    // WarrantyServiceEntity). Send them to the warranty page instead.
    if (isInPortal({ owner: true })) {
        return (
            <Redirect
                to={routes.warranty.getDetailsLink(Number(params.warrantyId), Number(params.jobId))}
            />
        );
    }

    // Flag-OFF: the standalone appointment page is new (flag-ON only). Redirect to the
    // warranty detail page so the legacy modal flow handles the appointment instead.
    if (!flagOn) {
        return (
            <Redirect
                to={routes.warranty.getDetailsLink(Number(params.warrantyId), Number(params.jobId))}
            />
        );
    }

    return (
        <>
            <PageTitle title="Schedule warranty service" />
            <WarrantyServiceComponent
                {...routeProps}
                match={match}
                renderMode="page"
                warrantyId={Number(params.warrantyId)}
                warrantyJobId={Number(params.jobId)}
                warrantyServiceId={Number(params.serviceId)}
                warrantyServiceCompleted={params.completed === "true"}
                updateServiceAppointmentsList={() => {}}
            />
        </>
    );
};
// ListPageLayout invokes `props.component(...)` as a plain function, so this must NOT use
// hooks — delegate to the inner element (same shape as WarrantyPage / WarrantyAddPage).
const WarrantyServicePage = (listProps: IListPageChildProps) => (
    <WarrantyServicePageInner jobIds={listProps.jobIds} />
);

const QuestionReportPrintPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const jobId: number = qsValues.jobId;

    return <QuestionReportPrint jobId={jobId} />;
};

const QuestionReportPage = (
    reactRouterProps: RouteComponentProps<{ id: string; jobIds: string }>,
) => {
    const urlParams = reactRouterProps.match.params;
    const questionId = parseInt(urlParams.id);
    const jobIds: number[] = urlParams.jobIds ? stringToArray(urlParams.jobIds) : [];

    return (
        <QuestionReport
            {...reactRouterProps}
            questionId={questionId}
            jobIds={jobIds}
            entityType={ListEntityType.SurveyReportAnswers}
        />
    );
};

const SelectionPrintPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const selectionIds: number[] = qsValues.ids ? stringToArray(qsValues.ids) : [];
    let sortColumn: SelectionsDynamicGridColumnTypes | undefined;
    let sortDirection: DirectionTypes | undefined;
    const jobIds: number[] = stringToArray(qsValues.jobIds);
    let selectedView: SelectionListContainerTabs | undefined;
    let shouldPrintAll: boolean = false;

    if (qsValues.sortColumn) {
        sortColumn = parseInt(qsValues.sortColumn);
    }
    if (qsValues.sortDirection) {
        sortDirection = parseInt(qsValues.sortDirection);
    }

    if (qsValues.shouldPrintAll) {
        shouldPrintAll = Boolean(qsValues.shouldPrintAll);
    }

    if (qsValues.selectedView) {
        selectedView = parseInt(qsValues.selectedView);
    }

    return (
        <SelectionPrint
            ids={selectionIds}
            shouldPrintAll={shouldPrintAll}
            jobIds={jobIds}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            selectedView={selectedView}
        />
    );
};

const SpecificationPrintPage = () => {
    const qsValues = useQueryString<{ specificationIds: string }>();
    const ids: number[] = stringToArray(qsValues.specificationIds);
    return <SpecificationPrint ids={ids} />;
};

const DependentSelectionsPage = (
    reactRouterProps: RouteComponentProps<{ choiceId: string; jobId: string }>,
) => {
    const choiceId = parseInt(reactRouterProps.match.params.choiceId);
    const jobId = parseInt(reactRouterProps.match.params.jobId);
    return <DependentSelections choiceId={choiceId} jobId={jobId} {...reactRouterProps} />;
};

const SelectionPage = (
    reactRouterProps: RouteComponentProps<{ id: string; jobId: string; lockingChoiceId?: string }>,
) => {
    const id = parseInt(reactRouterProps.match.params.id);
    const jobId = parseInt(reactRouterProps.match.params.jobId);

    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    let lockingChoiceId: number | undefined;
    if (qsValues.lockingChoiceId) {
        lockingChoiceId = parseInt(qsValues.lockingChoiceId);
    }

    return (
        <Selection
            id={id}
            jobId={jobId}
            lockingSelectionChoiceId={lockingChoiceId}
            {...reactRouterProps}
        />
    );
};

const SelectionChoicePage = (
    reactRouterProps: RouteComponentProps<{
        choiceId: string;
        jobId: string;
        selectionId?: string;
    }>,
) => {
    const choiceId = parseInt(reactRouterProps.match.params.choiceId);
    const jobId = parseInt(reactRouterProps.match.params.jobId);
    const selectionId =
        reactRouterProps.match.params.selectionId !== undefined
            ? parseInt(reactRouterProps.match.params.selectionId)
            : undefined;
    return (
        <SelectionChoice
            id={choiceId}
            jobId={jobId}
            selectionId={selectionId}
            {...reactRouterProps}
        />
    );
};

const CopyChoiceFromSelectionPage = (
    reactRouterProps: RouteComponentProps<{ targetSelectionId: string; jobId: string }>,
) => {
    const targetSelectionId = parseInt(reactRouterProps.match.params.targetSelectionId);
    const jobId = parseInt(reactRouterProps.match.params.jobId);
    return (
        <CopyChoiceFromSelection
            // eslint-disable-next-line deprecate/member-expression
            onClose={() => routesWebforms.closeModal()}
            id={targetSelectionId}
            jobId={jobId}
            {...reactRouterProps}
        />
    );
};

const SelectionOptionPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps<{
        selectionOptionId?: string;
        jobId: string;
        stateOverride?: string;
        selectionOptionGroupId: string;
    }>,
) => {
    const stringSelectionOptionId = reactRouterProps.match.params.selectionOptionId;
    const selectionOptionId = stringSelectionOptionId
        ? parseInt(stringSelectionOptionId)
        : undefined;
    const stringSelectionOptionGroupId = reactRouterProps.match.params.selectionOptionGroupId;
    const selectionOptionGroupId = stringSelectionOptionGroupId
        ? parseInt(stringSelectionOptionGroupId)
        : 0;

    const jobId = parseInt(reactRouterProps.match.params.jobId);
    const stateOverride = reactRouterProps.match.params
        .stateOverride as SelectionOptionStateOverride;
    return (
        <SelectionOptionRouteGuard
            jobId={jobId}
            reimaginedContent={
                <>
                    <PageTitle title="Selections" />
                    <SelectionOption
                        jobId={jobId}
                        jobName={listLayoutProps.jobName}
                        isTemplate={listLayoutProps.isTemplateMode}
                        entity={{
                            selectionOptionLineItems: [],
                            lineItemDefaults: { keysThatBreakCostLink: [] },
                            selectionOptionGroups: [],
                        }}
                        selectionOptionId={selectionOptionId}
                        stateOverride={stateOverride}
                        selectionOptionGroupId={selectionOptionGroupId}
                        {...reactRouterProps}
                    />
                </>
            }
            fallbackContent={<NotFoundError />}
        />
    );
};

const AllowancePrintPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const jobId = parseInt(qsValues.jobId);

    let id = 0;
    if (qsValues.id) {
        id = parseInt(qsValues.id);
    }

    return <AllowancePrint id={id} jobId={jobId} />;
};

const SurveyPrintPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const surveyIds = stringToArray(qsValues.ids);

    return <SurveyPrint ids={surveyIds} />;
};

const IntegrationsSettingsPage = (reactRouterProps: RouteComponentProps<{ type: string }>) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search, {
        parseBooleans: true,
    });
    const urlParams = reactRouterProps.match.params;

    const forceLogin: boolean = qsValues.forceLogin;
    const integrationType = parseInt(urlParams.type);
    return (
        <IntegrationsSettings
            {...reactRouterProps}
            forceLogin={forceLogin}
            integrationType={integrationType}
        />
    );
};

const ReconsentPage = (reactRouterProps: RouteComponentProps) => {
    return <Reconsent {...reactRouterProps} />;
};

const Auth0RedirectPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);

    return (
        <Auth0Redirect code={qsValues.code} {...reactRouterProps} redirectLink={qsValues.state} />
    );
};

const ToDoPrintPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const ids: number[] = stringToArray(qsValues.toDoIds);
    const jobsiteIds: number[] = qsValues.jobsiteIds ? stringToArray(qsValues.jobsiteIds) : [];
    const printDisplayOptions: ToDoPrintDisplayOptions = new ToDoPrintDisplayOptions({
        completedDetails: qsValues.completedDetails,
        customFields: qsValues.customFields,
        discussions: qsValues.discussions,
    });

    return (
        <ToDoPrint ids={ids} jobsiteIds={jobsiteIds} printDisplayOptions={printDisplayOptions} />
    );
};

const ToDoPage = (
    reactRouterProps: RouteComponentProps<{ id: string; jobId: string; isFromRfi?: string }>,
) => {
    const urlParams = reactRouterProps.match.params;
    const toDoId = parseInt(urlParams.id);
    const jobId = parseInt(urlParams.jobId);
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search, {
        parseBooleans: true,
    });
    const dailyLogId = parseInt(qsValues.dailyLogId);
    const scheduleId = parseInt(qsValues.scheduleId);
    const isFromRfi = urlParams.isFromRfi === "true";
    const refreshParentAfterSave = qsValues.refreshParentAfterSave;
    return (
        <ToDo
            id={toDoId}
            jobId={jobId}
            dailyLogId={dailyLogId}
            scheduleId={scheduleId}
            isFromRfi={isFromRfi}
            refreshParentAfterSave={refreshParentAfterSave}
            {...reactRouterProps}
        />
    );
};

const LeadPage = (reactRouterProps: RouteComponentProps<{ id: string }>) => {
    const id: number = parseInt(reactRouterProps.match.params.id);
    // eslint-disable-next-line deprecate/member-expression
    const { showRelatedJob, showAddressMap, selectedTab } = queryString.parse(
        reactRouterProps.location.search,
    ) as any;
    return (
        <Lead
            {...reactRouterProps}
            leadId={id}
            showRelatedJob={showRelatedJob !== "false"}
            showAddressMap={showAddressMap !== "false"}
            selectedTab={selectedTab}
        />
    );
};

const LeadPrintPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const leadIds = stringToArray(qsValues.ids);
    const portalLoginType = qsValues.portalTypeLogin;
    const sortDirection = qsValues.sortDirection;
    const sortExpression = qsValues.sortExpression;

    return (
        <LeadPrint
            ids={leadIds}
            portalLoginType={portalLoginType}
            sortDirection={sortDirection}
            sortExpression={sortExpression}
            printOnLoad={true}
        />
    );
};

const LeadActivityPage = (
    reactRouterProps: RouteComponentProps<{ id: string; leadId: string; openActivity: string }>,
) => {
    const id: number = parseInt(reactRouterProps.match.params.id);
    const leadId: number = parseInt(reactRouterProps.match.params.leadId);
    const openActivity: OpenActivityType = parseInt(reactRouterProps.match.params.openActivity);
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const activityDate: moment.Moment | undefined = qsValues.activityDate
        ? moment(qsValues.activityDate as string)
        : undefined;
    return (
        <LeadActivity
            id={id}
            leadId={leadId}
            openActivity={openActivity}
            activityDate={activityDate}
            {...reactRouterProps}
        />
    );
};

const BidPrintPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const bidId = parseInt(qsValues.id);
    return <BidPrint id={bidId} />;
};

const BidPackagePrintPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const bidPackageIds = stringToArray(qsValues.ids);
    return <BidPackagePrint ids={bidPackageIds} />;
};

const ChangeOrderPrintPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const coIds = stringToArray(qsValues.ids);
    const sortDirection =
        qsValues.sortDir && qsValues.sortDir.toLowerCase() === "desc"
            ? DirectionTypes.DESC
            : DirectionTypes.ASC;

    return (
        <ChangeOrderPrint ids={coIds} sortColumn={qsValues.sortExp} sortDirection={sortDirection} />
    );
};

const ChangeOrderOwnerRequestPage = (
    reactRouterProps: RouteComponentProps<{ id: string; jobId: string }>,
) => {
    const urlParams = reactRouterProps.match.params;
    const coId = parseInt(urlParams.id);
    const jobId = parseInt(urlParams.jobId);
    return <ChangeOrderOwnerRequest changeOrderId={coId} jobId={jobId} />;
};

// Once subSummary is out of webforms, we can remove the isInModal param and related logic.
const ChangeOrderPage = (
    reactRouterProps: RouteComponentProps<{
        id: string;
        jobId: string;
        // Don't copy this pattern for match props, change orders is having to do redirects since the changeOrder list is still native.
        platform?: string;
        mobileVersion?: string;
    }>,
) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search, {
        parseBooleans: true,
    });
    const urlParams = reactRouterProps.match.params;

    const coId = parseInt(urlParams.id);
    const jobId = parseInt(urlParams.jobId);
    let openedFrom = OpenedFrom.Nothing;
    if (qsValues.fromBid) {
        openedFrom = OpenedFrom.Bid;
    }
    if (qsValues.fromRFI) {
        openedFrom = OpenedFrom.Rfi;
    }

    const isInWebformsModal = qsValues.isModal;

    return (
        <ChangeOrder
            id={coId}
            jobId={jobId}
            openedFrom={openedFrom}
            isInWebformsModal={isInWebformsModal}
            {...reactRouterProps}
        />
    );
};

const ChangeOrderDetailsPage = (
    listPageProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps<{
        id: string;
        jobId: string;
        // Don't copy this pattern for match props, change orders is having to do redirects since the changeOrder list is still native.
        platform?: string;
        mobileVersion?: string;
    }>,
) => {
    const routeParams = reactRouterProps.match.params;
    const coId = parseInt(routeParams.id);
    const jobId = parseInt(routeParams.jobId);

    return (
        <>
            <PageTitle title="Change Orders" />
            <ChangeOrder
                {...reactRouterProps}
                id={coId}
                jobId={jobId}
                openedFrom={OpenedFrom.Nothing}
            />
        </>
    );
};

const OwnerInvoicePage = (
    reactRouterProps: RouteComponentProps<{
        id: string;
        jobId: string;
        invoiceFormat: string;
    }>,
) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search, {
        parseBooleans: true,
    });

    const invoiceId: number = parseInt(reactRouterProps.match.params.id);
    const jobId: number = parseInt(reactRouterProps.match.params.jobId);
    const invoiceFormat: number = parseInt(reactRouterProps.match.params.invoiceFormat);
    const fromRelatedItem = qsValues.fromRelatedItem;

    return (
        <OwnerInvoice
            id={invoiceId}
            jobId={jobId}
            invoiceFormat={invoiceFormat as InvoiceFormat}
            fromRelatedItem={fromRelatedItem}
            {...reactRouterProps}
        />
    );
};

const LeadProposalsCheckoutPage = (
    reactRouterProps: RouteComponentProps<{
        proposalId: string;
    }>,
) => {
    const proposalId: number = parseInt(reactRouterProps.match.params.proposalId);
    return <LeadProposalCheckout id={proposalId} {...reactRouterProps} />;
};

const OwnerInvoiceCheckoutPage = (
    reactRouterProps: RouteComponentProps<{
        id: string;
        jobId: string;
    }>,
) => {
    const invoiceId: number = parseInt(reactRouterProps.match.params.id);
    return <OwnerInvoiceCheckout id={invoiceId} {...reactRouterProps} />;
};

const DepositCheckoutPage = (
    reactRouterProps: RouteComponentProps<{
        id: string;
        jobId: string;
    }>,
) => {
    const invoiceId: number = parseInt(reactRouterProps.match.params.id);
    const jobId: number = parseInt(reactRouterProps.match.params.jobId);
    return <DepositCheckout id={invoiceId} jobId={jobId} {...reactRouterProps} />;
};

const ExternalDepositCheckoutPage = (
    reactRouterProps: RouteComponentProps<{
        shareToken: string;
    }>,
) => {
    const shareToken = reactRouterProps.match.params.shareToken;
    return <DepositCheckout shareToken={shareToken} {...reactRouterProps} />;
};

const OwnerInvoiceCheckoutExternalPage = (
    reactRouterProps: RouteComponentProps<{
        id: string;
        builderId: string;
        jobId: string;
        shareToken?: string;
    }>,
) => {
    return (
        <OwnerInvoiceCheckoutExternal
            builderId={reactRouterProps.match.params.builderId}
            id={reactRouterProps.match.params.id}
            jobId={reactRouterProps.match.params.jobId}
            shareToken={reactRouterProps.match.params.shareToken}
            {...reactRouterProps}
        />
    );
};

const SharedExternalOwnerInvoicePage = (
    reactRouterProps: RouteComponentProps<{
        id: string;
        builderId: string;
        jobId: string;
        shareToken: string;
    }>,
) => {
    return (
        <SharedExternalOwnerInvoice
            builderId={reactRouterProps.match.params.builderId}
            id={reactRouterProps.match.params.id}
            jobId={reactRouterProps.match.params.jobId}
            shareToken={reactRouterProps.match.params.shareToken}
            {...reactRouterProps}
        />
    );
};

const ExternalDepositPage = (
    reactRouterProps: RouteComponentProps<{
        depositId: string;
        builderId: string;
        jobId: string;
        shareToken?: string;
    }>,
) => {
    // eslint-disable-next-line deprecate/function
    updateWebformsQueryStringParams();
    return (
        <DepositExternal
            shareToken={reactRouterProps.match.params.shareToken}
            {...reactRouterProps}
        />
    );
};

const DepositPrintPage = (reactRouterProps: RouteComponentProps) => {
    const params = parseParams<IDepositPrintParams>(reactRouterProps.location.search, {
        parseNumbers: true,
    });
    const jobId: number = Number(params.jobId);
    const depositId: number = Number(params.depositId);

    return <DepositPrint jobId={jobId} depositId={depositId} />;
};

const ExternalDepositPrintPage = (
    reactRouterProps: RouteComponentProps<{
        shareToken: string;
    }>,
) => {
    return <DepositPrint shareToken={reactRouterProps.match.params.shareToken} />;
};
const OwnerInvoicePrintPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const invoiceId: number = Number(qsValues.invoiceId);
    const showCustomFields: boolean =
        qsValues.showCustomFields && qsValues.showCustomFields === "true";
    const showInvoiceDescription: boolean =
        qsValues.showInvoiceDescription && qsValues.showInvoiceDescription === "true";
    const showPaymentCode: boolean =
        qsValues.showPaymentCode && qsValues.showPaymentCode === "true";
    const isOwnerPreview: boolean = qsValues.isOwnerPreview && qsValues.isOwnerPreview === "true";

    return (
        <OwnerInvoicePrint
            showCustomFields={showCustomFields}
            showPaymentCode={showPaymentCode}
            showInvoiceDescription={showInvoiceDescription}
            invoiceId={invoiceId}
            isOwnerPreview={isOwnerPreview}
            isPrintPage={true}
        />
    );
};

const ExternalOwnerInvoicePrintPage = (
    reactRouterProps: RouteComponentProps<{
        shareToken: string;
        showCustomFields: string;
        showLineItems: string;
        showPaymentCode: string;
        showInvoiceDescription: string;
    }>,
) => {
    const showCustomFields = reactRouterProps.match.params.showCustomFields === "true";
    const showPaymentCode = reactRouterProps.match.params.showPaymentCode === "true";
    const showInvoiceDescription = reactRouterProps.match.params.showInvoiceDescription === "true";

    return (
        <OwnerInvoicePrint
            showPaymentCode={showPaymentCode}
            showInvoiceDescription={showInvoiceDescription}
            showCustomFields={showCustomFields}
            shareToken={reactRouterProps.match.params.shareToken}
            isPrintPage={true}
        />
    );
};

// Renders the AIA cover-sheet with signature-capture fields for pubsub PDF generation.
const OwnerInvoicePdfGenerationPage = () => {
    const urlParams = useParams<{ shareToken: string }>();
    return (
        <OwnerInvoicePrint
            showPaymentCode={false}
            showInvoiceDescription={true}
            showCustomFields={true}
            shareToken={urlParams.shareToken}
            isPrintPage={true}
            forSignatureCapture={true}
        />
    );
};

const BankValidationRedirectLandingPage = () => {
    useEffect(() => {
        const timeout = setTimeout(() => {
            let redirectUrl = BTLocalStorage.get("bt-string-paymentVerificationOauthRedirectUrl");
            if (redirectUrl) {
                redirectUrl += `&landingUrl=${encodeURIComponent(window.location.href)}`;
                window.location.assign(redirectUrl);
            } else {
                throw new Error("User landed on redirect landing page with missing redirect URL");
            }
        }, 0);
        return function () {
            clearTimeout(timeout);
        };
    }, []);

    return <></>;
};

const AdminPaymentsPrescreeningPage = (reactRouterProps: RouteComponentProps) => {
    return <AdminPaymentsPrescreening {...reactRouterProps} />;
};

const OnlinePaymentExternalPage = (reactRouterProps: RouteComponentProps) => {
    return <OnlinePaymentExternal {...reactRouterProps} />;
};

const OnlineInvoiceExternalPage = (
    reactRouterProps: RouteComponentProps<{ code: string; referralBy: string }>,
) => {
    return <OnlineInvoiceExternal {...reactRouterProps} />;
};

const OutboundPaymentsExternalPage = (
    reactRouterProps: RouteComponentProps<{
        shareToken: string;
    }>,
) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues = queryString.parse(reactRouterProps.location.search, {
        parseBooleans: true,
    });
    const isRedirected = qsValues.redirected as boolean;
    const fromEmail = qsValues.fromEmail as boolean;
    const download = qsValues.download as boolean;
    const fromLienWaivers = qsValues[FROM_LIEN_WAIVERS_PARAM] as boolean;
    return (
        <OutboundPaymentsExternal
            shareToken={reactRouterProps.match.params.shareToken}
            fromEmail={fromEmail}
            isRedirected={isRedirected}
            download={download}
            fromLienWaivers={fromLienWaivers}
            {...reactRouterProps}
        />
    );
};

const OutboundAccountSetupPage = (
    reactRouterProps: RouteComponentProps<{
        shareToken: string;
    }>,
) => {
    return (
        <OutboundAccountSetup
            shareToken={reactRouterProps.match.params.shareToken}
            {...reactRouterProps}
        />
    );
};

const DailyLogPrintPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const pageNum: number = Number(qsValues.pn);
    const pageSize: number = Number(qsValues.ps);
    const filters = JSON.parse(decodeURIComponent(qsValues.filters));

    return <DailyLogPrint pageNumber={pageNum} pageSize={pageSize} filters={filters} />;
};

const ClientUpdatePrintPage = (reactRouterProps: RouteComponentProps) => {
    const qsValues = parseParams<{ ids: string }>(reactRouterProps.location.search);
    const clientUpdateIds = stringToArray(qsValues.ids);

    return <ClientUpdatePrint clientUpdateIds={clientUpdateIds} />;
};

const LegacyProposalPrintPage = (
    reactRouterProps: RouteComponentProps<{ worksheetId?: string; shareToken?: string }>,
) => {
    return (
        <ProposalPrint
            worksheetId={reactRouterProps.match.params.worksheetId}
            shareToken={reactRouterProps.match.params.shareToken}
        />
    );
};

const ProposalPrintPage = (
    reactRouterProps: RouteComponentProps<{
        worksheetId?: string;
        proposalId?: string;
        shareToken?: string;
    }>,
) => {
    return (
        <ProposalPrint
            worksheetId={reactRouterProps.match.params.worksheetId}
            proposalId={reactRouterProps.match.params.proposalId}
            shareToken={reactRouterProps.match.params.shareToken}
        />
    );
};

const ProposalPrintByProposalIdPage = (
    reactRouterProps: RouteComponentProps<{
        proposalId: string;
    }>,
) => {
    return <ProposalPrint proposalId={reactRouterProps.match.params.proposalId} />;
};

const LeadProposalPrintPage = (reactRouterProps: RouteComponentProps<{ proposalId: string }>) => {
    return <LeadProposalPrint id={reactRouterProps.match.params.proposalId} />;
};

const ExternalProposalPage = (
    reactRouterProps: RouteComponentProps<{
        shareToken: string;
    }>,
) => {
    return (
        <OwnerProposal
            shareToken={reactRouterProps.match.params.shareToken}
            {...reactRouterProps}
        />
    );
};

const LeadProposalPdfGenerationPage = (
    reactRouterProps: RouteComponentProps<{ proposalId: string; shareToken?: string }>,
) => {
    return (
        <LeadProposalPdfGeneration
            id={reactRouterProps.match.params.proposalId}
            shareToken={reactRouterProps.match.params.shareToken}
            {...reactRouterProps}
        />
    );
};

const JobProposalPdfGenerationPage = (
    reactRouterProps: RouteComponentProps<{ shareToken: string }>,
) => {
    return (
        <ProposalPdfGeneration
            shareToken={reactRouterProps.match.params.shareToken}
            {...reactRouterProps}
        />
    );
};
const DailyLogDetailPage = () => {
    const routeProps = useRouteProps();
    const urlParams = useParams<{
        id: string;
        jobId: string;
    }>();
    const qsParams = useQueryString<{ todoId: string }>();

    const dailyLogId = Number(urlParams.id);
    const jobId = Number(urlParams.jobId);
    const todoId = qsParams.todoId ? Number(qsParams.todoId) : undefined;

    return (
        <>
            <PageTitle title="Daily Logs" />
            <DailyLog id={dailyLogId} jobId={jobId} todoId={todoId} {...routeProps} />
        </>
    );
};
const DailyLogEditPage = () => <DailyLogDetailPage />;

const DailyLogAddPageInner = () => {
    const routeProps = useRouteProps();
    const urlParams = useParams<{ jobId: string }>();
    const qsParams = useQueryString<{ todoId: string }>();
    const jobId = Number(urlParams.jobId);
    const todoId = qsParams.todoId ? Number(qsParams.todoId) : undefined;

    return (
        <>
            <PageTitle title="Daily Logs" />
            <DailyLog id={0} jobId={jobId} todoId={todoId} {...routeProps} />
        </>
    );
};
const DailyLogAddPage = () => <DailyLogAddPageInner />;

const ContactPage = (
    reactRouterProps: RouteComponentProps<{ id: string; isFromSelector: string }>,
) => {
    const contactId = parseInt(reactRouterProps.match.params.id);
    const isFromSelector = reactRouterProps.match.params.isFromSelector.toLowerCase() === "true";
    return <Contact id={contactId} isFromSelector={isFromSelector} {...reactRouterProps} />;
};

const ContactPrintPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const contactId = parseInt(qsValues.id);
    return <ContactPrint id={contactId} printOnLoad={true} />;
};

const LienWaiverDetailPage = (
    reactRouterProps: RouteComponentProps<{ id: string; jobId: string }>,
) => {
    const id: number = parseInt(reactRouterProps.match.params.id);
    const jobId: number = parseInt(reactRouterProps.match.params.jobId);
    return <LienWaiver id={id} jobId={jobId} {...reactRouterProps} />;
};

const LienWaiverPrintPage = (
    reactRouterProps: RouteComponentProps<{
        shareToken?: string;
    }>,
) => {
    const params = reactRouterProps.match.params;
    const qsValues = parseParams<{
        billIds?: string;
        lienWaiverIds?: string;
        isPDFMode?: boolean;
    }>(reactRouterProps.location.search, {
        parseBooleans: true,
    });
    const billIds = stringToArray(qsValues.billIds);
    const lienWaiverIds = stringToArray(qsValues.lienWaiverIds);
    const isPDFMode: boolean = qsValues.isPDFMode ?? false;
    return (
        <LienWaiverPrint
            billIds={billIds}
            lienWaiverIds={lienWaiverIds}
            isPdfMode={isPDFMode}
            // Gotenberg applies the page margins for lien waiver PDFs (see PdfPageProperties.LienWaiverLegacyLayout),
            // so the extra 25px body margin is not needed and would push content onto an extra page.
            shouldAddMargin={false}
            shareToken={params.shareToken}
        />
    );
};

const MarketplaceRedirectPage = (reactRouterProps: RouteComponentProps) => {
    const qsValues = parseParams<{ mlt: number }>(reactRouterProps.location.search, {
        parseNumbers: true,
    });
    return <MarketplaceRedirect marketplaceLinkType={qsValues.mlt ?? 0} {...reactRouterProps} />;
};
const NotificationPage = (
    reactRouterProps: RouteComponentProps<{
        notificationId: string;
        jobsiteId: string;
        dateSent: string;
        isArchive: string;
    }>,
) => {
    const urlParams = reactRouterProps.match.params;
    const notificationId = parseInt(urlParams.notificationId);
    const jobsiteId = parseInt(urlParams.jobsiteId);
    const dateSent = moment(urlParams.dateSent);
    const isArchive = urlParams.isArchive ? urlParams.isArchive === "true" : false;
    return (
        <Notification
            notificationId={notificationId}
            jobsiteId={jobsiteId}
            dateSent={dateSent}
            isArchive={isArchive}
            {...reactRouterProps}
        />
    );
};

const MessageDetailsExternalPage = (
    reactRouterProps: RouteComponentProps<{ shareToken: string }>,
) => {
    const shareToken = reactRouterProps.match.params.shareToken;
    return <MessageExternal shareToken={shareToken} {...reactRouterProps} />;
};

const MessageComposePage = (
    reactRouterProps: RouteComponentProps<{ messageId: string; jobId: string }>,
) => {
    const urlParams = reactRouterProps.match.params;
    // eslint-disable-next-line deprecate/member-expression
    const qsvalues: any = queryString.parse(reactRouterProps.location.search);

    const messageId = parseInt(urlParams.messageId);
    const isDraft = Boolean(qsvalues.isDraft);
    const jobId = parseInt(urlParams.jobId);
    const replyType = qsvalues.replyType ? (parseInt(qsvalues.replyType) as ReplyType) : undefined;
    const replyId = qsvalues.replyId ? parseInt(qsvalues.replyId) : undefined;
    const defaultRecipientId = qsvalues.defaultRecipientId
        ? parseInt(qsvalues.defaultRecipientId)
        : 0;
    const docInstanceIds =
        qsvalues.docInstanceIds?.split(",").map((str: string) => Number(str)) || [];
    const paymentIds = qsvalues.paymentIds?.split(",").map((str: string) => Number(str)) || [];

    return (
        <MessageCompose
            {...reactRouterProps}
            jobId={jobId}
            messageId={messageId}
            isDraft={isDraft}
            replyId={replyId}
            replyType={replyType}
            defaultRecipientId={defaultRecipientId}
            docInstanceIds={docInstanceIds}
            paymentIds={paymentIds}
        />
    );
};

const PaymentRequestPage = (reactRouterProps: RouteComponentProps) => {
    const routeValues: any = reactRouterProps.match.params;
    const purchaseOrderId = parseInt(routeValues.poId);
    const purchaseOrderPaymentId = parseInt(routeValues.popId);

    return (
        <PaymentRequest
            id={purchaseOrderPaymentId}
            purchaseOrderId={purchaseOrderId}
            {...reactRouterProps}
        />
    );
};

const SubDetailPage = (
    reactRouterProps: RouteComponentProps<{ id: string; defaultTab: string }>,
) => {
    const urlParams = reactRouterProps.match.params;
    const subId = parseInt(urlParams.id);
    const subDetailsTab = urlParams.defaultTab ? (urlParams.defaultTab as SubTabKeys) : undefined;
    return <SubVendor id={subId} defaultTab={subDetailsTab} {...reactRouterProps} />;
};

const BudgetLegacyPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Budget" />
            <ExpectedBudget listLayoutProps={listLayoutProps} {...reactRouterProps} />
        </>
    );
};

const OwnerBudgetPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Budget" />
            <OwnerBudget listLayoutProps={listLayoutProps} {...reactRouterProps} />
        </>
    );
};

const ReceiptUpload = (reactRouterProps: RouteComponentProps<{ id: string }>) => {
    return <ReceiptsUpload {...reactRouterProps} />;
};

const ReceiptPage = (reactRouterProps: RouteComponentProps<{ id: string }>) => {
    const id: number = parseInt(reactRouterProps.match.params.id);

    return <Receipt {...reactRouterProps} id={id} />;
};

const ReceiptListPage = (listLayoutProps: IListPageChildProps, route: RouteComponentProps<any>) => {
    return (
        <>
            <PageTitle title="Cost Inbox" />
            <ReceiptList {...route} {...listLayoutProps} entityType={ListEntityType.Receipts} />
        </>
    );
};

const ImportWizardPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const builderId = parseInt(qsValues.builderId);
    const importType = parseInt(qsValues.importType);
    return <ImportWizard importType={importType} builderId={builderId} {...reactRouterProps} />;
};

const CreditMemoPage = (reactRouterProps: RouteComponentProps<{ id: string; jobId: string }>) => {
    const id: number = parseInt(reactRouterProps.match.params.id);
    const jobId: number = parseInt(reactRouterProps.match.params.jobId);
    return <CreditMemo id={id} jobId={jobId} {...reactRouterProps} />;
};

const CreditMemoPrintPage = (
    reactRouterProps: RouteComponentProps<{ id: string; jobId: string }>,
) => {
    const id: number = parseInt(reactRouterProps.match.params.id);
    const jobId: number = parseInt(reactRouterProps.match.params.jobId);
    return <CreditMemoPrint id={id} jobId={jobId} {...reactRouterProps} />;
};

const InternalUserQuickAddPage = (reactRouterProps: RouteComponentProps) => {
    return <InternalUserQuickAdd {...reactRouterProps} />;
};

const InternalUserSettingsPage = (
    reactRouterProps: RouteComponentProps<{
        showCustomFields: string;
        fromInternalUserId: string;
        currentTab: InternalUserTabKeys;
    }>,
) => {
    const showCustomFields =
        reactRouterProps.match.params.showCustomFields.toLowerCase() === "true";
    const fromInternalUserId = parseInt(reactRouterProps.match.params.fromInternalUserId);
    const currentTab = reactRouterProps.match.params.currentTab;

    return (
        <InternalUserRoleListDev
            showCustomFields={showCustomFields}
            fromInternalUserId={fromInternalUserId}
            currentTab={currentTab}
            {...reactRouterProps}
        />
    );
};

const InternalUserRoleDetailsPage = (
    reactRouterProps: RouteComponentProps<{
        roleId: string;
        copyFromId: string | undefined;
        showCustomFields: string;
        fromInternalUserId: string;
        currentTab: InternalUserTabKeys;
    }>,
) => {
    const roleId: number = parseInt(reactRouterProps.match.params.roleId);

    let copyFromId: number | undefined = undefined;
    if (reactRouterProps.match.params.copyFromId) {
        copyFromId = parseInt(reactRouterProps.match.params.copyFromId);
    }

    const showCustomFields: boolean =
        reactRouterProps.match.params.showCustomFields.toLowerCase() === "true";
    const fromInternalUserId: number = parseInt(reactRouterProps.match.params.fromInternalUserId);
    const currentTab: InternalUserTabKeys = reactRouterProps.match.params.currentTab;

    return (
        <InternalUserRoleDetailsDev
            roleId={roleId}
            copyFromId={copyFromId}
            showCustomFields={showCustomFields}
            fromInternalUserId={fromInternalUserId}
            currentTab={currentTab}
            {...reactRouterProps}
        />
    );
};

const InternalUserPage = (reactRouterProps: RouteComponentProps<{ id: string }>) => {
    const id: number = parseInt(reactRouterProps.match.params.id);
    // eslint-disable-next-line deprecate/member-expression
    const qs: any = queryString.parse(reactRouterProps.location.search, { parseBooleans: true });
    const action: number | undefined = qs.internalUserLoadAction;

    let initialTab = qs.initialTab;
    let error = null;
    if (qs.thdConnected || qs.thdError) {
        initialTab = InternalUserTabKeys.Vendors;

        if (qs.thdError) {
            error = qs.thdError;
        }
        error = qs.thdError;
    } else if (qs.firstConnection || qs.connected || qs.type === "takeoff") {
        initialTab = InternalUserTabKeys.Takeoff;

        if (qs.error) {
            error = qs.error;
        }
    }
    return (
        <InternalUser
            id={id}
            thdConnected={qs.thdConnected}
            error={error}
            initialTab={initialTab}
            internalUserLoadAction={action}
            {...reactRouterProps}
        />
    );
};

const InternalUsersListPage = (
    listLayoutProps: IListPageChildProps,
    route: RouteComponentProps<any>,
) => {
    return (
        <>
            <PageTitle title="Internal Users" />
            <InternalUsersList
                {...route}
                {...listLayoutProps}
                entityType={ListEntityType.UsersList}
            />
        </>
    );
};

const ContactsListPage = (listLayoutProps: IListPageChildProps, route: RouteComponentProps) => {
    return (
        <>
            <PageTitle title="Client Contacts" />
            <ContactsList {...route} {...listLayoutProps} entityType={ListEntityType.Contacts} />
        </>
    );
};

const SubsListPage = (listLayoutProps: IListPageChildProps, route: RouteComponentProps) => {
    const systemFilter = getSystemFilter(route);
    return (
        <>
            <PageTitle title="Subs/Vendors" />
            <SubList
                {...route}
                {...listLayoutProps}
                entityType={ListEntityType.SubsList}
                systemFilter={systemFilter}
            />
        </>
    );
};

const SubscriptionManagerSettingsPage = (
    reactRouterProps: RouteComponentProps<{ fromSettings: string; fromFrozenBuilder: string }>,
) => {
    const urlParams: any = reactRouterProps.match.params;
    const fromSettings: boolean = urlParams.fromSettings.toLowerCase() === "true";
    const fromFrozenBuilder: boolean = urlParams.fromFrozenBuilder.toLowerCase() === "true";

    return (
        <SubscriptionManager
            fromSettings={fromSettings}
            fromFrozenBuilder={fromFrozenBuilder}
            {...reactRouterProps}
        />
    );
};

const SurveySettingsPage = (reactRouterProps: RouteComponentProps) => {
    return <SurveySettings {...reactRouterProps} />;
};

const SurveyPage = (reactRouterProps: RouteComponentProps) => {
    const urlParams: any = reactRouterProps.match.params;
    const surveyId = parseInt(urlParams.id);
    const jobId = parseInt(urlParams.jobId);
    const surveyDefinitionId = parseInt(urlParams.surveyDefinitionId);

    return (
        <Survey
            id={surveyId}
            jobId={jobId}
            surveyDefinitionId={surveyDefinitionId}
            {...reactRouterProps}
        />
    );
};

const OwnerSurveyPage = (reactRouterProps: RouteComponentProps) => {
    const urlParams: any = reactRouterProps.match.params;
    const surveyId = parseInt(urlParams.id);

    return <OwnerSurvey id={surveyId} {...reactRouterProps} />;
};

const MessagePrintPage = () => {
    const params = useParams<{ messageId: string; folderId: string }>();
    return <MessagePrint messageId={Number(params.messageId)} folderId={Number(params.folderId)} />;
};

const MediaMoveFolderPage = (
    reactRouterProps: RouteComponentProps<{
        folderId: string;
        jobId: string;
        mediaType: string;
    }>,
) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const urlParams = reactRouterProps.match.params;

    const folderId = parseInt(urlParams.folderId);
    const jobId = parseInt(urlParams.jobId);
    const mediaType = parseInt(urlParams.mediaType);

    const documentInstanceIds = qsValues.documentInstanceIds
        ? stringToArray(qsValues.documentInstanceIds)
        : [];
    const folderIds = qsValues.folderIds ? stringToArray(qsValues.folderIds) : [];

    return (
        <MediaMoveFolder
            currentFolderId={folderId}
            documentInstanceIds={documentInstanceIds}
            folderIds={folderIds}
            jobId={jobId}
            mediaType={mediaType}
            {...reactRouterProps}
        />
    );
};

const MediaFolderEmailInfoPage = (
    reactRouterProps: RouteComponentProps<{
        folderId: string;
        jobId: string;
        mediaType: string;
    }>,
) => {
    const folderId = parseInt(reactRouterProps.match.params.folderId);
    const jobId = parseInt(reactRouterProps.match.params.jobId);
    const mediaType = parseInt(reactRouterProps.match.params.mediaType);

    return (
        <MediaFolderEmailInfo
            parentFolderId={folderId}
            jobId={jobId}
            mediaType={mediaType}
            {...reactRouterProps}
        />
    );
};

const MediaFolderPropertiesPage = (
    reactRouterProps: RouteComponentProps<{ folderId: string; jobId: string }>,
) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const folderId: number = parseInt(reactRouterProps.match.params.folderId);
    const jobId: number = parseInt(reactRouterProps.match.params.jobId);
    const parentFolderId = parseInt(qsValues.parentFolderId);
    const mediaType = parseInt(qsValues.mediaType);
    return (
        <MediaFolderProperties
            id={folderId}
            parentFolderId={parentFolderId}
            jobId={jobId}
            mediaType={mediaType}
            {...reactRouterProps}
        />
    );
};

const MediaPropertiesPage = (
    reactRouterProps: RouteComponentProps<{
        docInstanceId: string;
        jobId: string;
        mediaType: string;
        folderId: string;
        templateType: string;
        templateDocumentInstanceId: string;
    }>,
) => {
    const urlParams = reactRouterProps.match.params;

    const documentInstanceId = parseInt(urlParams.docInstanceId);
    const folderId = urlParams.folderId !== "undefined" ? parseInt(urlParams.folderId) : undefined;
    const jobId = parseInt(urlParams.jobId);
    const mediaType = parseInt(urlParams.mediaType);
    const templateDocumentInstanceId =
        urlParams.templateDocumentInstanceId !== "undefined"
            ? parseInt(urlParams.templateDocumentInstanceId)
            : undefined;
    const templateType =
        urlParams.templateType !== "undefined"
            ? (parseInt(urlParams.templateType) as TemplateType)
            : undefined;

    return (
        <>
            {mediaType === MediaType.Document && (
                <Document
                    documentInstanceId={documentInstanceId}
                    folderId={folderId}
                    jobId={jobId}
                    mediaType={mediaType}
                    templateDocumentInstanceId={templateDocumentInstanceId}
                    templateType={templateType}
                    {...reactRouterProps}
                />
            )}

            {mediaType === MediaType.Photo && (
                <Photo
                    documentInstanceId={documentInstanceId}
                    jobId={jobId}
                    {...reactRouterProps}
                />
            )}

            {mediaType === MediaType.Video && (
                <Video
                    documentInstanceId={documentInstanceId}
                    jobId={jobId}
                    {...reactRouterProps}
                />
            )}
        </>
    );
};

const MediaReplacePage = (
    reactRouterProps: RouteComponentProps<{
        documentInstanceId: string;
        isFromMediaPage: string;
        mediaType: string;
        includeThisDoc?: string;
        linkedByAnnotationGroup?: string;
    }>,
) => {
    const urlParams = reactRouterProps.match.params;

    const documentInstanceId = parseInt(urlParams.documentInstanceId);
    const includeThisDoc = urlParams.includeThisDoc
        ? urlParams.includeThisDoc === "true"
        : undefined;
    const isFromMediaPage = urlParams.isFromMediaPage === "true";
    const linkedByAnnotationGroup = urlParams.linkedByAnnotationGroup
        ? urlParams.linkedByAnnotationGroup === "true"
        : undefined;
    const mediaType = parseInt(urlParams.mediaType);

    return (
        <MediaReplace
            documentInstanceId={documentInstanceId}
            includeThisDoc={includeThisDoc}
            isFromMediaPage={isFromMediaPage}
            linkedByAnnotationGroup={linkedByAnnotationGroup}
            mediaType={mediaType}
            onCancel={() => reactRouterProps.history.goBack()}
            onReplace={() => {
                reactRouterProps.history.goBack();
                return Promise.resolve();
            }}
            {...reactRouterProps}
        />
    );
};

// TODO: All of this complicated logic needs to go somewhere else
const PhotoPreviewPage = (reactRouterProps: RouteComponentProps<{ id: string; jobId: string }>) => {
    const [idsAndJobIds, setIdsAndJobIds] = useState<IDocInstanceAndJobId[]>([]);
    const [idsAndJobIdsIndex, setIdsAndJobIdsIndex] = useState<number>(0);

    const documentInstanceId: number = parseInt(reactRouterProps.match.params.id);
    const jobId: number = parseInt(reactRouterProps.match.params.jobId);
    const jobIds = BTSessionStorage.get("bt-numberArray-photoPreviewListOfJobIds");
    const docInstanceIds = BTSessionStorage.get("bt-numberArray-photoPreviewListOfDocInstanceIds");

    useEffect(() => {
        if (jobIds && docInstanceIds) {
            const docInstanceIdsMap =
                docInstanceIds?.map((value, index) => {
                    return { id: value, jobId: jobIds[index] };
                }) ?? [];
            if (!isEqual(idsAndJobIds, docInstanceIdsMap)) {
                setIdsAndJobIds(docInstanceIdsMap);
            }
        } else if (jobIds || docInstanceIds) {
            setIdsAndJobIds([]);
        }
    }, [docInstanceIds, idsAndJobIds, jobIds]);

    useEffect(() => {
        if (documentInstanceId) {
            const docInstanceIds = idsAndJobIds.map((i) => i.id);
            const index = docInstanceIds.findIndex((id) => id === documentInstanceId);
            setIdsAndJobIdsIndex(index);
        } else {
            setIdsAndJobIdsIndex(0);
        }
    }, [documentInstanceId, idsAndJobIds]);

    const handlePhotoIndexChanged = (index: number) => {
        setIdsAndJobIdsIndex(index);
    };

    return (
        <PhotoPreview
            id={documentInstanceId}
            jobId={jobId}
            ids={idsAndJobIds}
            onPhotoIndexChanged={handlePhotoIndexChanged}
            index={idsAndJobIdsIndex}
            {...reactRouterProps}
        />
    );
};

const PurchaseOrderListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    const systemFilter = getSystemFilter(reactRouterProps);

    return (
        <>
            <PageTitle title="Purchase Orders" />
            <PurchaseOrderList
                {...reactRouterProps}
                {...listLayoutProps}
                viewingTemplatesOnly={listLayoutProps.isTemplateMode}
                entityType={ListEntityType.PurchaseOrders}
                systemFilter={systemFilter}
            />
        </>
    );
};

const ScheduleOfValuesListPage = (listLayoutProps: IListPageChildProps) => {
    return (
        <ScheduleOfValuesList
            jobIds={listLayoutProps.jobIds}
            jobName={listLayoutProps.jobName}
            jobPickerReady={listLayoutProps.jobPickerReady}
            isAllJobsSelected={listLayoutProps.isAllJobsSelected}
        />
    );
};

const ScheduleOfValuesDetailPageInner = ({
    jobIds,
    isAllJobsSelected,
    isTemplateMode,
    route,
}: {
    jobIds: number[];
    isAllJobsSelected: boolean;
    isTemplateMode: boolean;
    route: RouteComponentProps<{ jobId: string }>;
}) => {
    const jobId = parseInt(route.match.params.jobId) || 0;
    useJobPickerDetailRedirect(
        jobIds,
        isAllJobsSelected,
        jobId,
        route.history,
        routes.scheduleOfValues.getListLink(),
    );
    return <ScheduleOfValuesPage jobId={jobId} canEdit={!isTemplateMode} />;
};

// ListPageLayout invokes props.component(...) as a plain function, so this outer wrapper must NOT use hooks. Delegate to the JSX-rendered inner component.
const ScheduleOfValuesDetailPage = (
    listLayoutProps: IListPageChildProps,
    route: RouteComponentProps<{ jobId: string }>,
) => (
    <ScheduleOfValuesDetailPageInner
        jobIds={listLayoutProps.jobIds}
        isAllJobsSelected={listLayoutProps.isAllJobsSelected}
        isTemplateMode={listLayoutProps.isTemplateMode}
        route={route}
    />
);

const PurchaseOrderPage = (
    reactRouterProps: RouteComponentProps<{
        id: string;
        jobId: string;
        varianceChangeOrderId?: string;
    }>,
) => {
    const routeParams = reactRouterProps.match.params;

    const id: number = parseInt(routeParams.id);
    const jobId: number = parseInt(routeParams.jobId);
    const varianceChangeOrderId: number | undefined =
        routeParams.varianceChangeOrderId !== undefined
            ? parseInt(routeParams.varianceChangeOrderId)
            : undefined;
    const openToScopeOfWork: boolean = reactRouterProps.location.pathname.includes("RFI");
    const isBuilder: boolean = isInPortal({ builder: true });
    // eslint-disable-next-line deprecate/member-expression
    const qsvalues: any = queryString.parse(reactRouterProps.location.search, {
        parseBooleans: true,
    });
    const fromBid = qsvalues.fromBid;

    return (
        <>
            <PurchaseOrder
                id={id}
                jobId={jobId}
                varianceChangeOrderId={varianceChangeOrderId}
                openToScopeOfWork={openToScopeOfWork}
                disableBidLinks={fromBid}
                loadLinkedBids={isBuilder}
                {...reactRouterProps}
            />
        </>
    );
};

const AdminCancellationPage = (
    reactRouterProps: RouteComponentProps<{ builderId: string; subscriptionId: string }>,
) => {
    const routeParams = reactRouterProps.match.params;
    const builderId = parseInt(routeParams.builderId);
    const subscriptionId = routeParams.subscriptionId;
    return (
        <AdminCancellation
            {...reactRouterProps}
            builderId={builderId}
            subscriptionId={subscriptionId}
        />
    );
};

const BuilderAccountChangeRequestPage = (
    reactRouterProps: RouteComponentProps<{ builderId: string; linkId: string }>,
) => {
    const routeParams = reactRouterProps.match.params;

    const builderID: number = parseInt(routeParams.builderId);
    const linkId: string = routeParams.linkId;
    return <BuilderAccountChangeRequest id={builderID} linkId={linkId} {...reactRouterProps} />;
};

const AdminSubscriptionPage = (
    reactRouterProps: RouteComponentProps<{
        fromSettings: string;
        builderId: string;
        isAdminUser: string;
    }>,
) => {
    const builderId: number = parseInt(reactRouterProps.match.params.builderId);
    const isAdminUser: boolean = reactRouterProps.match.params.isAdminUser === "true";

    return (
        <SubscriptionList
            builderId={builderId}
            isAdminUser={isAdminUser}
            showCancel
            {...reactRouterProps}
        />
    );
};

const ChangeOrderPaymentPage = (
    reactRouterProps: RouteComponentProps<{ id: string; jobId: string }>,
) => {
    const id: number = parseInt(reactRouterProps.match.params.id);
    const jobId: number = parseInt(reactRouterProps.match.params.jobId);
    return <ChangeOrderPayment id={id} jobId={jobId} {...reactRouterProps} />;
};

const ChangeOrderApprovalSuccessPage = () => {
    return <ChangeOrderApprovalSuccess />;
};

const LinkEntityToAccountingPage = (
    reactRouterProps: RouteComponentProps<{ idType: string; entityID: string }>,
) => {
    const idType: IDTypes = parseInt(reactRouterProps.match.params.idType);
    const entityID: number = parseInt(reactRouterProps.match.params.entityID);

    return (
        <LinkEntityToAccounting
            idType={idType}
            entityID={entityID}
            onCancel={() => reactRouterProps.history.goBack()}
            {...reactRouterProps}
        />
    );
};

const BillsListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps<{ view?: string }>,
) => {
    const systemFilter = getSystemFilter(reactRouterProps);
    const { view } = reactRouterProps.match.params;

    return (
        <>
            <PageTitle title="Bills" />
            <BillsListPageContent
                listLayoutProps={listLayoutProps}
                reactRouterProps={reactRouterProps}
                systemFilter={systemFilter}
                view={view}
            />
        </>
    );
};

const BillsInboxRoute = (listLayoutProps: IListPageChildProps) => {
    return <BillsInbox listLayoutProps={listLayoutProps} />;
};

const BillApprovalQueuePage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Bills To Approve" />
            <BillApprovalQueue
                {...reactRouterProps}
                {...listLayoutProps}
                entityType={ListEntityType.Payments}
            />
        </>
    );
};

// ListPageLayout invokes `props.component(...)` as a plain function, so the
// direct `component` here must NOT use hooks. Delegate to an inner element.
const BillPageInner = () => {
    const routeProps = useRouteProps();
    const urlParams = useParams<{ id: string; jobId: string; lienWaiver?: string }>();
    const qsParams = useQueryString<{ sendToAccounting: string; billToOwner: string }>();

    const purchaseOrderId = parseInt(urlParams.id);
    const jobId = parseInt(urlParams.jobId);
    const openToLienWaiver = urlParams.lienWaiver === "lienWaiver";
    const isBuilder: boolean = isInPortal({ builder: true });
    const isSub: boolean = isInPortal({ subs: true });
    const sendToAccounting = qsParams.sendToAccounting === "true";
    const billToOwner = qsParams.billToOwner === "true";

    return (
        <>
            {isBuilder && (
                <Bill
                    openToLienWaiver={openToLienWaiver}
                    id={purchaseOrderId}
                    jobId={jobId}
                    sendToAccounting={sendToAccounting}
                    billToOwner={billToOwner}
                    {...routeProps}
                />
            )}
            {isSub && <SubBill id={purchaseOrderId} jobId={jobId} {...routeProps} />}
        </>
    );
};
const BillPage = () => <BillPageInner />;

const BillAddPageInner = () => {
    const routeProps = useRouteProps();
    const urlParams = useParams<{ jobId: string }>();
    const qsParams = useQueryString<{
        sendToAccounting: string;
        billToOwner: string;
        purchaseOrderId: string;
        receiptId: string;
    }>();
    const jobId = Number(urlParams.jobId);
    const sendToAccounting = qsParams.sendToAccounting === "true";
    const billToOwner = qsParams.billToOwner === "true";
    const purchaseOrderId = qsParams.purchaseOrderId ? Number(qsParams.purchaseOrderId) : undefined;
    const receiptId = qsParams.receiptId ? Number(qsParams.receiptId) : undefined;
    const isBuilder: boolean = isInPortal({ builder: true });

    return (
        <>
            <PageTitle title="Bills" />
            {isBuilder && (
                <Bill
                    id={0}
                    jobId={jobId}
                    sendToAccounting={sendToAccounting}
                    billToOwner={billToOwner}
                    purchaseOrderId={purchaseOrderId}
                    receiptId={receiptId}
                    {...routeProps}
                />
            )}
        </>
    );
};
const BillAddPage = () => <BillAddPageInner />;

const BillPrintPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const billIds = stringToArray(qsValues.billIds);
    const jobId = qsValues.jobId;
    const isPdfMode: boolean = qsValues.isPDFMode ? qsValues.isPDFMode : false;
    const showPaymentInfo: boolean = qsValues.showPaymentInfo ? qsValues.showPaymentInfo : false;

    return (
        <BillPrint
            ids={billIds}
            jobId={jobId}
            isPdfMode={isPdfMode}
            showPaymentInfoForPdf={showPaymentInfo}
            shouldAddMargin={isPdfMode}
        />
    );
};

const BidPackagePage = (reactRouterProps: RouteComponentProps) => {
    const routeValues: any = reactRouterProps.match.params;
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search, {
        parseBooleans: true,
    });
    const bidPackageId = parseInt(routeValues.id);
    const jobId = parseInt(routeValues.jobId);
    const isFromRfi = qsValues.fromRFI;
    const isTemplateMode = qsValues.isTemplateMode;
    const initialTab = qsValues.initialTab;

    return (
        <BidPackage
            id={bidPackageId}
            jobId={jobId}
            isFromRfi={isFromRfi}
            isTemplateMode={isTemplateMode}
            initialTab={initialTab}
            {...reactRouterProps}
        />
    );
};

const AllowancePage = (reactRouterProps: RouteComponentProps) => {
    const routeValues: any = reactRouterProps.match.params;
    const allowanceId = parseInt(routeValues.id);
    const jobId = parseInt(routeValues.jobId);

    return <Allowance id={allowanceId} jobId={jobId} {...reactRouterProps} />;
};

const MobileDownloadPage = () => {
    return <MobileDownload />;
};

const CopyMediaPage = (reactRouterProps: RouteComponentProps<{ jobId: string }>) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const jobId = parseInt(reactRouterProps.match.params.jobId);
    const mediaType = parseInt(qsValues.mediaType);
    const mediaIds = qsValues.mediaIds ? stringToArray(qsValues.mediaIds) : [];
    const folderIds = qsValues.folderIds ? stringToArray(qsValues.folderIds) : [];

    return (
        <CopyMedia
            documentInstanceIds={mediaIds}
            folderIds={folderIds}
            jobId={jobId}
            mediaType={mediaType}
            {...reactRouterProps}
        />
    );
};

const ChangeOrderPoMappingPage = (reactRouterProps: RouteComponentProps) => {
    const params = parsePurchaseOrderMappingQueryStringParams(reactRouterProps.location.search);

    return <ChangeOrderPOMapping {...params} {...reactRouterProps} />;
};

const BidPoMappingPage = (reactRouterProps: RouteComponentProps) => {
    const params = parsePurchaseOrderMappingQueryStringParams(reactRouterProps.location.search);

    return <BidPOMapping {...params} {...reactRouterProps} />;
};

const EstimatePoMappingPage = (reactRouterProps: RouteComponentProps) => {
    const { ids, ...params } = parsePurchaseOrderMappingQueryStringParams(
        reactRouterProps.location.search,
    );

    return (
        <EstimatePOMapping
            {...params}
            idsAndTypes={ids.map((x) => ({
                id: x,
                lineItemType: POMappingEntityTypes.Estimates,
            }))}
            {...reactRouterProps}
        />
    );
};

const SelectionPoMappingPage = (reactRouterProps: RouteComponentProps) => {
    const params = parsePurchaseOrderMappingQueryStringParams(reactRouterProps.location.search);

    return (
        <SelectionPOMapping
            entityType={POMappingEntityTypes.Selections}
            {...params}
            {...reactRouterProps}
        />
    );
};

const SelectionChoicePoMappingPage = (reactRouterProps: RouteComponentProps) => {
    const params = parsePurchaseOrderMappingQueryStringParams(reactRouterProps.location.search);

    return (
        <SelectionPOMapping
            entityType={POMappingEntityTypes.SelectionChoices}
            {...params}
            {...reactRouterProps}
        />
    );
};

const JobFromTemplatePage = (
    reactRouterProps: RouteComponentProps<{
        leadId: string;
        jobType: string;
        leadSoldDate: string;
    }>,
) => {
    const leadId = parseInt(reactRouterProps.match.params.leadId);
    const jobType = parseInt(reactRouterProps.match.params.jobType);
    const leadSoldDate =
        reactRouterProps.match.params.leadSoldDate !== "0"
            ? reactRouterProps.match.params.leadSoldDate
            : undefined;

    return (
        <JobFromTemplate
            leadId={leadId}
            jobType={jobType}
            leadSoldDate={leadSoldDate}
            {...reactRouterProps}
        />
    );
};

const JobPageWebviewPage = () => {
    return <JobPageWebviewContainer />;
};

const WebviewJobPriceSummaryPrintPage = () => {
    const params = useParams() as IJobPriceSummaryPrintWebviewRouteParams;
    const routeProps = useRouteProps();
    return <JobPriceSummaryPrintWebviewContainer webviewRouteParams={params} {...routeProps} />;
};

const JobFinancialsPage = (
    listLayoutProps: IListPageChildProps,
    routeProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Financials" />
            <JobFinancials {...listLayoutProps} {...routeProps} />
        </>
    );
};

const JobPricePrintPage = (
    reactRouterProps: RouteComponentProps<{ jobId: string; printOnLoad?: string }>,
) => {
    const jobId = parseInt(reactRouterProps.match.params.jobId);
    const printOnLoad: boolean = reactRouterProps.match.params.printOnLoad === "true";
    const initialSortStates = parseJpsSortStates(reactRouterProps.location.search);

    return (
        <JobPriceSummaryPrint
            jobId={jobId}
            printOnLoad={printOnLoad}
            isOnPrintPage={true}
            initialSortStates={initialSortStates}
            {...reactRouterProps}
        />
    );
};

const JobPrintPage = (
    reactRouterProps: RouteComponentProps<{ jobId: string; printOnLoad?: string }>,
) => {
    const jobId = parseInt(reactRouterProps.match.params.jobId);
    const printOnLoad: boolean = reactRouterProps.match.params.printOnLoad === "true";

    return <JobPrint jobId={jobId} printOnLoad={printOnLoad} {...reactRouterProps} />;
};

const BidRequestPage = (reactRouterProps: RouteComponentProps) => {
    const routeValues: any = reactRouterProps.match.params;
    const bidRequestId = parseInt(routeValues.id);
    const jobId = parseInt(routeValues.jobId);
    const openedFrom: OpenedFrom = parseInt(routeValues.openedFrom);

    return (
        <BidRequest id={bidRequestId} jobId={jobId} openedFrom={openedFrom} {...reactRouterProps} />
    );
};

const BuilderRiskInsuranceSettingsPage = (
    reactRouterProps: RouteComponentProps<{
        settingsLayout: string;
        autoOpenForm: string;
    }>,
) => {
    const settingsLayout: boolean = reactRouterProps.match.params.settingsLayout === "true";
    const autoOpenForm: boolean = reactRouterProps.match.params.autoOpenForm === "true";

    return (
        <BuilderRiskInsuranceSettings
            settingsLayout={settingsLayout}
            autoOpenForm={autoOpenForm}
            {...reactRouterProps}
        />
    );
};

const ConversationPage = (
    reactRouterProps: RouteComponentProps<{
        entityId: string;
        entityType: string;
        builderId: string;
        jobId?: string;
    }>,
) => {
    const entityId = parseInt(reactRouterProps.match.params.entityId);
    const entityType = parseInt(reactRouterProps.match.params.entityType);
    const builderId = parseInt(reactRouterProps.match.params.builderId);
    const jobId =
        reactRouterProps.match.params.jobId !== undefined
            ? parseInt(reactRouterProps.match.params.jobId)
            : undefined;

    return (
        <ConversationWithEntityPrompt
            entityId={entityId}
            entityType={entityType}
            jobId={jobId && jobId > 0 ? jobId : undefined}
            builderId={builderId}
            {...reactRouterProps}
        />
    );
};

const TemplatePage = (reactRouterProps: RouteComponentProps<{ jobId: string }>) => {
    const urlParams = reactRouterProps.match.params;
    const jobId = parseInt(urlParams.jobId);
    // TODO Remove the fake handler after real endpoint is created
    return <Template id={jobId} {...reactRouterProps} />;
};
const TemplateFromTemplatePage = (
    reactRouterProps: RouteComponentProps<{ externalJobId: string }>,
) => {
    return (
        <TemplateFromTemplate
            externalJobId={reactRouterProps.match.params.externalJobId}
            {...reactRouterProps}
        />
    );
};

const UnreadDocumentPage = () => {
    return <UnreadDocument />;
};

const WebviewSignDocumentPage = () => {
    const params = useParams() as ISignDocumentWebviewRouteParams & { interfaceVersion: string };
    return <SignDocumentWebviewContainer webviewRouteParams={params} />;
};

const WebviewSignatureRequestPage = () => {
    const params = useParams() as ISignatureRequestWebviewRouteParams & {
        interfaceVersion: string;
    };
    return <SignatureRequestWebviewContainer webviewRouteParams={params} />;
};

const WebviewSignatureFlowPage = () => {
    const params = useParams() as IWebviewRouteParams & { interfaceVersion: string };
    return <SignatureFlowWebviewContainer webviewRouteParams={params} />;
};

const RFIPrintPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search);
    const id: number = parseInt(qsValues.id);

    return <RFIPrint id={id} />;
};

const BrandingSettingsPage = (reactRouterProps: RouteComponentProps) => {
    return <BrandingSettings {...reactRouterProps} />;
};

const ScheduleConflictReportPage = (
    reactRouterProps: RouteComponentProps<{ view: string; id?: string }>,
) => {
    const id =
        reactRouterProps.match.params.id !== undefined
            ? parseInt(reactRouterProps.match.params.id)
            : undefined;
    const view = parseInt(reactRouterProps.match.params.view);

    return (
        <ScheduleConflictReport
            startingId={id}
            startingView={view}
            // eslint-disable-next-line deprecate/member-expression
            handleContinue={() => routesWebforms.closeModal()}
            {...reactRouterProps}
        />
    );
};

const ScheduleNotificationPromptPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search, {
        parseNumbers: true,
    });

    let scheduleIds: number[] = [];
    if (qsValues.scheduleIds !== "") {
        scheduleIds = stringToArray(qsValues.scheduleIds);
    }

    return (
        <ScheduleNotificationPrompt
            schedulesToNotify={scheduleIds}
            // eslint-disable-next-line deprecate/member-expression
            onSend={() => routesWebforms.closeModal()}
            // eslint-disable-next-line deprecate/member-expression
            onClose={() => routesWebforms.closeModal()}
        />
    );
};

const NoAccessPage = (reactRouterProps: RouteComponentProps<{ reason: string }>) => {
    const routeValues: any = reactRouterProps.match.params;
    const reason = parseInt(routeValues.reason);
    const builderInfo = useContext(BuilderInfoContext);

    return <NoAccess reason={reason} builderName={builderInfo?.name} {...reactRouterProps} />;
};

const SubscriptionItemSetupPage = (reactRouterProps: RouteComponentProps) => {
    const urlParams: any = reactRouterProps.match.params;
    const id = parseInt(urlParams.id);

    return <SubscriptionItemSetup id={id} {...reactRouterProps} />;
};

const SubscriptionPlanLimitsPage = (reactRouterProps: RouteComponentProps) => {
    return <SubscriptionPlanLimitsDetails {...reactRouterProps} />;
};

const SearchUtilsPage = () => {
    return <SearchUtils />;
};

const FeatureFlagListPage = (reactRouterProps: RouteComponentProps) => {
    return (
        <FeatureFlagListContainer {...reactRouterProps} entityType={ListEntityType.FeatureFlag} />
    );
};

const FeatureFlagPage = (reactRouterProps: RouteComponentProps) => {
    const urlParams: any = reactRouterProps.match.params;
    const id = parseInt(urlParams.id);

    return <FeatureFlag id={id} {...reactRouterProps} />;
};

const PurchasingDashboardPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps<IPurchasingDashboardRouteProps>,
) => {
    return (
        <>
            <PageTitle title="Purchasing" />
            <PurchasingDashboard {...listLayoutProps} {...reactRouterProps} />
        </>
    );
};

const RebateReceiptReviewListPage = () => {
    return <RebateReceiptReviewList />;
};

const RebateReceiptReviewQueuePage = () => {
    return <RebateHawkQueue />;
};

const RebateHawkQueueListPage = (reactRouterProps: RouteComponentProps) => {
    return <RebateHawkQueueList {...reactRouterProps} />;
};

const HawkApp = () => <Hawk />;

const PartnerListPage = (reactRouterProps: RouteComponentProps) => {
    return <PartnerListContainer {...reactRouterProps} entityType={ListEntityType.Partners} />;
};

const PartnerPage = (reactRouterProps: RouteComponentProps) => {
    const urlParams: any = reactRouterProps.match.params;
    const id = parseInt(urlParams.id);

    return <Partner id={id} {...reactRouterProps} />;
};

const BuilderSetupSummaryPage = (
    listLayoutProps: IListPageChildProps,
    routeProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Setup" />
            <SetupSummary {...routeProps} />
        </>
    );
};

const BuilderSetupSearchPage = () => {
    return <BuilderSetupSearch />;
};

const BuilderSetupPage = (reactRouterProps: RouteComponentProps<{ builderId: string }>) => {
    const urlParams = reactRouterProps.match.params;
    const builderId = urlParams.builderId;
    return <BuilderSetup builderId={builderId} />;
};

const OwnerPortalRedirectPage = (reactRouterProps: RouteComponentProps) => {
    const urlParams: any = reactRouterProps.match.params;
    const jobId = parseInt(urlParams.jobId);
    const editPermissions = urlParams.editPermissions === "true";

    return (
        <OwnerPortalRedirect {...reactRouterProps} jobId={jobId} editPermission={editPermissions} />
    );
};

const ExcludeFromChurnPage = (reactRouterProps: RouteComponentProps<{ builderId: string }>) => {
    const builderId: number = parseInt(reactRouterProps.match.params.builderId);
    return <ExcludeFromChurn builderID={builderId} {...reactRouterProps} />;
};

const AccountingSettingsPage = (reactRouterProps: RouteComponentProps) => {
    return <AccountingSettings {...reactRouterProps} />;
};

const AuthRedirectPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qs: any = queryString.parse(reactRouterProps.location.search, { parseBooleans: true });
    const { code, state, clientId, ...rest } = qs;

    return (
        <AuthRedirect
            code={code}
            state={state}
            clientId={clientId}
            additionalParams={rest}
            {...reactRouterProps}
        />
    );
};

const OAuthRedirectPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qs: any = queryString.parse(reactRouterProps.location.search, { parseBooleans: true });
    return (
        <OAuthRedirect
            code={qs.code}
            realmId={qs.realmId}
            error={qs.error}
            state={qs.state}
            launch={qs.launch}
            disconnect={qs.disconnect}
            queryString={reactRouterProps.location.search}
            {...reactRouterProps}
        />
    );
};

const QuickBooksSyncSchedulePage = () => {
    return <QuickBooksSyncSchedule />;
};

const BidExternalPage = (
    reactRouterProps: RouteComponentProps<{
        builderId: string;
        bidId: string;
        subId: string;
        shareToken?: string;
    }>,
) => {
    return (
        <BidExternal
            builderId={reactRouterProps.match.params.builderId}
            bidId={reactRouterProps.match.params.bidId}
            subId={reactRouterProps.match.params.subId}
            shareToken={reactRouterProps.match.params.shareToken}
            {...reactRouterProps}
        />
    );
};

const PurchaseOrderExternalPage = (
    reactRouterProps: RouteComponentProps<{ poId: string; subId: string; shareToken?: string }>,
) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search, {
        parseBooleans: true,
    });
    const isPdfMode: boolean = qsValues.isPdfMode ?? false;
    return (
        <PurchaseOrderExternal
            poId={reactRouterProps.match.params.poId}
            subId={reactRouterProps.match.params.subId}
            shareToken={reactRouterProps.match.params.shareToken}
            isPdfMode={isPdfMode}
            {...reactRouterProps}
        />
    );
};

const SharedMediaExternalPage = (reactRouterProps: RouteComponentProps<{ shareToken: string }>) => {
    return (
        <SharedMedia shareToken={reactRouterProps.match.params.shareToken} {...reactRouterProps} />
    );
};

const CostCatalogSetupPage = (reactRouterProps: RouteComponentProps<{ viewType?: string }>) => {
    const viewType = +(reactRouterProps.match.params.viewType ?? CostCatalogViewType.Setup);
    return <CostCatalog viewType={viewType} {...reactRouterProps} />;
};

const CostGroupPage = (reactRouterProps: RouteComponentProps<{ id: string }>) => {
    return <CostGroup id={+reactRouterProps.match.params.id} {...reactRouterProps} />;
};

const CostItemPage = (
    reactRouterProps: RouteComponentProps<{ id: string; costCodeId: string }>,
) => {
    let selectedCostCodeId: number | undefined = undefined;
    if (reactRouterProps.match.params.costCodeId) {
        selectedCostCodeId = parseInt(reactRouterProps.match.params.costCodeId);
    }
    return (
        <CostItem
            id={parseInt(reactRouterProps.match.params.id)}
            selectedCostCodeId={selectedCostCodeId}
            {...reactRouterProps}
        />
    );
};

const CostCodeItemUsagePage: React.FunctionComponent<RouteComponentProps<{ id: string }>> = (
    reactRouterProps,
) => {
    return (
        <CostCodeItemUsage id={parseInt(reactRouterProps.match.params.id)} {...reactRouterProps} />
    );
};

const LeadProposalExternalPage = (
    reactRouterProps: RouteComponentProps<{
        proposalId: string;
        leadId: string;
        builderId: string;
        isBuilder: string;
        shareToken?: string;
    }>,
) => {
    return (
        <LeadProposalExternal
            encodedProposalId={reactRouterProps.match.params.proposalId}
            encodedLeadId={reactRouterProps.match.params.leadId}
            encodedBuilderId={reactRouterProps.match.params.builderId}
            isBuilder={reactRouterProps.match.params.isBuilder === "true"}
            shareToken={reactRouterProps.match.params.shareToken}
            {...reactRouterProps}
        />
    );
};

const LeadProposalExternalCheckoutPage = (
    reactRouterProps: RouteComponentProps<{
        builderId: string;
        proposalId: string;
        isBuilder: string;
        shareToken?: string;
    }>,
) => {
    return (
        <LeadProposalCheckout
            encodedProposalId={reactRouterProps.match.params.proposalId}
            externalBuilderId={reactRouterProps.match.params.builderId}
            isBuilder={reactRouterProps.match.params.isBuilder === "true"}
            shareToken={reactRouterProps.match.params.shareToken}
            {...reactRouterProps}
        />
    );
};

const LeadProposalTemplatePage = (
    reactRouterProps: RouteComponentProps<{ builderId: string; proposalTemplateId: string }>,
) => {
    const routeParams = reactRouterProps.match.params;

    const proposalId = parseInt(routeParams.proposalTemplateId);
    return <LeadProposal id={proposalId} leadId={0} {...reactRouterProps} />;
};

const ProposalPage = (
    listPageProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps<{ worksheetId: string }>,
) => {
    const routeParams = reactRouterProps.match.params;
    const worksheetId = parseInt(routeParams.worksheetId, 10);

    return (
        <>
            <PageTitle title="Proposals" />
            <Proposal
                {...listPageProps}
                worksheetId={worksheetId}
                proposalId={null}
                jobId={listPageProps.jobIds[0]}
                isTemplateMode={listPageProps.isTemplateMode}
            />
        </>
    );
};

const ProposalSnapshotPage = (
    listPageProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps<{ worksheetId: string; proposalId: string }>,
) => {
    const routeParams = reactRouterProps.match.params;
    const worksheetId = parseInt(routeParams.worksheetId, 10);
    const proposalId = parseInt(routeParams.proposalId, 10);

    return (
        <>
            <PageTitle title="Proposals" />
            <Proposal
                worksheetId={worksheetId}
                proposalId={proposalId}
                isTemplateMode={false}
                jobId={listPageProps.jobIds[0]}
            />
        </>
    );
};

const MobileLeadProposalPreviewPage = (
    reactRouterProps: RouteComponentProps<{ leadId?: string; proposalId: string }>,
) => {
    const routeParams = reactRouterProps.match.params;

    const proposalId = parseInt(routeParams.proposalId);
    return <MobileLeadProposalPreview id={proposalId} {...reactRouterProps} />;
};

const HomeDepotSettingsPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression
    const qsValues: any = queryString.parse(reactRouterProps.location.search, {
        parseBooleans: true,
    });
    const userContext = useContext(UserInfoContext);
    const builderInfo = useContext(BuilderInfoContext);

    const noAccessDialog = (
        <NoAccess
            {...reactRouterProps}
            reason={ForbiddenReason.Denied}
            builderName={builderInfo?.name}
        />
    );
    return (
        <>
            {userContext?.isAdmin && (
                <HomeDepotSettings
                    {...reactRouterProps}
                    isFirstConnection={qsValues.thdFirstConnected}
                    error={qsValues.thdError}
                />
            )}
            {!userContext?.isAdmin && noAccessDialog}
        </>
    );
};

const LowesSettingsPage = (reactRouterProps: RouteComponentProps) => {
    const userContext = useContext(UserInfoContext);
    const builderInfo = useContext(BuilderInfoContext);

    return userContext?.isAdmin ? (
        <LowesSettings />
    ) : (
        <NoAccess
            reason={ForbiddenReason.Denied}
            builderName={builderInfo?.name}
            {...reactRouterProps}
        />
    );
};

const LeadEmailWizardPage = (reactRouterProps: RouteComponentProps<{ id: string }>) => {
    const routeParams = reactRouterProps.match.params;

    const leadIds = stringToArray(routeParams.id);
    return <LeadEmailWizard ids={leadIds} {...reactRouterProps} />;
};

const ContactSupportPage = (reactRouterProps: RouteComponentProps) => {
    return <ContactUs {...reactRouterProps} />;
};

const VendorStoreFinderPage = (reactRouterProps: RouteComponentProps<{ vendorType: string }>) => {
    const routeParams = reactRouterProps.match.params;
    const vendorType = parseInt(routeParams.vendorType);
    return <VendorStoreFinder vendorType={vendorType} {...reactRouterProps} />;
};

const SubTradeAgreementPage = (
    reactRouterProps: RouteComponentProps<{ builderId: string; subId: string }>,
) => {
    const builderId = parseInt(reactRouterProps.match.params.builderId);
    const subId = parseInt(reactRouterProps.match.params.subId);
    return <TradeAgreement {...reactRouterProps} builderId={builderId} subId={subId} />;
};

const TradeAgreementPrintPage = (
    reactRouterProps: RouteComponentProps<{ tradeAgreementId: string }>,
) => {
    const id = parseInt(reactRouterProps.match.params.tradeAgreementId);
    return <TradeAgreementPrint {...reactRouterProps} id={id} />;
};

const SignupPriceCodePage = () => {
    return <SignupPriceCode />;
};

const SignupWhatsNextPage = (
    reactRouterProps: RouteComponentProps<{
        builderId: string;
        fromEmail: string;
        isRewin: string;
        loginId: string;
    }>,
) => {
    const id = reactRouterProps.match.params.builderId;
    const fromEmail: boolean = reactRouterProps.match.params.fromEmail === "true";
    const isRewin: boolean = reactRouterProps.match.params.isRewin === "true";
    const loginId: string = reactRouterProps.match.params.loginId;
    return (
        <SignupWhatsNext
            {...reactRouterProps}
            builderId={id}
            fromEmail={fromEmail}
            isRewin={isRewin}
            loginId={loginId}
        />
    );
};

const LandingListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Summary" />
            <LandingPage {...reactRouterProps} {...listLayoutProps} />
        </>
    );
};

const OwnerSummaryListPage = (listLayoutProps: IListPageChildProps, route: RouteComponentProps) => {
    return (
        <>
            <PageTitle title="Summary" />
            <OwnerSummary {...route} {...listLayoutProps} />
        </>
    );
};

const JobPickerRouterPage = (listLayoutProps: IListPageChildProps, route: RouteComponentProps) => {
    const nextUrl = parseJobPickerNextUrl(route);
    return <JobPickerRouter {...listLayoutProps} nextUrl={nextUrl} {...route} />;
};

const DailyLogListPage = (listLayoutProps: IListPageChildProps, _route: RouteComponentProps) => {
    return (
        <>
            <PageTitle title="Daily Logs" />
            <DailyLogList {...listLayoutProps} entityType={ListEntityType.DailyLogs} />
        </>
    );
};

const DailyLogViewPage = (listProps: IListPageChildProps) => (
    <>
        <PageTitle title="Daily Logs" />
        <DailyLogView {...listProps} />
    </>
);

const ToDoListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    const systemFilter = getSystemFilter(reactRouterProps);

    return (
        <>
            <PageTitle title="To-Do's" />
            <ToDoListWithPanel
                {...reactRouterProps}
                {...listLayoutProps}
                entityType={ListEntityType.Todos}
                systemFilter={systemFilter}
            />
        </>
    );
};

const OwnerProposalPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps<{
        worksheetId?: string;
        proposalId?: string;
    }>,
) => {
    let worksheetId: number | undefined = undefined;
    let proposalId: number | undefined = undefined;

    if (reactRouterProps.match.params.worksheetId) {
        worksheetId = parseInt(reactRouterProps.match.params.worksheetId);
    }

    if (reactRouterProps.match.params.proposalId) {
        proposalId = parseInt(reactRouterProps.match.params.proposalId);
    }

    return (
        <>
            <PageTitle title="Estimate" />
            <OwnerProposal
                worksheetId={worksheetId}
                proposalId={proposalId}
                jobId={listLayoutProps.jobIds[0]}
                {...reactRouterProps}
                {...listLayoutProps}
            />
        </>
    );
};

const OwnerProposalReviewPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps<{
        proposalId: string;
    }>,
) => {
    const proposalId = parseInt(reactRouterProps.match.params.proposalId);

    return (
        <>
            <PageTitle title="Estimate" />
            <OwnerProposal
                proposalId={proposalId}
                showPreviousProposals
                jobId={listLayoutProps.jobIds[0]}
                {...reactRouterProps}
                {...listLayoutProps}
            />
        </>
    );
};

const EstimateWorksheetPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Estimate" />
            <UserInfoContext.Consumer>
                {(userInfo) =>
                    userInfo && (
                        <Worksheet {...reactRouterProps} {...listLayoutProps} userInfo={userInfo} />
                    )
                }
            </UserInfoContext.Consumer>
        </>
    );
};

const WebviewBuildProposalPage = ({ selectedJobIds }: IWebContainerProps) => {
    return <BuildProposalWebviewContainer selectedJobIds={selectedJobIds} />;
};

const WebviewProposalDetailsPage = ({ selectedJobIds }: IWebContainerProps) => {
    return <ProposalDetailsWebviewContainer selectedJobIds={selectedJobIds} />;
};

const WebviewEstimatePage = ({ selectedJobIds }: IWebContainerProps) => {
    return <EstimateWebviewContainer selectedJobIds={selectedJobIds} />;
};

const WebviewProposalDashboardPage = ({ selectedJobIds }: IWebContainerProps) => {
    return <ProposalDashboardWebviewContainer selectedJobIds={selectedJobIds} />;
};

const WebviewPastWorksheetPage = ({ selectedJobIds }: IWebContainerProps) => {
    const params = useParams() as IReadonlyProposalWorksheetWebviewRouteParams;
    return (
        <ReadonlyProposalWorksheetWebviewContainer
            webviewRouteParams={params}
            jobId={selectedJobIds[0]}
        />
    );
};

const WebviewShiftDetailsPage = () => {
    const params = useParams() as IShiftWebviewRouteParams;
    const routeProps = useRouteProps();
    return <ShiftWebviewContainer webviewRouteParams={params} {...routeProps} />;
};

const WebviewBidDetailsPage = () => {
    const params = useParams() as IBidDetailsWebviewRouteParams;
    const routeProps = useRouteProps();
    return <BidDetailsWebviewContainer webviewRouteParams={params} {...routeProps} />;
};

const WebviewBidSubsDetailsPage = () => {
    const params = useParams() as IBidSubsDetailsWebviewRouteParams;
    const routeProps = useRouteProps();
    return <BidSubsDetailsWebviewContainer webviewRouteParams={params} {...routeProps} />;
};

const WebviewChangeOrderDetailsPage = () => {
    const params = useParams() as IChangeOrderDetailsWebviewRouteParams;
    const routeProps = useRouteProps();
    return <ChangeOrderDetailsWebviewContainer webviewRouteParams={params} {...routeProps} />;
};

const WebviewScheduleDetailsPage = () => {
    const params = useParams() as IScheduleDetailsWebviewRouteParams;
    const routeProps = useRouteProps();
    return <ScheduleDetailsWebviewContainer webviewRouteParams={params} {...routeProps} />;
};

const ProposalDashboardPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    const jobId = listLayoutProps.jobIds[0];
    return (
        <>
            <PageTitle title="Estimate" />
            <ProposalDashboard
                {...reactRouterProps}
                jobId={jobId}
                jobName={listLayoutProps.jobName}
            />
        </>
    );
};

const PastWorksheetPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps<{ worksheetId: string; proposalId: string }>,
) => {
    const routeParams = reactRouterProps.match.params;
    const proposalId = parseInt(routeParams.proposalId, 10);
    return (
        <>
            <PageTitle title="Estimate" />
            <ReadonlyProposalWorksheet
                {...reactRouterProps}
                jobId={listLayoutProps.jobIds[0]}
                jobName={listLayoutProps.jobName}
                proposalId={proposalId}
            />
        </>
    );
};

const OwnerInvoicePaymentsListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Invoice Payments" />
            <InvoicePaymentsList
                {...reactRouterProps}
                {...listLayoutProps}
                entityType={ListEntityType.InvoicePayments}
            />
        </>
    );
};

const OnlinePaymentReportPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Online Payment Report" />
            <OnlinePaymentReports {...reactRouterProps} {...listLayoutProps} />
        </>
    );
};

const OnlinePaymentReportSubsPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Online Payment Report" />
            <OnlinePaymentsList
                {...reactRouterProps}
                {...listLayoutProps}
                entityType={ListEntityType.OnlinePaymentReport}
            />
        </>
    );
};

const CreditMemoListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    const qsParams = parseParams<{ isExternal: string }>(reactRouterProps.location.search);
    const isExternal = qsParams.isExternal === "true";
    return (
        <>
            <PageTitle title="Credit memos" />
            <CreditMemosList
                {...reactRouterProps}
                {...listLayoutProps}
                entityType={ListEntityType.CreditMemos}
                isExternal={isExternal}
            />
        </>
    );
};

const BidPackageListBuildersPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    const systemFilter = getSystemFilter(reactRouterProps);
    return (
        <>
            <PageTitle title="Bids" />
            <BidPackageList
                {...reactRouterProps}
                {...listLayoutProps}
                entityType={ListEntityType.BidPackages}
                systemFilter={systemFilter}
            />
        </>
    );
};

const BidsListSubsPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Bids" />
            <BidList
                {...reactRouterProps}
                {...listLayoutProps}
                entityType={ListEntityType.SubsBids}
            />
        </>
    );
};

const ClientUpdateListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Client Updates" />
            <ClientUpdateList {...reactRouterProps} {...listLayoutProps} allowTemplates={false} />
        </>
    );
};

const PaymentsOverviewPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Buildertrend Payments" />
            <PaymentsOverview {...reactRouterProps} />
        </>
    );
};

const JobsListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Jobs" />
            <JobsList
                {...reactRouterProps}
                {...listLayoutProps}
                entityType={ListEntityType.JobsitesList}
                tab={JobListTabs.List}
            />
        </>
    );
};

const JobsListMapPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Jobs Map" />
            <JobsMapList
                {...reactRouterProps}
                {...listLayoutProps}
                entityType={ListEntityType.JobsMap}
            />
        </>
    );
};

const TemplatesListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps<{ tab: string }>,
) => {
    const tab =
        TemplateListTabs[reactRouterProps.match.params.tab as keyof typeof TemplateListTabs];
    return (
        <>
            <PageTitle title="Templates" />
            <TemplateListLanding {...listLayoutProps} {...reactRouterProps} tab={tab} />
        </>
    );
};

const WarrantyListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    const systemFilter = getSystemFilter(reactRouterProps);

    return (
        <>
            <PageTitle title="Warranties" />
            <WarrantiesList
                {...reactRouterProps}
                {...listLayoutProps}
                entityType={ListEntityType.Warranty}
                systemFilter={systemFilter}
            />
        </>
    );
};

const SubmittalListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    const systemFilter = getSystemFilter(reactRouterProps);

    return (
        <>
            <PageTitle title="Submittals" />
            <SubmittalListPageContainer
                {...listLayoutProps}
                {...reactRouterProps}
                tab="Logs"
                systemFilter={systemFilter}
            />
        </>
    );
};

const SubmittalDetailPageInner = ({
    jobIds,
    isAllJobsSelected,
    jobName,
    reactRouterProps,
}: {
    jobIds: number[];
    isAllJobsSelected: boolean;
    jobName: string;
    reactRouterProps: RouteComponentProps<{ id: string; jobId: string }>;
}) => {
    const routeParams = reactRouterProps.match.params;
    const externalId = routeParams.id;
    const jobId = parseInt(routeParams.jobId);

    useSubmittalJobRedirect(jobIds, isAllJobsSelected, jobId, reactRouterProps.history);

    return (
        <>
            <PageTitle title="Submittal" />
            <Submittal
                {...reactRouterProps}
                externalId={externalId}
                jobId={jobId}
                jobName={jobName}
            />
        </>
    );
};

const SubmittalDetailPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps<{ id: string; jobId: string }>,
) => (
    <SubmittalDetailPageInner
        jobIds={listLayoutProps.jobIds}
        isAllJobsSelected={listLayoutProps.isAllJobsSelected}
        jobName={listLayoutProps.jobName}
        reactRouterProps={reactRouterProps}
    />
);

const OwnerInvoicesListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    const qsParams = parseParams<{
        isExternal: string;
        r: number;
    }>(reactRouterProps.location.search);
    const isExternal = qsParams.isExternal === "true";
    const systemFilter = getSystemFilter(reactRouterProps);
    const referralBy = qsParams.r;
    if (referralBy) {
        const builderId = listLayoutProps.builderId;
        const jobId = listLayoutProps.jobIds[0];

        BTSessionStorage.set("bt-objectArray-CheckoutReferral", {
            builderId,
            jobId,
            isExternal,
            code: "",
            referralBy,
        });
    }
    return (
        <>
            <PageTitle title="Invoices" />
            <OwnerInvoiceList
                {...reactRouterProps}
                {...listLayoutProps}
                entityType={ListEntityType.CustomerInvoices}
                isExternal={isExternal}
                systemFilter={systemFilter}
            />
        </>
    );
};

const CommentsListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps<{ tab: string }>,
) => {
    const tab =
        CommentListTabKeys[reactRouterProps.match.params.tab as keyof typeof CommentListTabKeys];
    return (
        <>
            <PageTitle title="Comments" />
            <CommentsList
                {...reactRouterProps}
                {...listLayoutProps}
                entityType={ListEntityType.Comments}
                tab={tab}
            />
        </>
    );
};

const SurveyListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps<{ tab: string }>,
) => {
    const tab =
        SurveyListContainerTabs[
            reactRouterProps.match.params.tab as keyof typeof SurveyListContainerTabs
        ];

    return (
        <>
            <PageTitle title="Surveys" />
            <SurveyListContainer {...reactRouterProps} {...listLayoutProps} activeTab={tab} />
        </>
    );
};

const OwnerSurveyListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Surveys" />
            <OwnerSurveyList
                entityType={ListEntityType.OwnerSurvey}
                {...reactRouterProps}
                {...listLayoutProps}
            />
        </>
    );
};

const SpecificationListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Specifications" />
            <SpecificationList
                {...reactRouterProps}
                {...listLayoutProps}
                entityType={ListEntityType.Specification}
            />
        </>
    );
};

const SpecificationBookPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Specifications" />
            <SpecificationBook
                {...reactRouterProps}
                jobIds={listLayoutProps.jobIds}
                jobName={listLayoutProps.jobName}
                jobPickerReady={listLayoutProps.jobPickerReady}
                requireSingleJob={true}
            />
        </>
    );
};

/**
 * Base `/Specifications` route. Branches on persona: builders land on the grid, everyone else on
 * Book View. See SpecificationDefaultView.
 */
const SpecificationDefaultViewPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    return (
        <SpecificationDefaultView
            renderBookView={() => SpecificationBookPage(listLayoutProps, reactRouterProps)}
        />
    );
};

const TaskPage = (listLayoutProps: IListPageChildProps) => {
    return (
        <>
            <PageTitle title="Tasks" />
            <TaskPageContainer {...listLayoutProps} isWebView={false} />
        </>
    );
};

const MessagesListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps<{
        folder: string;
        messageId?: string;
    }>,
) => {
    const routeProps = getMessageListPropsFromRoute(reactRouterProps);

    return (
        <>
            <PageTitle title="Messages" />
            <MessagesList
                {...reactRouterProps}
                {...listLayoutProps}
                {...routeProps}
                entityType={ListEntityType.Messages}
            />
        </>
    );
};

const OldMessagesListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    const systemFilter = getSystemFilter(reactRouterProps);

    return (
        <>
            <PageTitle title="Messages" />
            <MessagesList
                {...reactRouterProps}
                {...listLayoutProps}
                entityType={ListEntityType.Messages}
                systemFilter={systemFilter}
                folderId={EmailFolder.Inbox}
            />
        </>
    );
};

const DocumentListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps<ISharedMediaRouteProps>,
) => {
    const directoryTypeName =
        DirectoryType[reactRouterProps.match.params.directoryType as keyof typeof DirectoryType];
    const folderId = parseInt(reactRouterProps.match.params.folderId);

    return (
        <>
            <PageTitle title="Documents" />
            <DocumentList
                {...reactRouterProps}
                {...listLayoutProps}
                entityType={ListEntityType.MediaList}
                allBuildersSelected={false}
                directoryType={directoryTypeName}
                folderId={folderId}
            />
        </>
    );
};

const PhotoListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps<ISharedMediaRouteProps>,
) => {
    const directoryTypeName =
        DirectoryType[reactRouterProps.match.params.directoryType as keyof typeof DirectoryType];
    const folderId = parseInt(reactRouterProps.match.params.folderId);

    return (
        <>
            <PageTitle title="Photos" />
            <PhotoList
                {...reactRouterProps}
                {...listLayoutProps}
                entityType={ListEntityType.MediaList}
                allBuildersSelected={false}
                directoryType={directoryTypeName}
                folderId={folderId}
            />
        </>
    );
};

const VideoListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps<ISharedMediaRouteProps>,
) => {
    const directoryTypeName =
        DirectoryType[reactRouterProps.match.params.directoryType as keyof typeof DirectoryType];
    const folderId = parseInt(reactRouterProps.match.params.folderId);

    return (
        <>
            <PageTitle title="Videos" />
            <VideoList
                {...reactRouterProps}
                {...listLayoutProps}
                entityType={ListEntityType.MediaList}
                allBuildersSelected={false}
                directoryType={directoryTypeName}
                folderId={folderId}
            />
        </>
    );
};

const ChatListPage = () => {
    return (
        <>
            <PageTitle title="Chat" />
            <ChatList />
        </>
    );
};

const SelectionsListPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps<{ tab: string }>,
) => {
    const tab: SelectionListContainerTabs =
        SelectionListContainerTabs[
            reactRouterProps.match.params.tab as keyof typeof SelectionListContainerTabs
        ];

    const systemFilter = getSystemFilter(reactRouterProps);

    // Matches SelectionListContainer's own check: Selections is single-job only, and negative IDs
    // are sentinels (JobIdTypes.NoJobs = -1). Anything else is no valid job, so the guard falls
    // through to the legacy area without fetching config the container would ignore anyway.
    const selectedJobId =
        listLayoutProps.jobIds.length === 1 && listLayoutProps.jobIds[0] > 0
            ? listLayoutProps.jobIds[0]
            : null;

    return (
        <>
            <PageTitle title="Selections" />
            <SelectionsAreaRouteGuard
                jobId={selectedJobId}
                isTemplateMode={listLayoutProps.isTemplateMode}
            >
                <SelectionListContainer
                    {...listLayoutProps}
                    {...reactRouterProps}
                    tab={tab}
                    systemFilter={systemFilter}
                />
            </SelectionsAreaRouteGuard>
        </>
    );
};

const SubSelectionsListPage = (listLayoutProps: IListPageChildProps) => {
    return (
        <>
            <PageTitle title="Selections" />
            <SubSelectionsListPageComponent {...listLayoutProps} />
        </>
    );
};

const SubSummaryListPage = (listLayoutProps: IListPageChildProps, route: RouteComponentProps) => {
    return (
        <>
            <PageTitle title="Summary" />
            <SubSummary {...route} {...listLayoutProps} />
        </>
    );
};

const TimeClockPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps<{ tab: string }>,
) => {
    const tab =
        ShiftListContainerTabs[
            reactRouterProps.match.params.tab as keyof typeof ShiftListContainerTabs
        ];

    return (
        <>
            <PageTitle title="Time Clock" />
            <TimeCardPage
                {...reactRouterProps}
                {...listLayoutProps}
                entityType={ListEntityType.TimeClock}
                currentTab={tab}
            />
        </>
    );
};

const WebviewChatListPage = () => {
    const params = useParams() as IWebviewRouteParams & { interfaceVersion: string };
    return <ChatWebviewContainer webviewRouteParams={params} />;
};

const WebviewTaskListPage = ({ selectedJobIds, generalJobId }: IWebContainerProps) => {
    return <TaskListWebviewContainer selectedJobIds={selectedJobIds} generalJobId={generalJobId} />;
};

const WebviewTaskDetailPage = () => {
    const params = useParams() as IWebviewRouteParams & {
        interfaceVersion: string;
        externalId: string;
        tabType?: string;
    };
    return <TaskDetailWebviewContainer webviewRouteParams={{ ...params }} />;
};

const WebviewDailyLogListPage = ({ selectedJobIds, ...routeProps }: IWebContainerProps) => {
    return <DailyLogListWebviewContainer selectedJobIds={selectedJobIds} {...routeProps} />;
};

const WebviewDailyLogViewPage = ({ ...routeProps }: IWebContainerProps) => {
    return <DailyLogViewWebviewContainer {...routeProps} />;
};

const WebviewDailyLogDetailsPage = ({ ...routeProps }: IWebContainerProps) => {
    return <DailyLogWebviewContainer {...routeProps} />;
};

const WebviewDailyLogAddPage = ({ ...routeProps }: IWebContainerProps) => {
    return <DailyLogAddWebviewContainer {...routeProps} />;
};

const WebviewWarrantyAddPage = ({ ...routeProps }: IWebContainerProps) => {
    return <WarrantyAddWebviewContainer {...routeProps} />;
};

const WebviewCommentThreadPage = () => {
    const params = useParams() as {
        builderId: string;
        entityId: string;
        entityType: string;
        jobId: string;
    } & IWebviewRouteParams;
    const { history, location } = useRouteProps();
    const match = useRouteMatch<IWebviewRouteParams>();

    const builderId = parseInt(params.builderId);
    const entityId = parseInt(params.entityId);
    const entityType = parseInt(params.entityType);
    const platform = params.platform;

    const jobIdParam = params.jobId;
    const jobId = jobIdParam !== undefined ? parseInt(jobIdParam) : undefined;

    return (
        <CommentThread
            builderId={builderId}
            entityId={entityId}
            entityType={entityType}
            jobId={jobId}
            platform={platform}
            history={history}
            location={location}
            match={match}
        />
    );
};

const WebviewOwnerInvoicePrintPage = lazyLoadComponent(
    () => import("./entity/ownerInvoice/OwnerInvoicePrint/OwnerInvoicePrintWebviewContainer"),
);

const BTMobileEditorPage = (
    routeProps: RouteComponentProps<
        {
            viewMode: "embed" | "fullscreen";
            interfaceVersion: string;
        } & IWebviewRouteParams
    >,
) => {
    return <BTEditorMobile {...routeProps.match.params} />;
};

const ScheduleBaselineListPage = (
    listLayoutProps: IListPageChildProps,
    route: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Schedule" />
            <ScheduleListContainer
                {...route}
                {...listLayoutProps}
                currentTab={ScheduleListContainerTabs.Baseline}
                allBuildersSelected={false}
            />
        </>
    );
};

const ScheduleListPage = (listLayoutProps: IListPageChildProps, route: RouteComponentProps) => {
    // TODO: SPA-Schedules -- clean up allBuildersSelected prop when clearing from Gantt usage
    const tabParm = Number((route.match.params as any).tab);
    const systemFilter = getSystemFilter(route);

    return (
        <>
            <PageTitle title="Schedule" />
            <ScheduleListContainer
                {...route}
                {...listLayoutProps}
                currentTab={tabParm}
                allBuildersSelected={false}
                systemFilter={systemFilter}
            />
        </>
    );
};

const bdsWorkdayExceptionFullPageExperience = (
    listLayoutProps: IListPageChildProps,
    route: RouteComponentProps<{ workdayExceptionID: string; jobID: string; isOffline: string }>,
) => {
    const workdayExceptionID: number = Number(route.match.params.workdayExceptionID);
    const jobID: number = Number(route.match.params.jobID);
    const history = route.history;
    const listPage = routes.schedule.getListLink(ScheduleListContainerTabs.Workdays);
    const isOffline: boolean = route.match.params.isOffline === "true";

    return (
        <>
            <PageTitle title="Schedule" />
            <WorkdayExceptionBDS
                {...route}
                {...listLayoutProps}
                workdayExceptionID={workdayExceptionID}
                modalConfig={{
                    parentRoute: listPage,
                    beforeClose: () => {
                        history.push(listPage);
                    },
                }}
                jobIds={listLayoutProps.jobIds}
                jobID={jobID}
                isOffline={isOffline}
            />
        </>
    );
};

const ChangeOrderListPage = (listLayoutProps: IListPageChildProps, route: RouteComponentProps) => {
    const systemFilter = getSystemFilter(route);

    return (
        <>
            <PageTitle title="Change Orders" />
            <ShowOnPortal
                builder
                subs
                render={({ isBuilder }) => (
                    <ChangeOrderList
                        {...route}
                        {...listLayoutProps}
                        entityType={
                            !isBuilder
                                ? ListEntityType.SubChangeOrders
                                : ListEntityType.ChangeOrders
                        }
                        systemFilter={systemFilter}
                    />
                )}
            />
            <ShowOnPortal
                owner
                render={() => (
                    <OwnerChangeOrderList
                        {...route}
                        {...listLayoutProps}
                        jobId={listLayoutProps.jobIds[0]}
                    />
                )}
            />
        </>
    );
};

const DepositListPage = (listLayoutProps: IListPageChildProps, route: RouteComponentProps<any>) => {
    return (
        <>
            <PageTitle title="Deposits" />
            <DepositList {...route} {...listLayoutProps} entityType={ListEntityType.Deposits} />
        </>
    );
};

const RFIsListPage = (listLayoutProps: IListPageChildProps, route: RouteComponentProps) => {
    const systemFilter = getSystemFilter(route);

    return (
        <>
            <PageTitle title="RFIs" />
            <RFIsList
                {...route}
                {...listLayoutProps}
                entityType={ListEntityType.RFIList}
                allBuildersSelected={false}
                systemFilter={systemFilter}
            />
        </>
    );
};

const NotificationHistoryListPage = (
    listLayoutProps: IListPageChildProps,
    route: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Notification History" />
            <NotificationHistoryList
                {...route}
                {...listLayoutProps}
                entityType={ListEntityType.NotificationHistory}
            />
        </>
    );
};

const AdminNotificationHistoryListPage = (
    reactRouterProps: RouteComponentProps<{ builderId: string }>,
) => {
    const urlParams = reactRouterProps.match.params;
    const builderId = Number(urlParams.builderId);
    return <AdminNotificationHistory builderId={builderId} />;
};

const SettingsListPage = (listLayoutProps: IListPageChildProps, route: RouteComponentProps) => {
    return (
        <>
            <PageTitle title="Settings" />
            <SettingList {...route} {...listLayoutProps} entityType={ListEntityType.Settings} />
        </>
    );
};

const OnlinePaymentSettingsFullPage = (
    listLayoutProps: IListPageChildProps,
    route: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Online Payments" />
            <OnlinePaymentSettings {...route} {...listLayoutProps} />
        </>
    );
};

const OnlinePaymentSettingsPage = (reactRouterProps: RouteComponentProps) => {
    return <OnlinePaymentSettings {...reactRouterProps} />;
};

const PaymentsHostedOnboardingPage = (
    listLayoutProps: IListPageChildProps,
    route: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Online Payments" />
            <PaymentsHostedOnboarding {...route} {...listLayoutProps} />
        </>
    );
};

const EnrollmentWizardStartPage = (
    listLayoutProps: IListPageChildProps,
    route: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Enroll in C&D" />
            <EnrollmentWizard {...route} {...listLayoutProps} />
        </>
    );
};

const PaymentTosPage = (
    listLayoutProps: IListPageChildProps,
    route: RouteComponentProps<{ service: string }>,
) => {
    return (
        <>
            <PageTitle title="Pending Terms of Service to Sign" />
            <PaymentTos {...route} {...listLayoutProps} />
        </>
    );
};

const LegacyWebformsServerErrorPage = () => {
    const loginType = useContext(UserInfoContext)?.loginType;

    if (loginType == null || loginType === BTLoginTypes.ALL) {
        return (
            <>
                <PageTitle title="Error Encountered" />
                <ServerError>
                    <BTLink useAutoSPARouting to={routes.login.getListLink()}>
                        Back to login
                    </BTLink>
                </ServerError>
            </>
        );
    }

    const summaryPageLink = routes.landingPage.getLinkForLoginType(loginType, true);

    return (
        <>
            <PageTitle title="Error Encountered" />
            <ServerError>
                <a href={summaryPageLink} target="_top">
                    Back to summary page
                </a>
            </ServerError>
        </>
    );
};

const VendorReceiptPage = (
    reactRouterProps: RouteComponentProps<{
        vendorType: string;
        vendorPurchaseId: string;
    }>,
) => {
    const vendorPurchaseId = parseInt(reactRouterProps.match.params.vendorPurchaseId);
    const vendorType = parseInt(reactRouterProps.match.params.vendorType) as VendorTypes;

    return <VendorReceipt id={vendorPurchaseId} vendorType={vendorType} {...reactRouterProps} />;
};

const OwnerSetupPage = (
    reactRouterProps: RouteComponentProps<{ jobId: string; externalId?: string }>,
) => {
    return (
        <OwnerSetup
            jobId={Number(reactRouterProps.match.params.jobId)}
            externalId={reactRouterProps.match.params.externalId}
            {...reactRouterProps}
        />
    );
};

const SubSetupPage = (
    reactRouterProps: RouteComponentProps<{
        id: string;
        initialTab: string;
        openTradeAgreement: string;
    }>,
) => {
    const id = Number(reactRouterProps.match.params.id);
    const initialTab = reactRouterProps.match.params.initialTab;
    const openTradeAgreement = reactRouterProps.match.params.openTradeAgreement === "true";

    return (
        <SubSetup
            id={id}
            initialTab={initialTab as SubSetupTabKeys}
            openTradeAgreement={openTradeAgreement}
            {...reactRouterProps}
        />
    );
};

const SharedContentPage = (reactRouterProps: RouteComponentProps<{ shareToken: string }>) => {
    const shareToken = reactRouterProps.match.params.shareToken;
    return <SharedContent shareToken={shareToken} {...reactRouterProps} />;
};

const MicrodepositVerificationExternal = (
    reactRouterProps: RouteComponentProps<{
        shareToken: string;
        builderId: string;
        merchantPaymentMethodId: string;
    }>,
) => {
    return (
        <PaymentServiceBankAccountExternal
            shareToken={reactRouterProps.match.params.shareToken}
            encodedBuilderId={reactRouterProps.match.params.builderId}
            encodedMerchantPaymentMethodId={reactRouterProps.match.params.merchantPaymentMethodId}
        />
    );
};

// Mobile pay online wrappers
const OwnerInvoicePayOnlineMobileWrapper = (
    reactRouterProps: RouteComponentProps<{ id: string; jobId: string }>,
) => {
    const qsValues = parseParams<{
        redirectDetails: string;
        landingUrl: string;
        isMobileInAppBrowser: boolean;
    }>(reactRouterProps.location.search, {
        parseBooleans: true,
    });
    let redirectDetails: IPaymentMethodRedirectDetails | undefined;
    if (qsValues.redirectDetails) {
        redirectDetails = JSON.parse(decodeURIComponent(qsValues.redirectDetails));
        if (qsValues.landingUrl) {
            redirectDetails!.oauthRedirectLandingUrl = qsValues.landingUrl;
        }
    }

    const invoiceId = parseInt(reactRouterProps.match.params.id);

    return (
        <OwnerInvoiceCheckout
            id={invoiceId}
            isFromMobileApp
            isMobileInAppBrowser={qsValues.isMobileInAppBrowser}
            redirectDetails={redirectDetails}
            {...reactRouterProps}
        />
    );
};
const LeadProposalPayOnlineMobileWrapper = (
    reactRouterProps: RouteComponentProps<{ id: string }>,
) => {
    const qsValues = parseParams<{
        isMobileInAppBrowser: boolean;
    }>(reactRouterProps.location.search, {
        parseBooleans: true,
    });
    return (
        <LeadProposalPayOnlineMobile
            id={parseInt(reactRouterProps.match.params.id)}
            isMobileInAppBrowser={qsValues.isMobileInAppBrowser}
            {...reactRouterProps}
        />
    );
};

const PostLoginPage = (reactRouterProps: RouteComponentProps) => {
    const qsValues = parseParams<{
        stateIdentifier: string;
        email: string;
        issuer: string;
        activating: string;
    }>(reactRouterProps.location.search);
    const stateIdentifier: string | undefined = qsValues.stateIdentifier;
    const email: string | undefined = qsValues.email;
    const issuer: string | undefined = qsValues.issuer;
    return (
        <PostLogin
            stateIdentifier={stateIdentifier}
            email={email ?? ""}
            isFromAuth0={!!issuer}
            {...reactRouterProps}
        />
    );
};

const LoginPage = (reactRouterProps: RouteComponentProps) => {
    // eslint-disable-next-line deprecate/member-expression, no-restricted-syntax
    const qs = queryString.parse((window as any).location.search);
    // eslint-disable-next-line deprecate/member-expression
    const memoryQs = queryString.parse(reactRouterProps.location.search);
    let oauthType =
        typeof qs.oauthRedirectType === "string"
            ? OAuthType[qs.oauthRedirectType.toLowerCase() as keyof typeof OAuthType]
            : undefined;
    // map legacy query string parameters
    if (qs.redirectskilljar === "true") {
        oauthType = OAuthType.skilljar;
    }
    if (qs.redirectdataproduct === "true") {
        oauthType = OAuthType.dataproduct;
    }
    const enableRedirect =
        typeof qs.performRedirect === "string" ? qs.performRedirect === "true" : true;

    let showOrgSelector =
        typeof qs.showOrgSelector === "string" ? qs.showOrgSelector === "true" : false;
    const userName = typeof memoryQs.userName === "string" ? memoryQs.userName : "";

    // THD needs this for now - integration url should be updated to pass in showOrgSelector instead
    if (oauthType === OAuthType.thd) {
        showOrgSelector = true;
    }

    const promptParams: string[] | undefined = qs.prompt?.toString().split(" ");
    const promptLogin: boolean = promptParams?.includes("login") ?? false;
    const promptSelectAccount: boolean = promptParams?.includes("select_account") ?? false;

    const lastPageRequested = qs.lpr?.toString();

    const oauthState = useOAuthState(lastPageRequested);

    return (
        <EntityListWrapper
            render={(props) => (
                <BTSuspense>
                    <EnvironmentInfoContext.Consumer>
                        {(environmentInfo) => (
                            <>
                                <Route
                                    path={routes.login.list}
                                    exact
                                    render={() => (
                                        <IdpReAuth
                                            oauthState={oauthState}
                                            isOAuthFlow={!!oauthType}
                                        >
                                            <LoginEmailUsernameEntry
                                                loginPostRedirect={lastPageRequested}
                                                reason={qs.reason?.toString()}
                                                oauthRedirectType={oauthType}
                                                redirectUri={qs.redirect_uri?.toString()}
                                                stateParam={qs.state?.toString()}
                                                loginHint={qs.login_hint?.toString()}
                                                userName={qs.bt_username?.toString()}
                                                promptLogin={promptLogin}
                                                issuer={qs.iss?.toString()}
                                                {...props}
                                                {...reactRouterProps}
                                            />
                                        </IdpReAuth>
                                    )}
                                />
                                <Route
                                    path={routes.login.passwordEntry}
                                    render={() => (
                                        <AppDefaultInfoContext.Consumer>
                                            {(appDefaultInfo) => (
                                                <Login
                                                    oauthRedirectType={oauthType}
                                                    redirectUri={qs.redirect_uri?.toString()}
                                                    enableRedirect={enableRedirect}
                                                    loginPostRedirect={qs.lpr?.toString()}
                                                    reason={qs.reason?.toString()}
                                                    stateParam={qs.state?.toString()}
                                                    promptLogin={promptLogin}
                                                    showOrgSelector={
                                                        showOrgSelector || promptSelectAccount
                                                    }
                                                    userName={userName}
                                                    ephEnv={qs.eph_env?.toString()}
                                                    supportMobileEph={
                                                        environmentInfo?.supportMobileEphemeralLogin
                                                    }
                                                    appDefaultInfo={appDefaultInfo}
                                                    {...props}
                                                    {...reactRouterProps}
                                                />
                                            )}
                                        </AppDefaultInfoContext.Consumer>
                                    )}
                                />
                            </>
                        )}
                    </EnvironmentInfoContext.Consumer>
                </BTSuspense>
            )}
        />
    );
};

const StandaloneLoginFramePage = () => {
    return <StandaloneLoginFrame />;
};

const AdminLogExceptionInSentryPage = (reactRouterProps: RouteComponentProps) => {
    return <AdminLogExceptionInSentry {...reactRouterProps} />;
};

const ScheduleConfirmationExternalPage = (
    reactRouterProps: RouteComponentProps<{
        builderId: string;
        userId: string;
        shareToken: string;
    }>,
) => {
    return (
        <ScheduleConfirmationExternal
            builderId={reactRouterProps.match.params.builderId}
            userId={reactRouterProps.match.params.userId}
            shareToken={reactRouterProps.match.params.shareToken}
            {...reactRouterProps}
        />
    );
};

const StuckPaymentUtilityPage = (reactRouterProps: RouteComponentProps) => {
    return <StuckPaymentUtility {...reactRouterProps} />;
};

const BRIUpdateUtilityPage = (reactRouterProps: RouteComponentProps) => {
    return <BRIUpdateUtility {...reactRouterProps} />;
};

const MobileLoginEmailUsernameEntryPage = (reactRouterProps: RouteComponentProps) => {
    const qsValues: any = parseParams(reactRouterProps.location.search);
    const clientId = qsValues.client_id;
    const redirectUri = qsValues.redirect_uri;
    const stateParam = qsValues.state;
    const audience = qsValues.audience;
    const scope = qsValues.scope;
    const codeChallengeMethod = qsValues.code_challenge_method;
    const codeChallenge = qsValues.code_challenge;
    return (
        <MobileLoginEmailUsernameEntry
            clientId={clientId}
            redirectUri={redirectUri}
            stateParam={stateParam}
            audience={audience}
            scope={scope}
            codeChallengeMethod={codeChallengeMethod}
            codeChallenge={codeChallenge}
            {...reactRouterProps}
        />
    );
};

const MobileLoginByCodePage = () => {
    const { code } = useParams<{ code: string }>();
    const location = useLocation();
    const qsValues = parseParams<{
        client_id: string;
        redirect_uri: string;
        state: string;
        audience: string;
        scope: string;
        code_challenge_method: string;
        code_challenge: string;
        redirectPath: string;
        builderId: string;
    }>(location.search);
    const builderId = Number(qsValues.builderId);
    return (
        <MobileLoginByCode
            code={code}
            clientId={qsValues.client_id}
            redirectUri={qsValues.redirect_uri}
            stateParam={qsValues.state}
            audience={qsValues.audience}
            scope={qsValues.scope}
            codeChallengeMethod={qsValues.code_challenge_method}
            codeChallenge={qsValues.code_challenge}
            redirectPath={qsValues.redirectPath}
            builderId={Number.isFinite(builderId) && builderId > 0 ? builderId : undefined}
        />
    );
};

const ImpersonationRedirectPage = () => {
    const location = useLocation();
    const qsValues = parseParams<{
        targetGlobalUserId: string;
        redirectPath: string;
    }>(location.search);
    const targetGlobalUserId = Number(qsValues.targetGlobalUserId);
    return (
        <ImpersonationRedirect
            targetGlobalUserId={
                Number.isFinite(targetGlobalUserId) && targetGlobalUserId > 0
                    ? targetGlobalUserId
                    : undefined
            }
            redirectPath={qsValues.redirectPath}
        />
    );
};

const FinancialDocumentCollectionPage = (
    listLayoutProps: IListPageChildProps,
    route: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Upload Documents" />
            <FinancialDocumentCollection {...route} {...listLayoutProps} />
        </>
    );
};

const CostCodesPage = (listLayoutProps: IListPageChildProps, routeProps: RouteComponentProps) => {
    return (
        <>
            <PageTitle title="Cost Codes" />
            <CostCodeList {...routeProps} {...listLayoutProps} />
        </>
    );
};

const JobCostingBudgetPage = (
    listLayoutProps: IListPageChildProps,
    routeProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Job Costing Budget" />
            <JobCostingBudget
                {...routeProps}
                {...listLayoutProps}
                entityType={ListEntityType.JobCostingBudget}
            />
        </>
    );
};
const LeadOpportunityListPage = (
    listLayoutProps: IListPageChildProps,
    route: RouteComponentProps,
) => {
    const systemFilter = getSystemFilter(route);
    return (
        <>
            <PageTitle title="Leads" />
            <LeadListContainer {...route} {...listLayoutProps} systemFilter={systemFilter} />
        </>
    );
};

const AdminMerchantPaymentListPage = (
    reactRouterProps: RouteComponentProps<{ activeTab: string }>,
) => {
    const activeTab = parseInt(
        reactRouterProps.match.params.activeTab,
    ) as AdminOnlinePaymentReportTabs;
    return <AdminOnlinePaymentReports {...reactRouterProps} activeTab={activeTab} />;
};
const AdminMainPage = () => {
    return <AdminMain />;
};

const AdminSubscriptionManagerPage = () => {
    return <AdminSubscriptionManager />;
};

const SpamEmailRemovalPage = () => {
    return <SpamEmailRemoval />;
};

const AdminUsernameLookupPage = () => {
    const params = useQueryString<{ username?: string }>();
    return <AdminUsernameLookup username={params.username ?? ""} />;
};

const AdminAppSettingsPage = () => {
    return <AdminAppSettings />;
};

const ClientJobPermissionsPage = (
    listLayoutProps: IListPageChildProps,
    reactRouterProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Permissions" />
            <ClientDefaultJobPermissions {...reactRouterProps} {...listLayoutProps} />
        </>
    );
};

const ReportListPage = () => {
    return (
        <>
            <PageTitle title="Reports" />
            <ReportListLazyLoaded />
        </>
    );
};

const WIPReportPage = (
    listLayoutProps: IListPageChildProps,
    routeProps: RouteComponentProps<{ openJobLimit: string }>,
) => {
    const openJobLimit = !isNaN(parseInt(routeProps.match.params.openJobLimit))
        ? parseInt(routeProps.match.params.openJobLimit)
        : undefined;
    return (
        <>
            <PageTitle title="Work in progress report" />
            <WorkInProgressReportLazyLoaded
                openJobLimit={openJobLimit}
                {...routeProps}
                {...listLayoutProps}
            />
        </>
    );
};

const ProfitabilityReportPage = (
    listLayoutProps: IListPageChildProps,
    routeProps: RouteComponentProps<{ openJobLimit: string }>,
) => {
    const openJobLimit = !isNaN(parseInt(routeProps.match.params.openJobLimit))
        ? parseInt(routeProps.match.params.openJobLimit)
        : undefined;
    return (
        <>
            <PageTitle title="Profitability report" />
            <ProfitabilityReportLazyLoaded
                openJobLimit={openJobLimit}
                {...routeProps}
                {...listLayoutProps}
            />
        </>
    );
};

const InvoicingReportPage = (
    listLayoutProps: IListPageChildProps,
    routeProps: RouteComponentProps<{ openJobLimit: string }>,
) => {
    const openJobLimit = !isNaN(parseInt(routeProps.match.params.openJobLimit))
        ? parseInt(routeProps.match.params.openJobLimit)
        : undefined;
    return (
        <>
            <PageTitle title="Invoicing report" />
            <InvoicingReportLazyLoaded
                openJobLimit={openJobLimit}
                {...routeProps}
                {...listLayoutProps}
            />
        </>
    );
};

const BudgetedVsProjectedReportPage = (
    listLayoutProps: IListPageChildProps,
    routeProps: RouteComponentProps<{ openJobLimit: string }>,
) => {
    const openJobLimit = !isNaN(parseInt(routeProps.match.params.openJobLimit))
        ? parseInt(routeProps.match.params.openJobLimit)
        : undefined;
    return (
        <>
            <PageTitle title="Budgeted vs. projected report" />
            <BudgetedVsProjectedReportLazyLoaded
                openJobLimit={openJobLimit}
                {...routeProps}
                {...listLayoutProps}
            />
        </>
    );
};

const HoursByJobReportPage = (
    listLayoutProps: IListPageChildProps,
    routeProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Hours worked, by job" />
            <HoursWorkedByJobReportLazyLoaded {...routeProps} {...listLayoutProps} />
        </>
    );
};

const HoursByEmployeeReportPage = (
    listLayoutProps: IListPageChildProps,
    routeProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Hours worked, by employee" />
            <HoursWorkedByEmployeeReportLazyLoaded {...routeProps} {...listLayoutProps} />
        </>
    );
};

const DailyLogCreationByJobReportPage = (
    listLayoutProps: IListPageChildProps,
    routeProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Daily log creation by job" />
            <DailyLogCreationByJobReportLazyLoaded {...routeProps} {...listLayoutProps} />
        </>
    );
};

const LeadCountBySalespersonReportPage = (
    listLayoutProps: IListPageChildProps,
    routeProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Lead count by salesperson" />
            <LeadCountBySalespersonReportLazyLoaded {...routeProps} {...listLayoutProps} />
        </>
    );
};

const DailyLogsByUserReportPage = (
    listLayoutProps: IListPageChildProps,
    routeProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Daily log count by user" />
            <DailyLogsByUserReportLazyLoaded {...routeProps} {...listLayoutProps} />
        </>
    );
};

const SchedulePercentCompleteReportPage = (
    listLayoutProps: IListPageChildProps,
    routeProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Schedule percent complete by job" />
            <SchedulePercentCompleteByJobReportLazyLoaded {...routeProps} {...listLayoutProps} />
        </>
    );
};

const LaborActualsVsBudgetedReportPage = (
    listLayoutProps: IListPageChildProps,
    routeProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Labor actuals vs. budgeted" />
            <LaborActualsVsBudgetedReportLazyLoaded {...routeProps} {...listLayoutProps} />
        </>
    );
};

const CashflowReportPage = (
    listLayoutProps: IListPageChildProps,
    routeProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Cashflow report" />
            <CashflowReportLazyLoaded {...routeProps} {...listLayoutProps} />
        </>
    );
};

const LeadActivitiesBySalespersonReportPage = (
    listLayoutProps: IListPageChildProps,
    routeProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Lead activities by salesperson" />
            <LeadActivitiesBySalespersonReportLazyLoaded {...routeProps} {...listLayoutProps} />
        </>
    );
};

const LeadStatusBySourceReportPage = (
    listLayoutProps: IListPageChildProps,
    routeProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Lead status by source" />
            <LeadStatusBySourceReportLazyLoaded {...routeProps} {...listLayoutProps} />
        </>
    );
};

const BaselineVsActualDurationReportPage = (
    listLayoutProps: IListPageChildProps,
    routeProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Baseline vs. actual duration by job" />
            <BaselineVsActualDurationReportLazyLoaded {...routeProps} {...listLayoutProps} />
        </>
    );
};

const ChangeOrderProfitReportPage = (
    listLayoutProps: IListPageChildProps,
    routeProps: RouteComponentProps,
) => {
    return (
        <>
            <PageTitle title="Change order profit" />
            <ChangeOrderProfitReportLazyLoaded {...routeProps} {...listLayoutProps} />
        </>
    );
};

const PlansListPage = (listLayoutProps: IListPageChildProps) => {
    return (
        <>
            <PageTitle title="Plans" />
            <PlansList {...listLayoutProps} allBuildersSelected={false} requireSingleJob />
        </>
    );
};

const PlanPageDetailsPage = (listLayoutProps: IListPageChildProps) => {
    return (
        <>
            <PageTitle title="Plan Viewer" />
            <PlanPageDetails {...listLayoutProps} allBuildersSelected={false} />
        </>
    );
};

const PlanRevisionsReviewPage = (listLayoutProps: IListPageChildProps) => {
    return (
        <>
            <PageTitle title="Review revisions" />
            <PlanRevisionsReview {...listLayoutProps} />
        </>
    );
};

// #endregion

export const AppRoutes = (): JSX.Element => {
    const builderInfo = useContext(BuilderInfoContext);
    const appContext = useContext(AppDefaultInfoContext);
    const userInfo = useContext(UserInfoContext);
    const isDraftBillsEffective = useIsDraftBillsEffective();
    const { isOptedIn: isInfoArchOptedIn } = useInfoArchOptIn();
    return (
        /* eslint-disable-next-line react/forbid-elements */
        <Switch>
            <Route path={apiDocsRoute.path}>
                <ApiDocs />
            </Route>
            {/* Buildertrend staff only, and only while impersonating a builder — `isImpersonatingBuilder`
            is the client's view of IUserContext.HasBTAdmin. Unrendered, the URL falls through to the
            404 at the bottom of this Switch, so the page never loads for a customer. This mirrors the
            same check on POST ai-agents/message, which is the gate that actually enforces it. */}
            {userInfo?.isImpersonatingBuilder === true && (
                <Route path={agentTesterRoute.path}>
                    <AgentTester />
                </Route>
            )}
            {/* BTAdmin Routes */}
            <BTAdminLayout
                path={routes.admin.users.usernameLookup}
                element={<AdminUsernameLookupPage />}
            />
            <BTAdminLayout
                path={routes.admin.devUtilities.appSettings}
                component={AdminAppSettingsPage}
            />
            {/* Global routes */}
            <Route path={routes.notConnected.path}>
                <OrgLinkNotConnectedEmptyStatePage />
            </Route>
            <Route path={routes.subscriptionInactive.path}>
                <SubscriptionInactiveEmptyState />
            </Route>
            <Route path={routes.auth0.stepUpCallback} exact>
                <StepUpAuthCallback />
            </Route>
            <Route
                path={routes.login.standaloneFrame}
                exact
                render={() => <StandaloneLoginFramePage />}
            />
            <DetailPageLayout path={routes.login.list} component={LoginPage} />
            <ListPageLayout
                path={routes.plans.viewer}
                component={PlanPageDetailsPage}
                showJobPicker={false}
                jobPickerDisplayMode={JobPickerDisplayModes.Specifications}
                jobPickerSelectMode={JobPickerSelectModes.Single}
                selectedMenuItem={MenuItemName.Specifications}
                hideOwnerHeaderInfo
                keepQueryParams
            />
            <ListPageLayout
                path={routes.plans.revisionsReview}
                component={PlanRevisionsReviewPage}
                showJobPicker={false}
                jobPickerDisplayMode={JobPickerDisplayModes.Specifications}
                jobPickerSelectMode={JobPickerSelectModes.Single}
                selectedMenuItem={MenuItemName.Specifications}
                hideOwnerHeaderInfo
                keepQueryParams
            />
            <ListPageLayout
                path={routes.plans.list}
                component={PlansListPage}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                jobPickerDisplayMode={JobPickerDisplayModes.Specifications}
                jobPickerSelectMode={JobPickerSelectModes.Single}
                selectedMenuItem={MenuItemName.Specifications}
            />
            <ListPageLayout
                path={routes.estimate.buildProposal}
                component={ProposalPage}
                showJobPicker={true}
                jobPickerDisplayMode={JobPickerDisplayModes.Estimates}
                jobPickerSelectMode={JobPickerSelectModes.Single}
                redirectOnJobChangeURL="/app/Estimate"
                keepQueryParams
                selectedMenuItem={MenuItemName.Estimate}
            />
            <ListPageLayout
                path={routes.estimate.proposalSnapshot}
                component={ProposalSnapshotPage}
                showJobPicker={true}
                jobPickerDisplayMode={JobPickerDisplayModes.Estimates}
                jobPickerSelectMode={JobPickerSelectModes.Single}
                redirectOnJobChangeURL={"/app" + routes.proposal.proposalDashboard}
                keepQueryParams
                selectedMenuItem={MenuItemName.Estimate}
            />
            <ListPageLayout
                path={preliminaryEstimateGenerateRoute.path}
                component={PreliminaryEstimateGeneratePage}
                showJobPicker={false}
                selectedMenuItem={MenuItemName.None}
                hideOwnerHeaderInfo
            />
            {/* Exact, because route paths otherwise match by prefix: without it the list would also
                answer for /PreliminaryEstimate/Generate and for the not-yet-registered detail route
                (#281500), showing the list instead of the page the URL asked for. */}
            <ListPageLayout
                path={preliminaryEstimateRoute.path}
                exact
                component={PreliminaryEstimateListPage}
                showJobPicker={false}
                selectedMenuItem={MenuItemName.None}
                hideOwnerHeaderInfo
            />
            <ListPageLayout
                path={routes.jobPicker.selectJob}
                component={JobPickerRouterPage}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
                jobPickerDisplayMode={JobPickerDisplayModes.JobPickerRouter}
                selectedMenuItem={MenuItemName.None}
                legacyIncludeGlobalJob={!builderInfo?.configFlags?.hideTimeClockGeneralJob}
                keepQueryParams
                isJobPickerRouter
            />
            <DetailPageLayout path={routes.account.frozen} element={<FrozenBuilderWrapper />} />
            <DetailPageLayout
                path={routes.account.frozenExternal}
                element={<FrozenBuilderWrapper />}
            />
            <DetailPageLayout path={routes.lead.emailUnsubscribe} element={<EmailUnsubscribe />} />
            <DetailPageLayout path={routes.lead.emailResubscribe} element={<EmailResubscribe />} />
            <Route
                path={routes.selectionChoice.signaturePad}
                component={SelectionChoiceSignaturePadPage}
            />
            <Route path={routes.selection.favorites} component={SelectionFavoriteListPage} />
            <Route path={routes.selection.projectedTotal} component={ProjectedTotalPage} />
            <ListPageLayout
                path={routes.landingPage.path}
                showJobPicker
                component={LandingListPage}
                selectedMenuItem={MenuItemName.Summary}
                jobPickerDisplayMode={JobPickerDisplayModes.Jobsites}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.reporting.WIPReport.path}
                showJobPicker={false}
                component={WIPReportPage}
                keepQueryParams
                selectedMenuItem={MenuItemName.Reporting}
            />
            <ListPageLayout
                path={routes.reporting.ProfitabilityReport.path}
                showJobPicker={false}
                component={ProfitabilityReportPage}
                keepQueryParams
                selectedMenuItem={MenuItemName.Reporting}
            />
            <ListPageLayout
                path={routes.reporting.InvoicingReport.path}
                showJobPicker={false}
                component={InvoicingReportPage}
                keepQueryParams
                selectedMenuItem={MenuItemName.Reporting}
            />
            <ListPageLayout
                path={routes.reporting.BudgetedVsProjected.path}
                showJobPicker={false}
                component={BudgetedVsProjectedReportPage}
                keepQueryParams
                selectedMenuItem={MenuItemName.Reporting}
            />
            <ListPageLayout
                path={routes.reporting.HoursByJobReport.path}
                showJobPicker={false}
                component={HoursByJobReportPage}
                keepQueryParams
                selectedMenuItem={MenuItemName.Reporting}
            />
            <ListPageLayout
                path={routes.reporting.DailyLogCreationByJobReport.path}
                showJobPicker={false}
                component={DailyLogCreationByJobReportPage}
                keepQueryParams
                selectedMenuItem={MenuItemName.Reporting}
            />
            <ListPageLayout
                path={routes.reporting.HoursByEmployeeReport.path}
                showJobPicker={false}
                component={HoursByEmployeeReportPage}
                keepQueryParams
                selectedMenuItem={MenuItemName.Reporting}
            />
            <ListPageLayout
                path={routes.reporting.SchedulePercentCompleteByJob.path}
                showJobPicker={false}
                component={SchedulePercentCompleteReportPage}
                keepQueryParams
                selectedMenuItem={MenuItemName.Reporting}
            />
            <ListPageLayout
                path={routes.reporting.LaborActualsVsBudgetedReport.path}
                showJobPicker={false}
                component={LaborActualsVsBudgetedReportPage}
                keepQueryParams
                selectedMenuItem={MenuItemName.Reporting}
            />
            <ListPageLayout
                path={routes.reporting.DailyLogsByUserReport.path}
                showJobPicker={false}
                component={DailyLogsByUserReportPage}
                keepQueryParams
                selectedMenuItem={MenuItemName.Reporting}
            />
            <ListPageLayout
                path={routes.reporting.CashflowReport.path}
                showJobPicker={false}
                component={CashflowReportPage}
                keepQueryParams
                selectedMenuItem={MenuItemName.Reporting}
            />
            <ListPageLayout
                path={routes.reporting.LeadActivitiesBySalespersonReport.path}
                showJobPicker={false}
                component={LeadActivitiesBySalespersonReportPage}
                keepQueryParams
                selectedMenuItem={MenuItemName.Reporting}
            />
            <ListPageLayout
                path={routes.reporting.LeadStatusBySourceReport.path}
                showJobPicker={false}
                component={LeadStatusBySourceReportPage}
                keepQueryParams
                selectedMenuItem={MenuItemName.Reporting}
            />
            <ListPageLayout
                path={routes.reporting.BaselineVsActualDurationReport.path}
                showJobPicker={false}
                component={BaselineVsActualDurationReportPage}
                keepQueryParams
                selectedMenuItem={MenuItemName.Reporting}
            />
            <ListPageLayout
                path={routes.reporting.ChangeOrderProfitReport.path}
                showJobPicker={false}
                component={ChangeOrderProfitReportPage}
                keepQueryParams
                selectedMenuItem={MenuItemName.Reporting}
            />
            <ListPageLayout
                path={routes.reporting.LeadCountBySalespersonReport.path}
                showJobPicker={false}
                component={LeadCountBySalespersonReportPage}
                keepQueryParams
                selectedMenuItem={MenuItemName.Reporting}
            />
            <ListPageLayout
                path={routes.reporting.reportList.path}
                showJobPicker={false}
                component={ReportListPage}
                selectedMenuItem={MenuItemName.Reporting}
            />
            {/* Customer Portal Routes */}
            <ListPageLayout
                path={routes.home}
                exact
                component={IndexPage}
                selectedMenuItem={MenuItemName.None}
                showJobPicker={false}
            />
            <Route path={routes.entityLink.path}>
                <EntityLinkRouter />
            </Route>
            <ListPageLayout
                path={routes.ownerSummary.getLink()}
                showJobPicker={false}
                component={OwnerSummaryListPage}
                selectedMenuItem={MenuItemName.Summary}
                hideOwnerHeaderInfo
            />
            <ListPageLayout
                path={routes.subSummary.path}
                showJobPicker={isInPortal({ subs: true })}
                component={SubSummaryListPage}
                selectedMenuItem={MenuItemName.Summary}
                jobPickerDisplayMode={JobPickerDisplayModes.Jobsites}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.lead.leadList}
                showJobPicker={false}
                component={LeadOpportunityListPage}
                selectedMenuItem={MenuItemName.Leads}
            />
            <ListPageLayout
                path={routes.dailyLog.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={DailyLogListPage}
                selectedMenuItem={MenuItemName.DailyLogs}
                jobPickerDisplayMode={JobPickerDisplayModes.DailyLogs}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.internalUsers.list}
                showJobPicker={false}
                component={InternalUsersListPage}
                selectedMenuItem={MenuItemName.Users}
            />
            <ListPageLayout
                path={routes.contact.list}
                showJobPicker={false}
                component={ContactsListPage}
                selectedMenuItem={MenuItemName.Contacts}
            />
            <ListPageLayout
                path={routes.sub.list}
                showJobPicker={false}
                component={SubsListPage}
                selectedMenuItem={MenuItemName.Subs}
            />
            <ListPageLayout
                path={routes.toDo.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={ToDoListPage}
                selectedMenuItem={MenuItemName.ToDos}
                jobPickerDisplayMode={JobPickerDisplayModes.ToDos}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <PrintPageLayout
                minWidth={800}
                path={routes.estimate.legacyProposalPrint}
                component={LegacyProposalPrintPage}
            />
            <PrintPageLayout
                minWidth={800}
                path={routes.estimate.proposalPrint}
                component={ProposalPrintPage}
            />
            <PrintPageLayout
                minWidth={800}
                path={routes.estimate.proposalPrintByProposalId}
                element={ProposalPrintByProposalIdPage}
            />
            <ListPageLayout
                path={routes.estimate.ownerProposal}
                showJobPicker={false}
                component={OwnerProposalPage}
                redirectOnJobChangeURL="/app/Owner/Estimate"
                selectedMenuItem={MenuItemName.Estimate}
            />
            <ListPageLayout
                path={routes.estimate.ownerProposalReview}
                showJobPicker={false}
                component={OwnerProposalReviewPage}
                selectedMenuItem={MenuItemName.Estimate}
            />
            <ListPageLayout
                path={routes.estimate.legacyOwnerProposal}
                showJobPicker={false}
                component={OwnerProposalPage}
                selectedMenuItem={MenuItemName.Estimate}
            />
            <ListPageLayout
                path={routes.estimate.ownerEstimate}
                showJobPicker={false}
                component={OwnerProposalPage}
                redirectOnJobChangeURL="/app/Owner/Estimate"
                selectedMenuItem={MenuItemName.Estimate}
            />
            <PrintPageLayout
                minWidth={800}
                path={routes.estimate.worksheetPrint}
                component={ProposalPrintPage}
            />
            <ListPageLayout
                path={routes.proposal.proposalDashboard}
                showJobPicker={isInPortal({ builder: true })}
                component={ProposalDashboardPage}
                selectedMenuItem={MenuItemName.Estimate}
                jobPickerDisplayMode={JobPickerDisplayModes.Estimates}
                jobPickerSelectMode={JobPickerSelectModes.Single}
            />
            <ListPageLayout
                path={routes.proposal.pastWorksheet}
                showJobPicker={isInPortal({ builder: true })}
                component={PastWorksheetPage}
                selectedMenuItem={MenuItemName.Estimate}
                jobPickerDisplayMode={JobPickerDisplayModes.Estimates}
                jobPickerSelectMode={JobPickerSelectModes.Single}
                redirectOnJobChangeURL="/app/Estimate"
            />
            <ListPageLayout
                path={routes.estimate.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={EstimateWorksheetPage}
                selectedMenuItem={MenuItemName.Estimate}
                jobPickerDisplayMode={JobPickerDisplayModes.Estimates}
                jobPickerSelectMode={JobPickerSelectModes.Single}
            />
            <ListPageLayout
                path={routes.bidPackage.list}
                showJobPicker={isInPortal({ builder: true })}
                component={BidPackageListBuildersPage}
                selectedMenuItem={MenuItemName.Bids}
                jobPickerDisplayMode={JobPickerDisplayModes.Bids}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.bid.list}
                showJobPicker={false}
                component={BidsListSubsPage}
                selectedMenuItem={MenuItemName.Bids}
                jobPickerDisplayMode={JobPickerDisplayModes.Bids}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.clientUpdates.list}
                showJobPicker={true}
                component={ClientUpdateListPage}
                selectedMenuItem={MenuItemName.ClientUpdates}
                jobPickerSelectMode={JobPickerSelectModes.Single}
                jobPickerDisplayMode={JobPickerDisplayModes.Custom}
            />
            <ListPageLayout
                path={routes.onlinePayments.paymentOverview}
                selectedMenuItem={MenuItemName.AcceptOnlinePayments}
                component={PaymentsOverviewPage}
                showJobPicker={false}
            />
            <ListPageLayout
                path={jobDetailRoute.path}
                component={JobPage}
                showJobPicker={false}
                selectedMenuItem={MenuItemName.JobInfo}
                keepQueryParams
            />
            <ListPageLayout
                path={routes.job.jobFinancials}
                component={JobFinancialsPage}
                showJobPicker={isInPortal({ builder: true })}
                jobPickerDisplayMode={JobPickerDisplayModes.Jobsites}
                jobPickerSelectMode={JobPickerSelectModes.Single}
                selectedMenuItem={MenuItemName.JobPriceSummary}
                keepQueryParams
            />
            <ListPageLayout
                path={routes.job.list}
                showJobPicker={false}
                component={JobsListPage}
                selectedMenuItem={MenuItemName.JobsList}
            />
            <ListPageLayout
                path={routes.job.map}
                showJobPicker={false}
                component={JobsListMapPage}
                selectedMenuItem={MenuItemName.JobsMap}
            />
            <ListPageLayout
                path={routes.template.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={TemplatesListPage}
                selectedMenuItem={MenuItemName.Templates}
                jobPickerDisplayMode={JobPickerDisplayModes.Jobsites}
                jobPickerSelectMode={JobPickerSelectModes.Single}
            />
            {/* Specification pages */}
            <ListPageLayout
                path={routes.specification.openConversation}
                showJobPicker={false}
                component={SpecificationConversationRedirect}
                selectedMenuItem={MenuItemName.Specifications}
                jobPickerSelectMode={JobPickerSelectModes.Single}
            />
            <PrintPageLayout
                path={routes.specification.print}
                element={<SpecificationPrintPage />}
            />
            <ListPageLayout
                path={routes.specification.bookWithSpec}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={SpecificationBookPage}
                selectedMenuItem={MenuItemName.Specifications}
                jobPickerDisplayMode={JobPickerDisplayModes.Specifications}
                jobPickerSelectMode={JobPickerSelectModes.Single}
            />
            <ListPageLayout
                path={routes.specification.book}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={SpecificationBookPage}
                selectedMenuItem={MenuItemName.Specifications}
                jobPickerDisplayMode={JobPickerDisplayModes.Specifications}
                jobPickerSelectMode={JobPickerSelectModes.Single}
            />
            <ListPageLayout
                path={routes.specification.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={SpecificationListPage}
                selectedMenuItem={MenuItemName.Specifications}
                jobPickerDisplayMode={JobPickerDisplayModes.Specifications}
                jobPickerSelectMode={JobPickerSelectModes.Single}
            />
            <ListPageLayout
                path={routes.specification.base}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={SpecificationDefaultViewPage}
                selectedMenuItem={MenuItemName.Specifications}
                jobPickerDisplayMode={JobPickerDisplayModes.Specifications}
                jobPickerSelectMode={JobPickerSelectModes.Single}
            />
            {/* Tasks pages */}
            <FullPageLayout path={routes.task.details} element={<TaskDetail />} title="Tasks" />
            <PrintPageLayout path={routes.task.singlePrint} element={TaskSinglePrintPage} />
            <PrintPageLayout path={routes.task.bulkPrint} element={TaskBulkPrintPage} />
            <ListPageLayout
                path={routes.task.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={TaskPage}
                selectedMenuItem={MenuItemName.Calendar}
                jobPickerDisplayMode={JobPickerDisplayModes.Jobsites}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
                keepQueryParams
                includeGeneralJob={builderInfo?.flags?.tasksGeneralJob ?? false}
                useLocalStorageFallbackJobIds
            />
            {/* TODO: Should this be using the print page layout? */}
            <DetailPageLayout path={routes.messages.print} element={<MessagePrintPage />} />
            <ListPageLayout
                path={routes.messages.details}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={MessagesListPage}
                selectedMenuItem={MenuItemName.Messages}
                jobPickerDisplayMode={JobPickerDisplayModes.Messages}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.messages.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={MessagesListPage}
                selectedMenuItem={MenuItemName.Messages}
                jobPickerDisplayMode={JobPickerDisplayModes.Messages}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            {/* Temporary to maintain old routes */}
            <ListPageLayout
                path={routes.messages.oldList}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={OldMessagesListPage}
                selectedMenuItem={MenuItemName.Messages}
                jobPickerDisplayMode={JobPickerDisplayModes.Messages}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <WebviewPageLayout
                path={routes.messages.webviewList}
                component={MessageListWebviewContainer}
                title="Messages"
            />
            <WebviewPageLayout
                path={routes.messages.webviewCompose}
                component={MessageComposeWebviewContainer}
                title="Compose Message"
            />
            <WebviewPageLayout
                path={routes.messages.webviewPrint}
                component={MessagePrintWebviewContainer}
                title="Message Print"
            />
            <WebviewPageLayout
                path={routes.messages.webviewDetails}
                component={MessageWebviewContainer}
                title="Message"
            />
            <ListPageLayout
                path={routes.document.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={DocumentListPage}
                selectedMenuItem={MenuItemName.Documents}
                jobPickerDisplayMode={JobPickerDisplayModes.Documents}
                jobPickerSelectMode={JobPickerSelectModes.Single}
                allowGlobalDocs
            />
            <ListPageLayout
                path={routes.photo.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={PhotoListPage}
                selectedMenuItem={MenuItemName.Photos}
                jobPickerDisplayMode={JobPickerDisplayModes.Photos}
                jobPickerSelectMode={JobPickerSelectModes.Single}
            />
            <ListPageLayout
                path={routes.video.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={VideoListPage}
                selectedMenuItem={MenuItemName.Videos}
                jobPickerDisplayMode={JobPickerDisplayModes.Videos}
                jobPickerSelectMode={JobPickerSelectModes.Single}
                allowGlobalDocs
            />
            <ListPageLayout
                path={routes.warranty.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={WarrantyListPage}
                selectedMenuItem={MenuItemName.Warranty}
                jobPickerDisplayMode={JobPickerDisplayModes.Warranty}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
                keepQueryParams
            />
            <PrintPageLayout path={routes.submittal.print} element={SubmittalPrint} />
            <ListPageLayout
                path={routes.submittal.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={SubmittalListPage}
                selectedMenuItem={MenuItemName.Submittals}
                jobPickerDisplayMode={JobPickerDisplayModes.Submittals}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.submittal.details}
                component={SubmittalDetailPage}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                jobPickerDisplayMode={JobPickerDisplayModes.Submittals}
                selectedMenuItem={MenuItemName.Submittals}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.ownerInvoice.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={OwnerInvoicesListPage}
                selectedMenuItem={MenuItemName.OwnerPayments}
                jobPickerDisplayMode={JobPickerDisplayModes.OwnerPayments}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.invoicePayments.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={OwnerInvoicePaymentsListPage}
                selectedMenuItem={MenuItemName.OwnerPayments}
                jobPickerDisplayMode={JobPickerDisplayModes.OwnerPayments}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <PrintPageLayout path={routes.creditMemos.print} component={CreditMemoPrintPage} />
            <ListPageLayout
                path={routes.creditMemos.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={CreditMemoListPage}
                selectedMenuItem={MenuItemName.OwnerPayments}
                jobPickerDisplayMode={JobPickerDisplayModes.OwnerPayments}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.onlinePayments.paymentReport}
                showJobPicker={isInPortal({ builder: true })}
                component={OnlinePaymentReportPage}
                selectedMenuItem={MenuItemName.OnlinePaymentReport}
                jobPickerDisplayMode={JobPickerDisplayModes.OnlinePaymentReport}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.onlinePayments.paymentReportSubs}
                showJobPicker={isInPortal({ subs: true })}
                component={OnlinePaymentReportSubsPage}
                selectedMenuItem={MenuItemName.OnlinePaymentReport}
                jobPickerDisplayMode={JobPickerDisplayModes.OnlinePaymentReport}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.comment.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={CommentsListPage}
                selectedMenuItem={MenuItemName.Comments}
                jobPickerDisplayMode={JobPickerDisplayModes.Comments}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.survey.ownerList}
                showJobPicker={isInPortal({ builder: true })}
                component={OwnerSurveyListPage}
                selectedMenuItem={MenuItemName.Surveys}
                jobPickerDisplayMode={JobPickerDisplayModes.None}
            />
            <ListPageLayout
                path={routes.survey.list}
                showJobPicker={isInPortal({ builder: true })}
                component={SurveyListPage}
                selectedMenuItem={MenuItemName.Surveys}
                jobPickerDisplayMode={JobPickerDisplayModes.Survey}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.chat.list}
                showJobPicker={false}
                component={ChatListPage}
                selectedMenuItem={MenuItemName.Chat}
                keepQueryParams
                hideOwnerHeaderInfo
            />
            {/* Sub-selections must come BEFORE selection.list to match /Subs/Selections/* first */}
            <ListPageLayout
                path={routes.selection.subSelectionsList}
                showJobPicker={isInPortal({ subs: true })}
                component={SubSelectionsListPage}
                selectedMenuItem={MenuItemName.Selections}
                jobPickerDisplayMode={JobPickerDisplayModes.Selections}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.selection.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={SelectionsListPage}
                selectedMenuItem={MenuItemName.Selections}
                jobPickerDisplayMode={JobPickerDisplayModes.Selections}
                jobPickerSelectMode={JobPickerSelectModes.Single}
            />
            <ListPageLayout
                path={routes.timeClock.list}
                showJobPicker={true}
                component={TimeClockPage}
                selectedMenuItem={MenuItemName.TimeClock}
                jobPickerDisplayMode={JobPickerDisplayModes.TimeClock}
                includeGeneralJob={
                    appContext?.useGlobalJobForTimeClock &&
                    !builderInfo?.configFlags?.hideTimeClockGeneralJob
                }
                legacyIncludeGlobalJob={
                    !appContext?.useGlobalJobForTimeClock &&
                    !builderInfo?.configFlags?.hideTimeClockGeneralJob
                }
            />
            {builderInfo?.flags?.billsFullPageConversion ? (
                <ResearchPreviewLayout
                    path={routes.bill.list}
                    showJobPicker={isInPortal({ builder: true, subs: true })}
                    component={isInfoArchOptedIn ? BillsInboxRoute : BillsListPage}
                    selectedMenuItem={MenuItemName.Bills}
                    jobPickerDisplayMode={JobPickerDisplayModes.Bills}
                    jobPickerSelectMode={JobPickerSelectModes.Multiple}
                    includeGeneralJob={isDraftBillsEffective}
                    generalJobFallbackOnly={true}
                    defaultToAllJobs={true}
                />
            ) : (
                <ListPageLayout
                    path={routes.bill.list}
                    showJobPicker={isInPortal({ builder: true, subs: true })}
                    component={BillsListPage}
                    selectedMenuItem={MenuItemName.Bills}
                    jobPickerDisplayMode={JobPickerDisplayModes.Bills}
                    jobPickerSelectMode={JobPickerSelectModes.Multiple}
                    includeGeneralJob={isDraftBillsEffective}
                    generalJobFallbackOnly={true}
                    defaultToAllJobs={true}
                />
            )}
            <ListPageLayout
                path={routes.bill.approvalQueue}
                showJobPicker={false}
                component={BillApprovalQueuePage}
                selectedMenuItem={MenuItemName.Bills}
                jobPickerDisplayMode={JobPickerDisplayModes.Bills}
            />
            <ListPageLayout
                path={routes.costCodeManagement.costCodes}
                showJobPicker={false}
                component={CostCodesPage}
                selectedMenuItem={MenuItemName.CostCodes}
            />
            {/**
             * Webview pages
             */}
            <WebviewPageLayout
                path={routes.plans.viewerWebview}
                component={PlanPageDetailsWebviewContainer}
                title="Plan Viewer"
            />
            <WebviewPageLayout
                path={routes.plans.revisionsReviewWebview}
                component={PlanRevisionsReviewWebviewContainer}
                title="Review revisions"
            />
            <WebviewPageLayout
                path={routes.plans.webview}
                component={PlansListWebviewContainer}
                title="Plans"
            />
            <WebviewPageLayout
                path={routes.jobCostingBudget.webview}
                component={JobCostingBudgetWebviewContainer}
                title="Job Costing Budget"
            />
            <WebviewPageLayout
                path={routes.chat.webview}
                component={WebviewChatListPage}
                title="Chat"
            />
            <WebviewPageLayout
                path={routes.task.webviewDetails}
                component={WebviewTaskDetailPage}
                title="Task Detail"
            />
            <WebviewPageLayout
                path={routes.task.webviewList}
                component={WebviewTaskListPage}
                title="Tasks"
            />
            <WebviewPageLayout
                path={routes.notifications.webviewHistory}
                component={NotificationListWebviewContainer}
                title="Notification History"
            />
            <WebviewPageLayout
                path={routes.bid.subsWebview}
                component={BidSubsListWebviewContainer}
                title="Bids"
            />
            <WebviewPageLayout
                path={routes.bid.webviewDetails}
                component={WebviewBidDetailsPage}
                title="Bid"
            />
            <WebviewPageLayout
                path={routes.bid.subsWebviewDetails}
                component={WebviewBidSubsDetailsPage}
                title="Bid"
            />
            <WebviewPageLayout
                path={routes.bidPackage.webview}
                component={BidPackageListWebviewContainer}
                title="Bid Packages"
            />
            <WebviewPageLayout
                path={routes.invoicePayments.webview}
                component={InvoicePaymentListWebviewContainer}
                title="Invoice Payments"
            />
            <WebviewPageLayout
                path={routes.bidPackage.detailsWebview}
                component={BidPackageWebviewContainer}
                title="Bid Package"
            />
            <WebviewPageLayout
                path={routes.bill.webview}
                component={BillsListWebviewContainer}
                title="Bills"
            />
            <WebviewPageLayout
                path={routes.submittal.webviewPrint}
                component={SubmittalPrintWebviewContainer}
                title="Submittal Print"
            />
            <WebviewPageLayout
                path={routes.submittal.webviewDetails}
                component={SubmittalWebviewContainer}
                title="Submittal"
            />
            <WebviewPageLayout
                path={routes.submittal.webview}
                component={SubmittalListWebviewContainer}
                title="Submittals"
            />
            <WebviewPageLayout
                path={routes.internalUsers.webview}
                component={InternalUserListWebviewContainer}
                title="Internal Users"
            />
            <WebviewPageLayout
                path={routes.contact.webview}
                component={ContactListWebviewContainer}
                title="Client Contacts"
            />
            <WebviewPageLayout
                path={routes.sub.webview}
                component={SubListWebviewContainer}
                title="Subs/Vendors"
            />
            <WebviewPageLayout
                path={routes.bill.webviewDetails}
                component={BillWebviewContainer}
                title="Bill"
            />
            <WebviewPageLayout
                path={routes.purchaseOrder.webview}
                component={PurchaseOrderListWebviewContainer}
                title="Purchase Orders"
            />
            <WebviewPageLayout
                path={routes.purchaseOrder.webviewDetails}
                component={PurchaseOrderWebviewContainer}
                title="Purchase Order"
            />
            <WebviewPageLayout
                path={routes.selectionOptions.webviewList}
                component={SelectionOptionsListWebviewContainer}
                title="Selections"
            />
            <WebviewPageLayout
                path={routes.selection.webviewList}
                component={SelectionListWebviewContainer}
                title="Selections"
            />
            <WebviewPageLayout
                path={routes.selection.webviewSubList}
                component={SubSelectionsListWebviewContainer}
                title="Selections"
            />
            <WebviewPageLayout
                path={routes.onlinePayments.paymentReportWebview}
                component={OnlinePaymentGroupedListWebviewContainer}
                title="Online Payment Report"
            />
            <WebviewPageLayout
                path={routes.clientUpdates.webviewPrint}
                component={ClientUpdatePrintWebviewContainer}
                title="Client Update"
            />
            <WebviewPageLayout
                path={routes.clientUpdates.webviewSettings}
                component={ClientUpdateSettingsWebviewContainer}
                title="Client Updates"
            />
            <WebviewPageLayout
                path={routes.clientUpdates.webviewDetails}
                component={ClientUpdateDetailWebviewContainer}
                title="Client Update"
            />
            <WebviewPageLayout
                path={routes.clientUpdates.webview}
                component={ClientUpdateListWebviewContainer}
                title="Client Updates"
            />
            <WebviewPageLayout
                path={routes.ownerProjectUpdates.webviewList}
                component={OwnerProjectUpdatesWebviewContainer}
                title="Your Updates"
            />
            <WebviewPageLayout
                path={routes.video.webview}
                component={VideoListWebviewContainer}
                title="Videos"
            />
            <WebviewPageLayout
                path={routes.rfi.webviewList}
                component={RfiListWebviewContainer}
                title="RFIs"
            />
            <WebviewPageLayout
                path={routes.deposits.webviewList}
                component={DepositsListWebviewContainer}
                title="Deposits"
            />
            <WebviewPageLayout
                path={routes.document.webview}
                component={DocumentListWebviewContainer}
                title="Documents"
            />
            <WebviewPageLayout
                path={routes.document.unsignedDocumentsWebview}
                component={UnsignedDocumentListWebviewContainer}
                title="Documents to Sign"
            />
            <WebviewPageLayout
                path={routes.deposits.printWebview}
                component={DepositPrintWebviewContainer}
                title="Deposit Print"
            />
            <WebviewPageLayout
                path={routes.receipts.webview}
                component={ReceiptsListWebviewContainer}
                title="Cost Inbox"
            />
            <WebviewPageLayout
                path={routes.warranty.webview}
                component={WarrantyListWebviewContainer}
                title="Warranties"
            />
            <WebviewPageLayout
                path={routes.warranty.webviewDetails}
                component={WarrantyWebviewContainer}
                title="Warranty"
            />
            <WebviewPageLayout
                path={routes.warranty.webviewDetailsPage}
                component={WarrantyWebviewContainer}
                title="Warranty"
            />
            <WebviewPageLayout
                path={routes.warranty.webviewAddPage}
                component={WebviewWarrantyAddPage}
                title="Add Warranty"
            />
            <WebviewPageLayout
                path={routes.warranty.webviewServicePage}
                component={WarrantyServiceWebviewContainer}
                title="Warranty service"
            />
            <WebviewPageLayout
                path={routes.photo.webview}
                component={PhotoListWebviewContainer}
                title="Photos"
            />
            <WebviewPageLayout
                path={routes.bill.webviewApprovalQueue}
                component={BillApprovalQueueWebviewContainer}
                title="Bills to approve"
            />
            <WebviewPageLayout
                path={routes.timeClock.webview}
                component={ShiftListWebviewContainer}
                title="Time Clock"
            />
            <WebviewPageLayout
                path={routes.timeClock.clockInClockOutWebview}
                component={ClockInClockOutWebviewContainer}
                title="Clock In/Clock Out"
            />
            <WebviewPageLayout
                path={routes.dailyLog.webviewList}
                component={WebviewDailyLogListPage}
                title="Daily Logs"
            />
            <WebviewPageLayout
                path={routes.dailyLog.webviewViewState}
                component={WebviewDailyLogViewPage}
                title="Daily Log"
            />
            <WebviewPageLayout
                path={routes.dailyLog.webviewDetails}
                component={WebviewDailyLogDetailsPage}
                title="Daily Log"
            />
            <WebviewPageLayout
                path={routes.dailyLog.webviewAddPage}
                component={WebviewDailyLogAddPage}
                title="Daily Log"
            />
            <WebviewPageLayout
                path={routes.specification.webviewWithSpec}
                component={SpecificationBookWebviewContainer}
                title="Specifications"
            />
            <WebviewPageLayout
                path={routes.specification.webview}
                component={SpecificationBookWebviewContainer}
                title="Specifications"
            />
            <WebviewPageLayout
                path={routes.receipts.receiptWebview}
                component={ReceiptWebviewContainer}
                title="Receipt"
            />
            <WebviewPageLayout
                path={routes.selectionOptions.webviewDetailsModal}
                component={SelectionOptionModalWebviewContainer}
                title="Selection Option"
            />
            <WebviewPageLayout
                path={routes.selectionOptions.webviewDetails}
                component={SelectionOptionWebviewContainer}
                title="Selection Option"
            />
            <WebviewPageLayout
                path={routes.selection.webviewDetails}
                component={SelectionWebviewContainer}
                title="Selection"
            />
            <WebviewPageLayout
                path={routes.selectionChoice.webviewDetails}
                component={SelectionChoiceWebviewContainer}
                title="Selection Choice"
            />
            <WebviewPageLayout
                path={routes.allowance.webview}
                component={AllowanceWebviewContainer}
                title="Allowance"
            />
            <WebviewPageLayout
                path={routes.media.mediaPropertiesWebview}
                component={MediaPropertiesWebviewContainer}
                title="Media Properties"
            />
            <WebviewPageLayout
                path={routes.lead.webview}
                component={LeadWebviewContainer}
                title="Lead Opportunity"
            />
            <WebviewPageLayout
                path={routes.creditMemos.printWebview}
                component={CreditMemoPrintWebviewContainer}
                title="Credit Memo Print"
            />
            <WebviewPageLayout
                path={routes.creditMemos.webview}
                component={CreditMemoWebviewContainer}
                title="Credit memo"
            />
            <WebviewPageLayout
                path={routes.creditMemos.listWebview}
                component={CreditMemoListWebviewContainer}
                title="Credit Memos"
            />
            <WebviewPageLayout
                path={routes.rfi.webviewDetailsLinked}
                component={RfiWebviewContainer}
                title="RFI"
            />
            <WebviewPageLayout
                path={routes.rfi.webviewDetails}
                component={RfiWebviewContainer}
                title="RFI"
            />
            <WebviewPageLayout
                path={routes.rfi.relatedWebview}
                component={RelatedRfiWebviewContainer}
                title="Related RFIs"
            />
            <WebviewPageLayout
                path={routes.deposits.externalWebview}
                component={DepositExternalWebviewContainer}
                title="Deposit"
            />
            <WebviewPageLayout
                path={routes.deposits.webview}
                component={DepositWebviewContainer}
                title="Deposit"
            />
            <WebviewPageLayout
                path={routes.lead.webviewContactFormPreview}
                component={LeadContactFormPreviewWebviewContainer}
                title="Contact form preview"
            />
            <WebviewPageLayout
                path={routes.lead.webviewList}
                component={LeadListWebviewContainer}
                title="Lead Opportunities"
            />
            <WebviewPageLayout
                path={routes.comment.webview}
                component={WebviewCommentThreadPage}
                title="Comments"
            />
            <WebviewPageLayout
                path={routes.comment.webviewList}
                component={CommentListWebviewContainer}
                title="Comments"
            />
            <WebviewPageLayout
                path={routes.ownerInvoice.printWebview}
                component={WebviewOwnerInvoicePrintPage}
                title="Owner Invoice Print"
            />
            <WebviewPageLayout
                path={routes.ownerInvoice.webview}
                component={OwnerInvoiceWebviewContainer}
                title="Owner Invoice"
            />
            <WebviewPageLayout
                path={routes.ownerInvoice.listWebview}
                component={OwnerInvoiceListWebviewContainer}
                title="Invoices"
            />
            <WebviewPageLayout
                path={routes.estimate.fullpageProposalWebview}
                component={WebviewProposalDetailsPage}
                title="Proposal"
            />
            <WebviewPageLayout
                path={routes.estimate.buildProposalWebView}
                component={WebviewBuildProposalPage}
                title="Build Proposal"
            />
            <WebviewPageLayout
                path={routes.proposal.proposalDashboardWebview}
                component={WebviewProposalDashboardPage}
                title="Proposals"
            />
            <WebviewPageLayout
                path={routes.estimate.ownerProposalWebview}
                component={OwnerProposalWebviewContainer}
                title="Owner Proposal"
            />
            <WebviewPageLayout
                path={routes.estimate.estimateWebview}
                component={WebviewEstimatePage}
                title="Estimates"
            />
            <WebviewPageLayout
                path={routes.proposal.pastWorksheetWebview}
                component={WebviewPastWorksheetPage}
                title="Proposal Worksheet"
            />
            <WebviewPageLayout
                path={routes.changeOrder.changeOrderDetailsPageWebview}
                component={WebviewChangeOrderDetailsPage}
                title="Change Order"
            />
            <WebviewPageLayout
                path={routes.changeOrder.webview}
                component={ChangeOrderListWebviewContainer}
                title="Change Orders"
            />
            <WebviewPageLayout
                path={routes.schedule.detailsWebview}
                component={WebviewScheduleDetailsPage}
                title="Schedule"
            />
            <WebviewPageLayout
                path={routes.job.jobPriceSummaryPrintWebView}
                component={WebviewJobPriceSummaryPrintPage}
                title="Job Price Summary"
            />
            <WebviewPageLayout
                path={routes.job.jobFinancialsWebview}
                component={JobFinancialsWebviewContainer}
                title="Job Price Summary"
            />
            <WebviewPageLayout
                path={routes.signature.detailsWebView}
                component={WebviewSignatureFlowPage}
                title="Sign Document"
            />
            <WebviewPageLayout
                path={routes.signatureRequest.webviewSign}
                component={WebviewSignDocumentPage}
                title="Sign document"
            />
            <WebviewPageLayout
                path={routes.signatureRequest.webviewSignatureRequest}
                component={WebviewSignatureRequestPage}
                title="Signature request"
            />
            <WebviewPageLayout
                path={routes.timeClock.shiftDetailsWebView}
                component={WebviewShiftDetailsPage}
                title="Shift"
            />
            <WebviewPageLayout
                path={routes.reporting.DailyLogCreationByJobReport.webview}
                component={DailyLogCreationByJobReportWebviewContainer}
                title="Daily Log Creation by Job"
            />
            <WebviewPageLayout
                path={routes.reporting.ProfitabilityReport.webview}
                component={ProfitabilityReportWebviewContainer}
                title="Profitability report"
            />
            <WebviewPageLayout
                path={routes.reporting.InvoicingReport.webview}
                component={InvoicingReportWebviewContainer}
                title="Invoicing report"
            />
            <WebviewPageLayout
                path={routes.reporting.WIPReport.webview}
                component={WorkInProgressReportWebviewContainer}
                title="Work in progress report"
            />
            <WebviewPageLayout
                path={routes.reporting.BudgetedVsProjected.webview}
                component={BudgetedVsProjectedReportWebviewContainer}
                title="Budgeted vs. projected report"
            />
            <WebviewPageLayout
                path={routes.reporting.HoursByJobReport.webview}
                component={HoursWorkedByJobWebviewContainer}
                title="Hours worked by job"
            />
            <WebviewPageLayout
                path={routes.reporting.HoursByEmployeeReport.webview}
                component={HoursWorkedByEmployeeWebviewContainer}
                title="Hours worked by employee"
            />
            <WebviewPageLayout
                path={routes.reporting.DailyLogsByUserReport.webview}
                component={DailyLogsByUserReportWebviewContainer}
                title="Daily log count by user"
            />
            <WebviewPageLayout
                path={routes.reporting.LaborActualsVsBudgetedReport.webview}
                component={LaborActualsVsBudgetedReportWebviewContainer}
                title="Labor actuals vs. budgeted report"
            />
            <WebviewPageLayout
                path={routes.reporting.CashflowReport.webview}
                component={CashflowReportWebviewContainer}
                title="Cashflow report"
            />
            <WebviewPageLayout
                path={routes.reporting.LeadActivitiesBySalespersonReport.webview}
                component={LeadActivitiesBySalespersonReportWebviewContainer}
                title="Lead activities by salesperson report"
            />
            <WebviewPageLayout
                path={routes.reporting.LeadStatusBySourceReport.webview}
                component={LeadStatusBySourceReportWebviewContainer}
                title="Lead status by source report"
            />
            <WebviewPageLayout
                path={routes.reporting.LeadCountBySalespersonReport.webview}
                component={LeadCountBySalespersonReportWebviewContainer}
                title="Lead count by salesperson report"
            />
            <WebviewPageLayout
                path={routes.reporting.BaselineVsActualDurationReport.webview}
                component={BaselineVsActualDurationReportWebviewContainer}
                title="Baseline vs. actual duration report"
            />
            <WebviewPageLayout
                path={routes.reporting.ChangeOrderProfitReport.webview}
                component={ChangeOrderProfitReportWebviewContainer}
                title="Change order profit report"
            />
            <WebviewPageLayout
                path={routes.reporting.SchedulePercentCompleteByJob.webview}
                component={SchedulePercentCompleteByJobReportWebviewContainer}
                title="Schedule percent complete by job report"
            />
            <WebviewPageLayout
                path={routes.reporting.reportList.webview}
                component={ReportListWebviewContainer}
                title="Reports"
            />
            <WebviewPageLayout
                path={routes.job.webviewList}
                component={JobsListWebviewContainer}
                title="Jobs"
            />
            <WebviewPageLayout
                path={routes.job.webviewDetails}
                component={JobPageWebviewPage}
                title="Jobsite"
            />
            <WebviewPageLayout
                path={routes.onlinePayments.paymentsOverviewWebview}
                component={PaymentsOverviewWebviewContainer}
                title="Buildertrend payments"
            />
            <WebviewPageLayout
                path={routes.onlinePayments.paymentsHostedOnboardingWebview}
                component={PaymentsHostedOnboardingWizardWebviewContainer}
                title="Buildertrend payments"
            />
            <WebviewPageLayout
                path={routes.builderSetup.webview}
                component={SetupSummaryWebviewContainer}
                title="Setup"
            />
            <WebviewPageLayout
                path={routes.leadActivity.webview}
                component={LeadActivityWebviewContainer}
                title="Lead Activity"
            />
            <WebviewPageLayout
                path={routes.notifications.webview}
                component={NotificationsWebviewContainer}
                title="Notifications"
            />
            <WebviewPageLayout
                path={routes.changeOrder.ownerRequestWebview}
                component={ChangeOrderOwnerRequestWebviewContainer}
                title="Change Order Request"
            />
            <WebviewPageLayout
                path={routes.owner.setupWebview}
                component={OwnerSetupWebviewContainer}
                title="Owner Setup"
            />
            <WebviewPageLayout
                path={routes.sub.setupWebview}
                component={SubSetupWebviewContainer}
                title="Sub/Vendor Setup"
            />
            <WebviewPageLayout
                path={routes.onlinePayments.paymentReportSubsWebview}
                component={OnlinePaymentListWebviewContainer}
                title="Online Payment Report"
            />
            {/**
             * SPA Schedules list page route/component
             */}
            <ListPageLayout
                path={routes.schedule.baselineList}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={ScheduleBaselineListPage}
                selectedMenuItem={MenuItemName.Calendar}
                jobPickerDisplayMode={JobPickerDisplayModes.Calendar}
                jobPickerSelectMode={JobPickerSelectModes.Single}
            />
            <ListPageLayout
                path={routes.schedule.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={ScheduleListPage}
                selectedMenuItem={MenuItemName.Calendar}
                jobPickerDisplayMode={JobPickerDisplayModes.Calendar}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.changeOrder.changeOrderDetailsPage}
                component={ChangeOrderDetailsPage}
                showJobPicker={isInPortal({ builder: true, subs: true, owner: false })}
                hideOwnerHeaderInfo
                jobPickerDisplayMode={JobPickerDisplayModes.ChangeOrder}
                selectedMenuItem={MenuItemName.ChangeOrders}
                redirectOnJobChangeURL={"/app" + routes.changeOrder.getListLink()}
            />
            <ListPageLayout
                path={routes.changeOrder.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={ChangeOrderListPage}
                selectedMenuItem={MenuItemName.ChangeOrders}
                jobPickerDisplayMode={JobPickerDisplayModes.ChangeOrder}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.deposits.list}
                showJobPicker={isInPortal({ builder: true })}
                component={DepositListPage}
                selectedMenuItem={MenuItemName.Deposits}
                jobPickerDisplayMode={JobPickerDisplayModes.OwnerPayments}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.rfi.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={RFIsListPage}
                selectedMenuItem={MenuItemName.RFIs}
                jobPickerDisplayMode={JobPickerDisplayModes.Rfis}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.notifications.legacyList}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={NotificationHistoryListPage}
                selectedMenuItem={MenuItemName.NotificationHistory}
                jobPickerDisplayMode={JobPickerDisplayModes.NotificationHistory}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            <ListPageLayout
                path={routes.jobCostingBudget.path}
                showJobPicker={isInPortal({ builder: true })}
                component={(listProps, route) => JobCostingBudgetPage(listProps, route)}
                selectedMenuItem={MenuItemName.JobCostingBudget}
                jobPickerDisplayMode={JobPickerDisplayModes.Budget}
                jobPickerSelectMode={
                    builderInfo?.hasJobGroups
                        ? JobPickerSelectModes.Multiple
                        : JobPickerSelectModes.Single
                }
                allJobsText="Job Group view"
            />
            {/* todo switch to ListPageLayout in the SPA project */}
            <DetailPageLayout
                path={routes.errorPage.legacyWebformsServerError}
                component={LegacyWebformsServerErrorPage}
            />
            {/* Master Pages */}
            <DetailPageLayout
                path={routes.multiFactorAuth.multiFactorAuth}
                component={MultiFactorAuthPage}
            />
            {/* Message page */}
            <DetailPageLayout path={routes.notifications.details} component={NotificationPage} />
            <DetailPageLayout path={routes.messages.compose} component={MessageComposePage} />
            <DetailPageLayout
                path={routes.messages.detailsExternal}
                component={MessageDetailsExternalPage}
            />
            {/* RFI pages */}
            <DetailPageLayout path={routes.rfi.related} component={RelatedRfiPage} />
            <DetailPageLayout path={routes.rfi.details} component={RFIPage} />
            <PrintPageLayout path={routes.rfi.print} component={RFIPrintPage} />
            {/* Online Payment pages*/}
            <ListPageLayout
                path={routes.onlinePayments.settings}
                showJobPicker={false}
                component={OnlinePaymentSettingsFullPage}
                selectedMenuItem={MenuItemName.None}
            />
            <ListPageLayout
                path={routes.onlinePayments.paymentsHostedOnboarding}
                showJobPicker={false}
                component={PaymentsHostedOnboardingPage}
                selectedMenuItem={MenuItemName.None}
                hideNav={builderInfo?.redirectToSetupExperience ?? false}
            />
            <ListPageLayout
                path={routes.onlinePayments.financialDocumentCollection}
                showJobPicker={false}
                component={FinancialDocumentCollectionPage}
                selectedMenuItem={MenuItemName.None}
                hideNav={builderInfo?.redirectToSetupExperience ?? false}
            />
            <DetailPageLayout
                path={routes.onlinePayments.bankValidationRedirect}
                component={BankValidationRedirectLandingPage}
            />
            <BTAdminLayout
                path={routes.onlinePayments.adminPrescreening}
                component={AdminPaymentsPrescreeningPage}
            />
            {/* Pretty sure this is never hit */}
            <DetailPageLayout
                path={routes.onlinePayments.settings}
                component={OnlinePaymentSettingsPage}
            />
            <DetailPageLayout
                path={routes.onlinePayments.paymentServicePaymentMethod.external}
                component={MicrodepositVerificationExternal}
            />
            <DetailPageLayout
                path={routes.onlinePayments.paymentServicePaymentMethod.externalWithShareToken}
                component={MicrodepositVerificationExternal}
            />
            <DetailPageLayout
                path={routes.ownerInvoice.payOnlineMobile}
                component={OwnerInvoicePayOnlineMobileWrapper}
            />
            <DetailPageLayout
                path={routes.leadProposals.payOnlineMobile}
                component={LeadProposalPayOnlineMobileWrapper}
            />
            <BTAdminLayout
                path={routes.onlinePayments.stuckPaymentUtility}
                component={StuckPaymentUtilityPage}
            />
            <BTAdminLayout
                path={routes.builderRiskInsurance.briUpdateUtility}
                component={BRIUpdateUtilityPage}
            />
            <BTAdminLayout
                path={routes.notifications.adminNotificationHistory}
                component={AdminNotificationHistoryListPage}
            />
            {/* Warranty pages */}
            <PrintPageLayout path={routes.warranty.print} component={WarrantyPrintPage} />
            <ListPageLayout
                selectedMenuItem={MenuItemName.Warranty}
                path={routes.warranty.addPage}
                component={WarrantyAddPage}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
                jobPickerDisplayMode={JobPickerDisplayModes.Warranty}
            />
            <ListPageLayout
                selectedMenuItem={MenuItemName.Warranty}
                path={routes.warranty.servicePage}
                component={WarrantyServicePage}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
                jobPickerDisplayMode={JobPickerDisplayModes.Warranty}
            />
            <ListPageLayout
                selectedMenuItem={MenuItemName.Warranty}
                path={routes.warranty.details}
                component={WarrantyPage}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                // Multiple (not Single) preserves "All Jobs" when arriving from /Warranties.
                // With Single, ListPageLayout's Multi→Single reconcile would coerce AllJobs to
                // NoJobs and persist it, leaving the user stuck on "No job selected" after
                // closing the page. Picker changes mid-page are handled separately by
                // useRedirectOnJobPickerSwitch.
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
                jobPickerDisplayMode={JobPickerDisplayModes.Warranty}
                // Preserve ?commentId so a comment notification / list comment indicator
                // deep-link reaches the page and auto-opens the comment drawer; without this
                // ListPageLayout.componentDidMount clears the query on mount (ADO 277242).
                keepQueryParams
            />
            {/* Selection pages */}
            <PrintPageLayout path={routes.selection.print} component={SelectionPrintPage} />
            <DetailPageLayout path={routes.selection.details} component={SelectionPage} />
            {/* Selection Choice Pages */}
            <DetailPageLayout
                path={routes.selectionChoice.copyChoicesFromSelection}
                component={CopyChoiceFromSelectionPage}
            />
            <DetailPageLayout
                path={routes.selectionChoice.details}
                component={SelectionChoicePage}
            />
            <DetailPageLayout
                path={routes.selectionChoice.dependentSelections}
                component={DependentSelectionsPage}
            />
            {/* Selection Option Pages */}
            <ListPageLayout
                path={routes.selectionOptions.details}
                selectedMenuItem={MenuItemName.Selections}
                component={SelectionOptionPage}
                showJobPicker={isInPortal({ builder: true })}
                jobPickerDisplayMode={JobPickerDisplayModes.Selections}
                jobPickerSelectMode={JobPickerSelectModes.Single}
                redirectOnJobChangeURL={"/app" + routes.selectionOptions.list}
            />
            {/* Allowance pages */}
            <PrintPageLayout path={routes.allowance.print} component={AllowancePrintPage} />
            <DetailPageLayout path={routes.allowance.details} component={AllowancePage} />
            <DetailPageLayout
                path={routes.mobile.getMobileDownload()}
                component={MobileDownloadPage}
            />
            <DetailPageLayout
                path={routes.mobile.getEmailLinkRedirect()}
                component={EmailLinkRedirect}
            />
            {/* Survey pages */}
            <PrintPageLayout path={routes.survey.print} component={SurveyPrintPage} />
            <PrintPageLayout
                path={routes.survey.questionReportPrint}
                component={QuestionReportPrintPage}
            />
            <DetailPageLayout path={routes.survey.questionReport} component={QuestionReportPage} />
            <DetailPageLayout path={routes.survey.settings} component={SurveySettingsPage} />
            <DetailPageLayout path={routes.survey.details} component={SurveyPage} />
            <DetailPageLayout path={routes.survey.ownerSurvey} component={OwnerSurveyPage} />
            {/* ToDo pages */}
            <PrintPageLayout path={routes.toDo.print} component={ToDoPrintPage} />
            <DetailPageLayout path={routes.toDo.details} component={ToDoPage} />
            {/* Lead pages */}
            <PrintPageLayout path={routes.lead.print} component={LeadPrintPage} />
            <DetailPageLayout path={routes.leadActivity.details} component={LeadActivityPage} />
            <DetailPageLayout path={routes.lead.details} component={LeadPage} />
            <DetailPageLayout path={routes.lead.emailWizard} component={LeadEmailWizardPage} />
            {/* Marketplace Integrations pages */}
            <DetailPageLayout
                path={routes.marketplace.integrationSettings}
                component={IntegrationsSettingsPage}
            />
            <DetailPageLayout path={routes.marketplace.reconsent} component={ReconsentPage} />
            {/* Auth0 Pages */}
            <DetailPageLayout path={routes.auth0.redirect} component={Auth0RedirectPage} />
            {/* Bid pages */}
            <PrintPageLayout path={routes.bid.print} component={BidPrintPage} />
            <DetailPageLayout path={routes.bid.external} component={BidExternalPage} />
            <DetailPageLayout path={routes.bid.details} component={BidRequestPage} />
            {/* Job pages*/}
            <PrintPageLayout path={routes.job.print} component={JobPricePrintPage} />
            <PrintPageLayout path={routes.job.jobPrint} component={JobPrintPage} />
            <DetailPageLayout path={routes.job.jobFromTemplate} component={JobFromTemplatePage} />
            {/* Cost Catalog Pages */}
            <DetailPageLayout path={routes.costCatalog.settings} component={CostCatalogSetupPage} />
            <DetailPageLayout
                path={routes.costCatalog.costGroupDetailsPath}
                component={CostGroupPage}
            />
            <DetailPageLayout
                path={routes.costCatalog.costCodePath}
                component={CostCodeItemUsagePage}
            />
            <ListPageLayout
                path={routes.ownerBudget.list}
                component={OwnerBudgetPage}
                showJobPicker={false}
                selectedMenuItem={MenuItemName.BillsPurchaseOrders}
            />
            <ListPageLayout
                path={routes.budget.legacy}
                component={BudgetLegacyPage}
                showJobPicker={true}
                jobPickerDisplayMode={JobPickerDisplayModes.Budget}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
                selectedMenuItem={MenuItemName.Budget}
            />
            <DetailPageLayout
                path={routes.costCatalog.costItemDetailsPath}
                component={CostItemPage}
            />
            {/* Cost Code pages*/}
            <DetailPageLayout
                path={routes.costCode.manageAccounting}
                component={CostCodesExportToAccountingPage}
            />
            <DetailPageLayout
                path={routes.costCode.legacyAccountingImport}
                component={CostCodesImportFromAccountingPage}
            />
            {/* Change Order pages */}
            <PrintPageLayout path={routes.changeOrder.print} component={ChangeOrderPrintPage} />
            <DetailPageLayout
                path={routes.changeOrder.changeOrderPaymentDetails}
                component={ChangeOrderPaymentPage}
            />
            <DetailPageLayout
                path={routes.changeOrder.ownerRequest}
                component={ChangeOrderOwnerRequestPage}
            />
            <DetailPageLayout
                path={routes.changeOrder.changeOrderApprovalSuccess}
                component={ChangeOrderApprovalSuccessPage}
            />
            <DetailPageLayout
                path={routes.changeOrder.changeOrderDetails}
                component={ChangeOrderPage}
            />
            {/* Estimate and Proposal Pages */}
            <DetailPageLayout
                path={routes.estimate.proposalPdfGenerationPage}
                component={JobProposalPdfGenerationPage}
            />
            <PrintPageLayout
                minWidth={800}
                path={routes.estimate.proposalPrintExternal}
                component={ProposalPrintPage}
            />
            <DetailPageLayout
                path={routes.estimate.externalProposalPage}
                component={ExternalProposalPage}
            />
            <DetailPageLayout
                path={routes.leadProposals.checkoutBuilder}
                component={LeadProposalsCheckoutPage}
            />
            <DetailPageLayout
                path={routes.leadProposals.checkoutExternal}
                component={LeadProposalExternalCheckoutPage}
            />
            <DetailPageLayout
                path={routes.leadProposals.externalDetails}
                component={LeadProposalExternalPage}
            />
            <DetailPageLayout
                path={routes.leadProposals.mobilePreviewLegacy}
                component={MobileLeadProposalPreviewPage}
            />
            <DetailPageLayout
                path={routes.leadProposals.mobilePreview}
                component={MobileLeadProposalPreviewPage}
            />
            <DetailPageLayout
                path={routes.leadProposals.proposalPdfGenerationPage}
                component={LeadProposalPdfGenerationPage}
            />
            <DetailPageLayout
                path={routes.leadProposals.templateDetails}
                component={LeadProposalTemplatePage}
            />
            <PrintPageLayout
                minWidth={800}
                path={routes.leadProposals.leadProposalPrint}
                component={LeadProposalPrintPage}
            />
            <PrintPageLayout
                minWidth={800}
                path={routes.leadProposals.leadProposalPrintExternal}
                component={LeadProposalPrintPage}
            />
            {/* Bid Package pages */}
            <PrintPageLayout path={routes.bidPackage.print} component={BidPackagePrintPage} />
            <DetailPageLayout path={routes.bidPackage.details} component={BidPackagePage} />
            {/* Purchase Order Pages */}
            <PrintPageLayout path={routes.purchaseOrder.print} component={PurchaseOrderPrintPage} />
            <PrintPageLayout
                enforceContentWidth
                maxWidth={700}
                path={routes.purchaseOrder.printExternal}
                component={PurchaseOrderPrintPage}
            />
            <DetailPageLayout path={routes.purchaseOrder.details} component={PurchaseOrderPage} />
            <DetailPageLayout
                path={routes.purchaseOrder.approvedPODetails}
                element={PurchaseOrderPage}
            />
            <DetailPageLayout
                path={routes.purchaseOrder.external}
                component={PurchaseOrderExternalPage}
            />
            <ListPageLayout
                path={routes.purchaseOrder.list}
                showJobPicker={isInPortal({ builder: true, subs: true })}
                component={PurchaseOrderListPage}
                selectedMenuItem={MenuItemName.PurchaseOrders}
                jobPickerDisplayMode={JobPickerDisplayModes.PurchaseOrders}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
            />
            {/* Schedule of Values (AiaPurchaseOrder feature, builder portal only) */}
            <ListPageLayout
                path={routes.scheduleOfValues.list}
                exact
                showJobPicker
                component={ScheduleOfValuesListPage}
                selectedMenuItem={MenuItemName.ScheduleValues}
                jobPickerDisplayMode={JobPickerDisplayModes.PurchaseOrders}
                jobPickerSelectMode={JobPickerSelectModes.Multiple}
                defaultToAllJobs
            />
            <ListPageLayout
                path={routes.scheduleOfValues.details}
                showJobPicker
                component={ScheduleOfValuesDetailPage}
                selectedMenuItem={MenuItemName.ScheduleValues}
                jobPickerDisplayMode={JobPickerDisplayModes.PurchaseOrders}
            />
            {/* Bill Pages */}
            <PrintPageLayout path={routes.bill.print} component={BillPrintPage} />
            <ListPageLayout
                selectedMenuItem={MenuItemName.Bills}
                path={routes.bill.addPage}
                component={BillAddPage}
                hideOwnerHeaderInfo
                showJobPicker
                jobPickerSelectMode={JobPickerSelectModes.Single}
                jobPickerDisplayMode={JobPickerDisplayModes.Custom}
            />
            <ListPageLayout
                selectedMenuItem={MenuItemName.Bills}
                path={routes.bill.details}
                component={BillPage}
                hideOwnerHeaderInfo
                showJobPicker={isInPortal({ builder: true, subs: true })}
                jobPickerDisplayMode={JobPickerDisplayModes.Bills}
                jobPickerSelectMode={JobPickerSelectModes.Single}
            />
            <DetailPageLayout
                path={routes.bill.lienWaiverReview}
                component={LienWaiverReviewPage}
            />
            <DetailPageLayout
                path={routes.bill.lienWaiverReviewExternal}
                component={LienWaiverReviewPageExternal}
            />
            <DetailPageLayout
                path={routes.outboundPayments.external}
                component={OutboundPaymentsExternalPage}
            />
            <DetailPageLayout
                path={routes.outboundPayments.setup}
                component={OutboundAccountSetupPage}
            />
            {/* Owner Invoice pages */}
            <DetailPageLayout
                path={routes.ownerInvoice.checkoutOwner}
                component={OwnerInvoiceCheckoutPage}
            />
            <DetailPageLayout
                path={routes.ownerInvoice.checkoutBuilder}
                component={OwnerInvoiceCheckoutPage}
            />
            <DetailPageLayout
                path={routes.ownerInvoice.checkoutExternal}
                component={OwnerInvoiceCheckoutExternalPage}
            />
            {/* Invoice pages */}
            <DetailPageLayout path={routes.ownerInvoice.details} component={OwnerInvoicePage} />
            <DetailPageLayout
                path={routes.ownerInvoice.externalDetails}
                component={SharedExternalOwnerInvoicePage}
            />
            <DetailPageLayout
                path={routes.deposits.checkoutExternal}
                component={ExternalDepositCheckoutPage}
            />
            <DetailPageLayout
                path={routes.deposits.checkoutOwner}
                component={DepositCheckoutPage}
            />
            <DetailPageLayout
                path={routes.deposits.checkoutBuilder}
                component={DepositCheckoutPage}
            />
            <DetailPageLayout path={routes.deposits.external} component={ExternalDepositPage} />
            <PrintPageLayout path={routes.deposits.print} component={DepositPrintPage} />
            <PrintPageLayout
                path={routes.deposits.externalPrintShareToken}
                component={ExternalDepositPrintPage}
            />
            <PrintPageLayout
                path={routes.deposits.externalDepositPrintLink}
                component={DepositPrintPage}
            />
            <PrintPageLayout path={routes.ownerInvoice.print} component={OwnerInvoicePrintPage} />
            <PrintPageLayout
                path={routes.ownerInvoice.externalPrint}
                component={ExternalOwnerInvoicePrintPage}
            />
            <PrintPageLayout
                path={routes.ownerInvoice.externalPrintShareToken}
                component={ExternalOwnerInvoicePrintPage}
            />
            <PrintPageLayout
                path={routes.ownerInvoice.pdfGeneration}
                element={<OwnerInvoicePdfGenerationPage />}
            />
            <DetailPageLayout
                path={routes.onlinePayments.OnlinePaymentCodeEntry}
                component={OnlinePaymentExternalPage}
            />
            <DetailPageLayout
                path={routes.onlinePayments.OnlineInvoiceCodeRefEntry}
                component={OnlineInvoiceExternalPage}
            />
            <DetailPageLayout
                path={routes.onlinePayments.OnlineInvoiceCodeEntry}
                component={OnlineInvoiceExternalPage}
            />
            <DetailPageLayout
                path={routes.onlinePayments.OutboundPaymentCodeEntry}
                element={<OutboundPaymentShortLink />}
                exact
            />
            {/* Daily Log pages */}
            <PrintPageLayout path={routes.dailyLog.print} component={DailyLogPrintPage} />
            <PrintPageLayout path={routes.clientUpdates.print} component={ClientUpdatePrintPage} />
            <ListPageLayout
                selectedMenuItem={MenuItemName.DailyLogs}
                path={routes.dailyLog.addPage}
                component={DailyLogAddPage}
                hideOwnerHeaderInfo
                showJobPicker
                jobPickerDisplayMode={JobPickerDisplayModes.DailyLogs}
                redirectOnJobChangeURL={"/app" + routes.dailyLog.getListLink()}
            />
            <ListPageLayout
                selectedMenuItem={MenuItemName.DailyLogs}
                path={routes.dailyLog.details}
                component={DailyLogEditPage}
                hideOwnerHeaderInfo
                showJobPicker
                jobPickerDisplayMode={JobPickerDisplayModes.DailyLogs}
                redirectOnJobChangeURL={"/app" + routes.dailyLog.getListLink()}
            />
            <ListPageLayout
                path={dailyLogViewRoute.path}
                component={DailyLogViewPage}
                selectedMenuItem={MenuItemName.DailyLogs}
                hideOwnerHeaderInfo
                showJobPicker={isInPortal({ builder: true, subs: true })}
                jobPickerDisplayMode={JobPickerDisplayModes.DailyLogs}
                redirectOnJobChangeURL={"/app" + routes.dailyLog.getListLink()}
            />
            {/* Contact */}
            <DetailPageLayout path={routes.contact.details} component={ContactPage} />
            <PrintPageLayout path={routes.contact.print} component={ContactPrintPage} />
            {/* Lien Waiver */}
            <DetailPageLayout path={routes.lienWaiver.details} component={LienWaiverDetailPage} />
            <PrintPageLayout
                enforceContentWidth
                maxWidth={800}
                path={routes.lienWaiver.print}
                component={LienWaiverPrintPage}
            />
            <PrintPageLayout
                path={routes.lienWaiver.printExternal}
                component={LienWaiverPrintPage}
            />
            {/* Receipts pages */}
            <ListPageLayout
                path={routes.receipts.list}
                showJobPicker={false}
                component={ReceiptListPage}
                selectedMenuItem={MenuItemName.Receipts}
            />
            <DetailPageLayout path={routes.receipts.upload} component={ReceiptUpload} />
            <DetailPageLayout path={routes.receipts.details} component={ReceiptPage} />
            {/* Builder Risk Insurance pages */}
            <DetailPageLayout
                path={routes.builderRiskInsurance.settings}
                component={BuilderRiskInsuranceSettingsPage}
            />
            {/* Branding pages */}
            <DetailPageLayout path={routes.branding.settings} component={BrandingSettingsPage} />
            <DetailPageLayout
                path={routes.marketplace.getRedirectPath()}
                component={MarketplaceRedirectPage}
            />
            <ListPageLayout
                path={routes.onlinePayments.PaymentTos}
                showJobPicker={false}
                component={PaymentTosPage}
                selectedMenuItem={MenuItemName.None}
            />
            <BTAdminLayout
                path={routes.onlinePayments.adminMerchantPaymentList}
                component={AdminMerchantPaymentListPage}
            />
            {/* Comment pages */}
            <DetailPageLayout path={routes.conversation.details} component={ConversationPage} />
            {/* Sub/Vendor pages */}
            <DetailPageLayout path={routes.sub.paymentRequest} component={PaymentRequestPage} />
            <DetailPageLayout path={routes.sub.details} component={SubDetailPage} />
            {/* Document pages */}
            <DetailPageLayout path={routes.document.unread} component={UnreadDocumentPage} />
            {/* Signature pages */}
            <FullPageLayout
                path={routes.signature.details}
                element={<SignatureFlow />}
                showNav={false}
                title="Sign Document"
            />
            {/* PhotoPreview */}
            <DetailPageLayout path={routes.photo.preview} component={PhotoPreviewPage} />
            {/* Media pages */}
            <DetailPageLayout path={routes.media.copy} component={CopyMediaPage} />
            <DetailPageLayout path={routes.media.mediaProperties} component={MediaPropertiesPage} />
            <DetailPageLayout
                path={routes.media.folderProperties}
                component={MediaFolderPropertiesPage}
            />
            <DetailPageLayout path={routes.media.move} component={MediaMoveFolderPage} />
            <DetailPageLayout path={routes.media.replace} component={MediaReplacePage} />
            <DetailPageLayout
                path={routes.media.folderEmailInfo}
                component={MediaFolderEmailInfoPage}
            />
            <DetailPageLayout path={routes.media.shared} component={SharedMediaExternalPage} />
            {/* Schedule pages */}
            <DetailPageLayout path={routes.schedule.details} component={SchedulePage} />
            <DetailPageLayout
                path={routes.schedule.conflictReport}
                component={ScheduleConflictReportPage}
            />
            <DetailPageLayout
                path={routes.schedule.notificationPrompt}
                component={ScheduleNotificationPromptPage}
            />
            <DetailPageLayout
                path={routes.schedule.confirmationExternal}
                component={ScheduleConfirmationExternalPage}
            />
            {/* Time Clock pages */}
            <DetailPageLayout path={routes.timeClock.shiftDetails} component={ShiftPage} />
            <DetailPageLayout
                path={routes.timeClock.clockInClockOut}
                component={ClockInClockOutPage}
            />
            {/* Import Wizard Page */}
            <DetailPageLayout path={routes.importWizard.adminPath} component={ImportWizardPage} />
            {/* Templates Page */}
            <DetailPageLayout path={routes.template.templateDetails} component={TemplatePage} />
            <DetailPageLayout
                path={routes.template.templateFromTemplate}
                component={TemplateFromTemplatePage}
            />
            {/* Credit memo Pages */}
            <DetailPageLayout path={routes.creditMemos.details} component={CreditMemoPage} />
            {/* Internal Users */}
            <DetailPageLayout
                path={routes.internalUsers.quickAdd}
                component={InternalUserQuickAddPage}
            />
            <DetailPageLayout
                path={routes.internalUsers.internalUserRoleDetails}
                component={InternalUserRoleDetailsPage}
            />
            <DetailPageLayout
                path={routes.internalUsers.internalUserSettings}
                component={InternalUserSettingsPage}
            />
            <DetailPageLayout
                path={routes.internalUsers.internalUserDetails}
                component={InternalUserPage}
            />
            {/* Generic id-less self-service deep link (distributable in email blasts). Resolves
                the current user's id from context and renders their own details page with full
                SPA chrome (nav bar). `exact` so it never shadows /InternalUser/:id. */}
            <FullPageLayout
                path={routes.internalUsers.myUserDetails}
                exact
                element={<RouteMyUserDetails displayMode="page" />}
                title="My Preferences"
            />
            {/* Subscription Manager Page*/}
            <DetailPageLayout
                path={routes.subscriptionManager.settings}
                component={SubscriptionManagerSettingsPage}
            />
            {/* Accounting Pages */}
            <DetailPageLayout
                path={routes.accounting.quickBooksSyncSchedule}
                component={QuickBooksSyncSchedulePage}
            />
            <DetailPageLayout
                path={routes.accounting.linkEntityToAccounting.details}
                component={LinkEntityToAccountingPage}
            />
            <DetailPageLayout
                path={routes.accounting.oAuthRedirect}
                component={OAuthRedirectPage}
            />
            <DetailPageLayout
                path={routes.accounting.settings}
                component={AccountingSettingsPage}
            />
            {/* OAuth Pages */}
            <DetailPageLayout path={routes.oauth.authRedirect} component={AuthRedirectPage} />
            {/* Vendor Integration Pages */}
            <DetailPageLayout
                path={routes.vendor.homeDepotSettings}
                component={HomeDepotSettingsPage}
            />
            <DetailPageLayout path={routes.lowes.lowesSettings} element={LowesSettingsPage} />
            <DetailPageLayout
                path={routes.vendor.vendorStoreFinder}
                component={VendorStoreFinderPage}
            />
            {/* Builder Settings Page - Should be below all other settings pages */}
            <ListPageLayout
                path={routes.settings.settings}
                showJobPicker={false}
                component={SettingsListPage}
                selectedMenuItem={MenuItemName.None}
            />
            {/* Subscription */}
            <DetailPageLayout
                path={routes.subscription.adminList}
                component={AdminSubscriptionPage}
            />
            {/* Subscription Pricing Guardrails */}
            <BTAdminLayout
                path={routes.admin.subscriptionPricingLimits.list}
                component={SubscriptionPlanLimitsPage}
            />
            {/* Account Change Request*/}
            <DetailPageLayout
                path={routes.accountChange.builderAccountChangeRequest}
                component={BuilderAccountChangeRequestPage}
            />
            {/* Cancellation */}
            <DetailPageLayout
                path={routes.cancellation.cancelBuilder}
                component={AdminCancellationPage}
            />
            {/* Entity to Purchase Order (PO) Mapping */}
            <DetailPageLayout
                path={routes.poMapping.getChangeOrderPath()}
                component={ChangeOrderPoMappingPage}
            />
            <DetailPageLayout path={routes.poMapping.getBidPath()} component={BidPoMappingPage} />
            <DetailPageLayout
                path={routes.poMapping.getEstimatePath()}
                component={EstimatePoMappingPage}
            />
            <DetailPageLayout
                path={routes.poMapping.getSelectionPath()}
                component={SelectionPoMappingPage}
            />
            <DetailPageLayout
                path={routes.poMapping.getSelectionChoicePath()}
                component={SelectionChoicePoMappingPage}
            />
            {/* Search Sync Utility */}
            <BTAdminLayout path={routes.search.btAdminUtility} component={SearchUtilsPage} />
            {/* Accounting Webhook Testing */}
            <BTAdminLayout
                path={routes.admin.accountingWebhookTesting}
                component={AccountingWebhookTestingPage}
            />
            {/* Feature Flag */}
            <BTAdminLayout path={routes.featureFlags.list} component={FeatureFlagListPage} />
            <BTAdminLayout path={routes.featureFlags.details} component={FeatureFlagPage} />
            {/* Rebate Pages */}
            <ListPageLayout
                component={PurchasingDashboardPage}
                path={routes.purchasing.dashboard}
                selectedMenuItem={MenuItemName.Rebates}
                showJobPicker={false}
            />
            <BTAdminLayout
                path={routes.rebateReceiptReview.queues}
                component={RebateHawkQueueListPage}
            />
            <BTAdminLayout
                path={routes.rebateReceiptReview.queue}
                component={RebateReceiptReviewQueuePage}
            />
            <BTAdminLayout
                path={routes.rebateReceiptReview.list}
                component={RebateReceiptReviewListPage}
            />
            <BTAdminLayout path={hawkRouteConfig.base.pathname} component={HawkApp} />
            <BTAdminLayout path={routes.partners.list} component={PartnerListPage} />
            <DetailPageLayout path={routes.partners.details} component={PartnerPage} />
            <ListPageLayout
                path={routes.purchasing.enrollmentWizard}
                showJobPicker={false}
                component={EnrollmentWizardStartPage}
                selectedMenuItem={MenuItemName.None}
            />
            {/* Subscription Item Setup Utility*/}
            <BTAdminLayout
                path={routes.subscriptionItemSetup.details}
                component={SubscriptionItemSetupPage}
            />
            {/* Builder Setup */}
            <BTAdminLayout path={routes.builderSetup.adminSetup} component={BuilderSetupPage} />
            <BTAdminLayout
                path={routes.builderSetup.adminSearch}
                component={BuilderSetupSearchPage}
            />
            <ListPageLayout
                path={routes.builderSetup.setup}
                showJobPicker={false}
                component={BuilderSetupSummaryPage}
                selectedMenuItem={MenuItemName.None}
                hideNav
            />
            {/* Owner Portal Redirect for Builder Jobpicker */}
            <DetailPageLayout
                path={routes.ownerPortalRedirect}
                component={OwnerPortalRedirectPage}
            />
            {/* Owner Portal Redirect for Builder Jobpicker */}
            <DetailPageLayout
                path={routes.excludeFromChurn.excludeFromChurn}
                component={ExcludeFromChurnPage}
            />
            {/* Error pages... */}
            <DetailPageLayout path={routes.errorPage.noAccess} component={NoAccessPage} />
            {/* Log Exception in Sentry Page */}
            <DetailPageLayout
                path={routes.sentry.adminLogExceptionInSentry}
                component={AdminLogExceptionInSentryPage}
            />
            {/* Help link pages */}
            <DetailPageLayout path={routes.contact.contactUs} component={ContactSupportPage} />
            {/* Sub Trade Agreement Page */}
            <PrintPageLayout
                path={routes.tradeAgreement.print}
                component={TradeAgreementPrintPage}
            />
            <DetailPageLayout
                path={routes.tradeAgreement.subTradeAgreement}
                component={SubTradeAgreementPage}
            />
            {/* Signup Whats Next Page */}
            <DetailPageLayout
                path={routes.signupWhatsNext.whatsNext}
                component={SignupWhatsNextPage}
            />
            {/* Signup page with quote */}
            <DetailPageLayout
                path={routes.signupFormWithQuote.signup}
                component={SignupFormWithQuotePage}
            />
            {/* Signup page */}
            <DetailPageLayout path={routes.signupForm.signup} component={SignupFormPage} />
            {/* Signup Price Code Detail Page */}
            <DetailPageLayout
                path={routes.signupPriceCodePage.signup}
                component={SignupPriceCodePage}
            />
            {/* Vendor receipt external print/pdf page */}
            <PrintPageLayout path={routes.vendor.vendorReceipt} component={VendorReceiptPage} />
            <DetailPageLayout
                path={routes.owner.externalIdPaymentMethodApprove}
                component={OwnerSetupPage}
            />
            <DetailPageLayout path={routes.owner.setup} component={OwnerSetupPage} />
            <DetailPageLayout path={routes.sub.setup} component={SubSetupPage} />
            {/* Post Login Page */}
            <DetailPageLayout path={routes.user.postLogin} component={PostLoginPage} />
            {/* Shared Content Pages */}
            <DetailPageLayout path={routes.share.path} component={SharedContentPage} />
            {/* Workday Exception BDS Full Page Experience */}
            <ListPageLayout
                path={routes.workdayException.details}
                component={bdsWorkdayExceptionFullPageExperience}
                selectedMenuItem={MenuItemName.Calendar}
                showJobPicker={true}
                jobPickerDisplayMode={3} // 3 = calendar
            />
            <DetailPageLayout path={routes.editor.mobile} component={BTMobileEditorPage} />
            <DetailPageLayout path={routes.editor.legacyMobile} component={BTMobileEditorPage} />
            <DetailPageLayout
                path={routes.mobile.mobileLogin}
                component={MobileLoginEmailUsernameEntryPage}
            />
            <DetailPageLayout
                path={routes.mobile.mobileLoginByCode}
                element={<MobileLoginByCodePage />}
            />
            <DetailPageLayout
                path={routes.mobile.mobileLoginByCodeAuth}
                element={<MobileLoginByCodePage />}
            />
            <ListPageLayout
                path={routes.contact.clientDefaultJobPermissionSettings}
                component={ClientJobPermissionsPage}
                showJobPicker={false}
                selectedMenuItem={MenuItemName.None}
            />
            <BTAdminLayout path={routes.admin.adminMain} component={AdminMainPage} />
            <BTAdminLayout path={routes.admin.btAdminUsers} element={<BTAdminUsersList />} />
            {/* Durable deep link into one-click impersonation — mints the code on arrival */}
            <BTAdminLayout
                path={routes.admin.impersonate}
                element={<ImpersonationRedirectPage />}
            />
            <BTAdminLayout
                path={routes.admin.lowesAccountStatus}
                element={<LowesAccountStatus />}
            />
            <BTAdminLayout
                path={routes.admin.adminSubscriptionManager.subscriptionManager}
                component={AdminSubscriptionManagerPage}
            />
            <BTAdminLayout
                path={routes.admin.spamEmailRemoval}
                element={<SpamEmailRemovalPage />}
            />
            <BTAdminLayout
                path={routes.admin.notificationServiceTemplates}
                element={<AdminNotificationServiceTemplates />}
            />
            <BTAdminLayout path={routes.admin.inAppMessages} element={<AdminInAppMessages />} />
            <BTAdminLayout
                path={routes.admin.customerFeedback}
                element={<CustomerFeedbackList />}
            />
            <BTAdminLayout path={routes.admin.builders} element={<AdminBuilders />} />
            <DetailPageLayout path={routes.healthcheck} element={<NotFoundError />} />
            {/* Error page must be last, place nothing below this */}
            {window.self !== window.top ? (
                <DetailPageLayout path="" component={() => <NotFoundError />} />
            ) : (
                <ListPageLayout
                    showJobPicker={false}
                    component={() => <NotFoundError />}
                    selectedMenuItem={MenuItemName.None}
                />
            )}
        </Switch>
    );
};

function BdsConfigSettings() {
    const history = useHistory();
    useEffect(() => {
        BdsLinkConfig.routePush = history.push;
    }, [history.push]);
    return null;
}

class AppInternalBase extends Component<ITrackingProp> {
    render() {
        // Not ideal long-term here, but we need this to set the brand color on our /app page and the error page for the footer. Need to probably put it in context long-term from the nav bar request
        return (
            <>
                {/* AppProvider's own default handler is APIX-backed (ado-282449) — this used to be
                the one remaining call site falling through to the legacy btJScriptGlobals default,
                which meant RedirectToFrozenPage/RedirectToSetupSummary below (siblings of AppRoutes,
                reading this same root context) never saw real loginsDisabled/
                shouldRedirectToSetupExperience values. No explicit handler needed here anymore. */}
                <AppProvider sendPageView>
                    <AppRouter basename={routes.appBase + "/"}>
                        <BdsConfigSettings />
                        <CompatRouter>
                            <AppRouteChange />
                            <RedirectForCredentialUpgrade />
                            <MfaEnrollmentBlock />
                            <RedirectToFrozenPage />
                            <RedirectToSetupSummary />
                            <AppRoutes />
                        </CompatRouter>
                    </AppRouter>
                    <RefreshPrompt />
                </AppProvider>
                <BrowserSupportedWarning />
                {/* For mobile accessibility, if your page has content that can't be made responsive and needs to be zoomed, see the component EnablePinchZooming */}
                <Helmet>
                    <meta
                        name="viewport"
                        content="width=device-width, initial-scale=1, user-scalable=no"
                    />
                </Helmet>
            </>
        );
    }
}

const AppInternal = track(undefined, {
    dispatch: (data) => dispatchTracking(data),
    process: (ownTrackingData) => processTracking(ownTrackingData),
})(AppInternalBase);

export const App: React.FC = () => {
    return (
        <ServerErrorBoundary>
            <AppInternal />
        </ServerErrorBoundary>
    );
};
export default App;
